// /apps/pdf-tools/pdf-editor — a browser-based PDF editor.
//
// Full-bleed app (root.tsx `handle.fullBleed` plumbing), laid out like
// a desktop PDF editor:
//
//   ┌───────────────────────────────────────────────┐
//   │ ◄ PDF Tools · file.pdf   [▐▌ T ▉] … ⟲ ⟳ ⊕ ⊖ ⬇ │ ← toolbar
//   ├────┬──────────────────────────────────────────┤
//   │ p1 │                                          │
//   │ p2 │              page canvases               │
//   │ p3 │      (annotation overlays on top)        │
//   ├────┴──────────────────────────────────────────┤
//   │ page 2 / 14 · 130% · 3 annotations            │ ← status bar
//   └───────────────────────────────────────────────┘
//
// Unlike the single-purpose tools in this suite, the editor is built
// on MuPDF compiled to WebAssembly (the official `mupdf` npm package)
// — a full PDF *editor* engine, not just a renderer. It runs in a Web
// Worker (lib/worker.ts); this file is pure UI. The
// pay-off over pdf.js + pdf-lib: glyph-accurate text selection via
// structured text, and exports that contain real PDF Highlight /
// FreeText annotation objects (still editable in Acrobat), with
// incremental save preserving the original file structure. The same
// engine already supports the roadmap: forms, redaction, page
// add/remove/reorder, ink/shape annotations, text edit.
//
// Wired up so far: multi-page upload + rendering (lazy, zoomable,
// thumbnail rail), text annotations (click to place, drag to move,
// double-click to re-edit, size/colour controls), the full text-markup
// family (highlight / underline / strikethrough / squiggly — drag over
// text; selection is computed by the engine from glyph quads, exactly
// like a desktop viewer), freehand ink drawing, shapes (rectangle,
// ellipse, line, arrow — with move + endpoint resize), sticky notes,
// undo/redo, find-in-document (Ctrl+F, engine-computed hit quads),
// password-protected files (session-only password, never persisted),
// page management (rotate / delete / drag-reorder / insert blank via
// the thumbnail rail — each op rebases the document baseline and is
// undoable; see the reducer comment), AcroForm filling (HTML inputs
// overlaid on widget rects; a name → value map replayed at save, with
// an optional flatten), signatures & image stamps (draw / type /
// upload → Stamp annotation with an image appearance; last signature
// remembered in localStorage), whiteout (white-filled rect shape),
// true redaction (text-quads or area; applied at export with a
// garbage-collected full rewrite so removed content leaves no trace),
// importing a document's own annotations into the editable overlay
// (supported kinds are converted + stripped from the baseline on
// first open, so they select/move/restyle/delete like native ones),
// content-text editing (Select tool: press a line, press again to
// erase-and-replace,
// reusing the document's own embedded font when it covers the new
// glyphs, else Base-14 matching; empty deletes the line; clicking an
// image selects it for move / aspect-locked resize / replace / delete
// — all rebasing ops, so undoable), print (Ctrl+P — the saved PDF is
// handed to the browser's viewer in a hidden iframe), and download.
//
// Documents live in tabs (image-editor pattern): the editor opens on a
// blank A4 page (first visit shows a welcome overlay: choose a PDF or
// keep the blank page), and every tab autosaves to IndexedDB — bytes +
// annotations — so previously loaded PDFs come back on the next visit.
// The worker holds ONE document at a time; switching tabs stashes the
// live annotation state in memory and re-opens the target tab's bytes.
//
// The annotation list is plain serialisable state owned by this
// component; the worker re-applies it to a pristine copy of the
// original bytes on every save. That keeps undo/redo as cheap array
// snapshots and makes "save twice" idempotent.

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type DragEvent,
  type ForwardedRef,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  EXPORT_DPI,
  exportPageImages,
  formatOf,
  IMAGE_FORMATS,
  packImages,
  parsePageRange,
  type ImageFormat,
} from "./lib/image-export.ts";
import { useCompactShell } from "./lib/use-coarse-pointer.ts";
import { applyXfdfValues, buildXfdf, parseXfdf } from "./lib/xfdf.ts";
import { buildAnnotXfdf, parseAnnotXfdf } from "./lib/xfdf-annots.ts";
import type { CjkScript } from "./lib/cjk.ts";
import { variantKey } from "./lib/font-variant.ts";
import "./styles.css";
import { PdfEngine, setEngineAssetPath } from "./lib/engine.ts";
import {
  FONT_LIBRARY,
  ensureFontFace,
  fetchFontBytes,
  isCustomFamily,
  isLibraryFamily,
  libraryEntry,
  pickVariant,
  type CustomFont,
} from "./lib/fonts.ts";
import {
  A4,
  isMarkup,
  NOTE_SIZE,
  quadBounds,
  shapeHeads,
  type Annotation,
  type AnnotationStatus,
  type ContentDraw,
  type ContentErase,
  type DashStyle,
  type FormValues,
  type HeadStyle,
  type FillAction,
  type FillInfo,
  type HeaderFooterConfig,
  type ImageAction,
  type LinkAnnotation,
  type DocFontInfo,
  type DocInfo,
  type A11yPatch,
  type DocInfoFields,
  type SecurityOptions,
  type OcrWord,
  type OutlineItem,
  type RegionItem,
  type RegionMove,
  type ZObject,
  type RenderResult,
  type MarkupKind,
  type PageOp,
  type Quad,
  type ShapeAnnotation,
  type ShapeKind,
  type TextAlign,
  type WatermarkConfig,
  type WidgetInfo,
} from "./lib/protocol.ts";
import {
  deleteCustomStamp,
  listCustomStamps,
  renderTextStamp,
  saveCustomStamp,
  STAMP_PRESETS,
  stampText,
  type CustomStamp,
} from "./lib/stamps.ts";
import {
  loadSession,
  saveSession,
  MAX_PERSIST_BYTES,
  type SessionData,
} from "./lib/session-store.ts";
import {
  bytesToPdfBlob,
  downloadBlob,
  formatBytes,
  pdfFilename,
} from "./lib/file-utils.ts";
import {
  annotBounds,
  canRotate,
  remapAnnots,
  shiftAnnotWithPage,
  rotateAnnot,
  translateAnnot,
} from "./lib/annot-geometry.ts";
import {
  clampAnnotToPage,
  cloneForPaste,
  parseAnnotClipboard,
  serializeAnnots,
} from "./lib/clipboard.ts";
import {
  alignAnnots,
  alignBoxes,
  boxesIntersect,
  buildSnapTargets,
  computeSnap,
  distributeAnnots,
  distributeBoxes,
  type AlignMode,
  type Box,
  type SnapTargets,
} from "./lib/snap.ts";
import {
  HIGHLIGHT_COLORS,
  INSPECT_COLORS,
  MARKUP_COLORS,
  NOTE_COLORS,
} from "./lib/color-utils.ts";
import { base14Name, familyOfBase14 } from "./lib/text-style.ts";
import { imageFileToDataUrl } from "./lib/raster.ts";
import {
  commandGroup,
  dispatchShortcut,
  groupOfTool,
  toolDef,
  toolIsSticky,
  TOOL_GROUPS,
  isEditableTarget,
  PRIMARY_TOOLS,
  SECONDARY_TOOLS,
  SHAPE_META,
  toolShortcutText,
  type EditorCommand,
  type CommandGroup,
} from "./lib/commands.ts";
import {
  annotIdOf,
  annotItemId,
  clearKind,
  hasObjectSelection,
  isAnnotItem,
  mergeRegionItems,
  NO_SELECTION,
  shiftRegionItem,
  splitRegionItems,
  toggleRegionItem,
  type Selection,
} from "./lib/selection.ts";
import {
  docReducer,
  EMPTY_DOC,
  isMdFile,
  isPdfFile,
  nextAnnotId,
  nextGen,
  nextTabId,
  untitledName,
  type Baseline,
  type DocTab,
} from "./lib/state.ts";
import {
  MAX_SCALE,
  MIN_SCALE,
  MM_TO_PT,
  NUDGE_FLUSH_MS,
  ptToUnit,
  ZOOM_STEPS,
} from "./lib/ui-constants.ts";
import {
  isMarkupTool,
  isShapeTool,
  type ActiveTextEdit,
  type AnnotStylePatch,
  type DragPhase,
  type EditFamily,
  type PendingStamp,
  type SearchHit,
  type Tool,
  type ToolStyles,
} from "./lib/ui-types.ts";
import {
  SHEET_ACCEPT,
  isSheetFile,
  parseWorkbook,
  sheetBaseName,
} from "./lib/sheet-data.ts";
import type { SheetData } from "./lib/sheet-layout.ts";
import {
  IMAGE_IMPORT_ACCEPT,
  imageBaseName,
  isImageImportFile,
  resolveImageFiles,
  type ResolvedImportImage,
} from "./lib/image-import.ts";
import {
  DOCX_ACCEPT,
  HTML_ACCEPT,
  docBaseName,
  docxToMarkdown,
  htmlToMarkdown,
  isDocxFile,
  isHtmlFile,
} from "./lib/doc-import.ts";
import { BookmarksPanel } from "./components/BookmarksPanel.tsx";
import { CommandPalette } from "./components/CommandPalette.tsx";
import { ColorPicker } from "./components/ColorPicker.tsx";
import { CommentsPanel } from "./components/CommentsPanel.tsx";
import { CompareView } from "./components/CompareView.tsx";
import { DecorPanel } from "./components/DecorPanel.tsx";
import { FontsPanel } from "./components/FontsPanel.tsx";
import { HelpOverlay } from "./components/HelpOverlay.tsx";
import { ImportMdView } from "./components/ImportMdView.tsx";
import { ImportPhotosView } from "./components/ImportPhotosView.tsx";
import { ImportSheetView } from "./components/ImportSheetView.tsx";
import { ObjectsPanel } from "./components/ObjectsPanel.tsx";
import {
  MobileBottomBar,
  MobileTopBar,
} from "./components/MobileChrome.tsx";
import {
  FindRedactPanel,
  type ScanResults,
} from "./components/FindRedactPanel.tsx";
import { compilePatterns, type ScanRequest } from "./lib/redact-patterns.ts";
import { PageSetupPanel } from "./components/PageSetupPanel.tsx";
import { PanelDrawer, type PanelId } from "./components/PanelDrawer.tsx";
import { PropertiesPanel } from "./components/PropertiesPanel.tsx";
import { AccessibilityPanel } from "./components/AccessibilityPanel.tsx";
import {
  CertifyDialog,
  placementRect,
  type CertifyRequest,
} from "./components/CertifyDialog.tsx";
import type {
  A11yFacts,
  A11yFixId,
  A11yGoTo,
  A11yOverlayFacts,
} from "./lib/a11y.ts";
import { SecurityDialog } from "./components/SecurityDialog.tsx";
import {
  OptionsPanel,
  type PropsBarProps,
} from "./components/PropsBar.tsx";
import { PageView } from "./components/PageView.tsx";
import { SignatureDialog } from "./components/SignatureDialog.tsx";
import { ThumbCanvas, ThumbView } from "./components/ThumbView.tsx";
import {
  EditorContextMenu,
  CommandButton,
  PaneResizer,
  ToolMenu,
  TabBar,
  ToolButton,
  type ContextMenuItem,
} from "./components/chrome.tsx";

// ──────────────────────────── public API ────────────────────────────
//
// Everything below is what an embedder sees. The defaults are exactly
// the standalone editor, so `<PdfEditor />` with no props is the same
// application that ships at xtract.bot — the host app in this repo
// renders precisely that.

/** Which parts of the editor exist. An omitted key means "keep it";
 *  gating is opt-OUT, so a new feature never silently disappears from
 *  an embedder that hasn't heard of it yet. */
export interface PdfEditorFeatures {
  /** Tools offered in the toolbar. Omitted = all of them. Note that
   *  "select" is always available: with no pointer there is no way to
   *  reach an annotation once it is drawn. */
  tools?: readonly Tool[];
  /** Command groups kept in the palette, menus and keyboard map.
   *  Omitted = all. */
  commandGroups?: readonly CommandGroup[];
  /** Side panels kept. Omitted = all. */
  panels?: readonly PanelId[];
  /** Multi-document tab bar. Default true; false pins the editor to a
   *  single document, which is usually what an embedder wants. */
  tabs?: boolean;
  /** Autosave the open documents to IndexedDB and restore them on the
   *  next visit. Default true. Turn this OFF when the host owns the
   *  document: otherwise the editor keeps its own copy of someone
   *  else's file, in the host's origin, indefinitely. */
  session?: boolean;
  /** The download / export affordances. Default true. Independent of
   *  `onSave` — an embedder may want both, or neither. */
  download?: boolean;
  /** Letting the user bring in OTHER documents: the Open command, the
   *  new-tab "+", and dropping files onto the editor. Default true.
   *  Turn it off when the host decides which document is being edited. */
  open?: boolean;
  /** The status bar along the bottom (page, zoom, annotation count).
   *  Default true. */
  statusBar?: boolean;
}

/** The document to open on mount. Give either `bytes` or `url`. */
export interface PdfEditorDocument {
  /** Filename shown in the tab and used for the saved file. Defaults to
   *  the last segment of `url`, or "document.pdf". */
  name?: string;
  /** The PDF itself. A Uint8Array is copied; a Blob/File is read. */
  bytes?: ArrayBuffer | Uint8Array | Blob;
  /** Fetched with `fetch(url)` — same-origin, or CORS-enabled. */
  url?: string;
}

/** Colour scheme. "system" (default) follows the OS preference, and
 *  honours a `data-theme="dark|light"` attribute or a `dark` class on
 *  `<html>` when the host sets one. */
export type PdfEditorTheme = "light" | "dark" | "system";

/** Imperative surface, via `ref`, for hosts whose own UI drives the
 *  editor — a Save button in the host's toolbar, a dirty check before
 *  navigating away. */
export interface PdfEditorHandle {
  /** The fully-applied PDF — annotations burnt in, forms filled,
   *  security applied — exactly what Save/Download would produce. Does
   *  NOT call `onSave`; the host asked for the bytes, so it has them. */
  save(opts?: { flatten?: "none" | "forms" | "all" }): Promise<Uint8Array>;
  /** Whether the current document has unsaved annotations or form
   *  values. Mirrors what `onDirtyChange` reports. */
  isDirty(): boolean;
  /** The filename Save would use, e.g. "contract-annotated.pdf". */
  getDocumentName(): string | null;
}

export interface PdfEditorProps {
  /** Directory the engine worker and its wasm are served from, e.g.
   *  "/pdf-editor". Needed when the published `dist/assets/` files are
   *  copied somewhere your bundler doesn't know about — see the README.
   *  Omit it when the bundler resolves the worker itself, which is the
   *  normal case for Vite and webpack 5. */
  assetPath?: string;
  /** Document to open on mount, instead of the file picker (or the
   *  restored session). */
  initialDocument?: PdfEditorDocument;
  /** Called when the user saves, instead of the browser download.
   *  Receives the fully-applied PDF — annotations burnt in, forms
   *  filled, security applied — exactly the bytes a download would
   *  have produced, plus the filename it would have used. */
  onSave?: (bytes: Uint8Array, filename: string) => void | Promise<void>;
  /** Fires when the document gains or loses unsaved changes. Use it to
   *  enable a host Save button or to warn before navigation. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Feature gating — see PdfEditorFeatures. */
  features?: PdfEditorFeatures;
  /** Extra classes for the editor's root element. The root is a flex
   *  column that fills its container's height (`height: 100%`), so
   *  give the container a definite height — or pass one in `style`. */
  className?: string;
  /** Inline styles for the root element, e.g. `{ height: 600 }`. */
  style?: CSSProperties;
  /** Colour scheme; default "system". */
  theme?: PdfEditorTheme;
  /** Load the Remixicon stylesheet from jsDelivr if the page doesn't
   *  already have it. Default true. Set false if you self-host the
   *  icon font or your CSP forbids the CDN — then include it yourself. */
  loadIcons?: boolean;
}

const REMIXICON_URL = "https://cdn.jsdelivr.net/npm/remixicon@4.6.0/fonts/remixicon.css";

/** Commands that bring in another document (gated by `features.open`). */
const OPEN_COMMANDS = new Set([
  "doc.openFile",
  "doc.newTab",
  // These each build a NEW document from a file the user picks.
  "doc.combine",
  "doc.importMd",
  "doc.importDocx",
  "doc.importHtml",
  "doc.importSheet",
  "doc.importPhotos",
]);
/** Commands that write a file to the user's disk (gated by
 *  `features.download`). */
const DOWNLOAD_COMMANDS = new Set(["doc.download", "doc.downloadFlat"]);

async function resolveInitialDocument(
  doc: PdfEditorDocument
): Promise<{ name: string; bytes: ArrayBuffer }> {
  if (doc.url !== undefined) {
    const res = await fetch(doc.url);
    if (!res.ok) throw new Error(`Could not fetch ${doc.url} (${res.status})`);
    const fromUrl = decodeURIComponent(
      doc.url.split("?")[0]!.split("#")[0]!.split("/").pop() || ""
    );
    const name = doc.name ?? (fromUrl || "document.pdf");
    return { name, bytes: await res.arrayBuffer() };
  }
  const name = doc.name ?? "document.pdf";
  const b = doc.bytes;
  if (b instanceof Blob) return { name, bytes: await b.arrayBuffer() };
  if (b instanceof ArrayBuffer) return { name, bytes: b };
  if (b instanceof Uint8Array) {
    // A view may be a window onto a larger (or shared) buffer; the
    // engine wants its own ArrayBuffer.
    const copy = new Uint8Array(b.byteLength);
    copy.set(b);
    return { name, bytes: copy.buffer as ArrayBuffer };
  }
  throw new Error("initialDocument needs `bytes` or `url`");
}

/** Resolve "system" against the OS preference and any theme the host
 *  already declares on <html> (`data-theme` or a `dark` class), and
 *  keep following both. */
function useResolvedTheme(theme: PdfEditorTheme): "light" | "dark" {
  const [system, setSystem] = useState<"light" | "dark">("light");
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;
    const html = document.documentElement;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const compute = () => {
      const declared = html.dataset.theme;
      if (declared === "dark" || declared === "light") setSystem(declared);
      else if (html.classList.contains("dark")) setSystem("dark");
      else setSystem(mq.matches ? "dark" : "light");
    };
    compute();
    mq.addEventListener("change", compute);
    const mo = new MutationObserver(compute);
    mo.observe(html, { attributes: true, attributeFilter: ["data-theme", "class"] });
    return () => {
      mq.removeEventListener("change", compute);
      mo.disconnect();
    };
  }, [theme]);
  return theme === "system" ? system : theme;
}

// ─────────────────────────── main component ───────────────────────────

const AUTHOR_KEY = "xtract-pdf-editor-author";
const PANE_KEY = "xtract-pdf-editor-pane-open";

/** An in-memory restore point: the full doc state at a moment in time.
 *  Baseline/annots are immutable references — no copying needed. */
interface Checkpoint {
  id: number;
  time: number;
  label: string;
  annots: Annotation[];
  baseline: Baseline;
  formValues: FormValues;
}

let checkpointSeq = 0;

function PdfEditorInner(
  {
    assetPath,
    initialDocument,
    onSave,
    onDirtyChange,
    features,
    className,
    style,
    theme = "system",
    loadIcons = true,
  }: PdfEditorProps,
  ref: ForwardedRef<PdfEditorHandle>
) {
  // ── feature gating ──
  //
  // Resolved once, here, so every render site asks the same question
  // the same way. Each predicate answers "keep it?" and defaults to
  // yes: an embedder opts features OUT, never in, so shipping a new
  // tool can't make it vanish from an existing integration.
  const toolAllowed = useCallback(
    (t: Tool): boolean =>
      // Without a pointer there is no way to select what you drew, so
      // "select" survives any filter.
      t === "select" || !features?.tools || features.tools.includes(t),
    [features?.tools]
  );
  const groupAllowed = useCallback(
    (g: CommandGroup | undefined): boolean =>
      !features?.commandGroups || !g || features.commandGroups.includes(g),
    [features?.commandGroups]
  );
  const panelAllowed = useCallback(
    (id: PanelId): boolean => !features?.panels || features.panels.includes(id),
    [features?.panels]
  );
  const tabsEnabled = features?.tabs ?? true;
  const sessionEnabled = features?.session ?? true;
  const downloadEnabled = features?.download ?? true;
  const openEnabled = features?.open ?? true;
  const statusBarEnabled = features?.statusBar ?? true;
  const resolvedTheme = useResolvedTheme(theme);

  // Icons are an external font. Default to fetching it, so the editor
  // works out of the box; a host that self-hosts (or has a CSP) opts out.
  useEffect(() => {
    if (!loadIcons || typeof document === "undefined") return;
    if (document.querySelector('link[href*="remixicon"]')) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = REMIXICON_URL;
    link.dataset.peIcons = "";
    document.head.appendChild(link);
    // Left in place on unmount: another instance may be using it.
  }, [loadIcons]);

  // Before anything can construct an engine — ensureEngine may run
  // during the first render, so an effect would be too late.
  if (assetPath) setEngineAssetPath(assetPath);

  const engineRef = useRef<PdfEngine | null>(null);
  const ensureEngine = useCallback((): PdfEngine => {
    engineRef.current ??= new PdfEngine();
    return engineRef.current;
  }, []);

  const [tabs, setTabs] = useState<DocTab[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [pageOpBusy, setPageOpBusy] = useState(false);
  // First-run overlay: shown when there's no saved session to restore.
  const [welcome, setWelcome] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const [tool, setTool] = useState<Tool>("select");
  // Spring-loaded tools. Arming one is a loaded gesture, not a mode
  // change: it makes ONE thing and hands the pointer straight back with
  // that thing selected, so the properties bar is already showing its
  // colour and size. `toolLocked` is the opt-out — set from a tool's
  // `sticky` default (you do draw several strokes in a row) or by
  // holding its button.
  const [toolLocked, setToolLocked] = useState(false);
  // Phone shell: a finger and a narrow screen. Everything below still
  // renders — the compact chrome summons the same commands rather than
  // offering a reduced editor.
  const compact = useCompactShell();
  const [mobileDrawer, setMobileDrawer] = useState(false);
  const toolLockedRef = useRef(toolLocked);
  toolLockedRef.current = toolLocked;
  /** The last member used in each group, so its button can offer it. */
  const [lastPick, setLastPick] = useState<Record<string, Tool>>({});
  // Space held down = temporary Hand tool, released back to the real
  // one (the standard pan gesture in every graphics app).
  const [spaceHeld, setSpaceHeld] = useState(false);
  // True while a pan drag is in flight — only drives the cursor.
  const [panning, setPanning] = useState(false);
  const [scale, setScale] = useState(1);
  // Inspector: outline every object (text blocks/lines, images, form
  // fields, annotations) with colour-coded bounding boxes.
  const [inspect, setInspect] = useState(false);
  // The side panels are one drawer with a tab strip, so their state is
  // one id rather than six independent booleans that could all be true.
  const [panel, setPanel] = useState<PanelId | null>(null);
  const [pageObjects, setPageObjects] = useState<ZObject[] | null>(null);
  const [selectedObjId, setSelectedObjId] = useState<string | null>(null);
  // Side-pane widths (drag handles); double-click a handle to reset.
  const [railWidth, setRailWidth] = useState(112);
  const [paneWidth, setPaneWidth] = useState(288);
  /** Is the right column showing? Sticky, and the user's to set — see
   *  `rightPaneOpen`. Remembered across visits because it is a layout
   *  preference, not document state. Defaults to open: it holds the
   *  options for every drawing tool, so starting hidden would leave a
   *  first-time user with no way to change a colour. */
  const [paneOpen, setPaneOpenState] = useState(true);
  const setPaneOpen = useCallback((open: boolean) => {
    setPaneOpenState(open);
    try {
      localStorage.setItem(PANE_KEY, open ? "1" : "0");
    } catch {
      /* private mode */
    }
  }, []);
  // Read the stored preference AFTER the first paint rather than in the
  // initialiser: the server renders this route, and a value that differs
  // between server and client is a hydration mismatch.
  useEffect(() => {
    try {
      if (localStorage.getItem(PANE_KEY) === "0") setPaneOpenState(false);
    } catch {
      /* private mode */
    }
  }, []);
  const [docFonts, setDocFonts] = useState<DocFontInfo[] | null>(null);
  /** The document's bookmark tree. Owned here (not in the panel) so the
   *  re-read effect can be declared AFTER the engine-sync effect: child
   *  effects run first, and an early read would queue ahead of undo's
   *  document re-open and return the pre-undo outline. */
  const [outlineItems, setOutlineItems] = useState<OutlineItem[] | null>(null);
  const [highlightColor, setHighlightColor] = useState(HIGHLIGHT_COLORS[0]!);
  const [markupColor, setMarkupColor] = useState(MARKUP_COLORS[0]!);
  const [textColor, setTextColor] = useState("#e03131");
  const [textSize, setTextSize] = useState(14);
  /** Text-box typeface for the NEXT box (see TextAnnotation.font). */
  const [textFont, setTextFont] = useState("helv");
  /** Which CJK ordering the next text box draws Han with. */
  const [textCjkScript, setTextCjkScript] = useState<CjkScript>("zh-Hans");
  const [textBold, setTextBold] = useState(false);
  const [textItalic, setTextItalic] = useState(false);
  const [textAlign, setTextAlign] = useState<TextAlign>("left");
  const [textOpacity, setTextOpacity] = useState(1);
  const [inkColor, setInkColor] = useState("#e03131");
  const [inkWidth, setInkWidth] = useState(2);
  /** Last shape picked from the menu — what the Shape button activates. */
  const [shapeKind, setShapeKind] = useState<ShapeKind>("rect");
  const [shapeColor, setShapeColor] = useState("#e03131");
  const [shapeWidth, setShapeWidth] = useState(2);
  const [shapeFillOn, setShapeFillOn] = useState(false);
  const [shapeFillColor, setShapeFillColor] = useState("#ffe066");
  const [shapeDash, setShapeDash] = useState<DashStyle>("solid");
  const [shapeHeadStart, setShapeHeadStart] = useState<HeadStyle>("none");
  const [shapeHeadEnd, setShapeHeadEnd] = useState<HeadStyle>("none");
  const [noteColor, setNoteColor] = useState(NOTE_COLORS[0]!);

  // Signature / image stamping: the dialog (or image picker) produces a
  // PendingStamp, the "stamp" tool drops it on the next page click.
  const [sigDialog, setSigDialog] = useState(false);
  const [pendingStamp, setPendingStamp] = useState<PendingStamp | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Stamp library (presets + user stamps from IndexedDB).
  const [stampMenuOpen, setStampMenuOpen] = useState(false);
  const [customStamps, setCustomStamps] = useState<CustomStamp[]>([]);
  const stampUploadInputRef = useRef<HTMLInputElement>(null);

  // Thumbnail multi-selection (batch page ops).
  const [pageSel, setPageSel] = useState<ReadonlySet<number>>(new Set());
  const pageSelAnchor = useRef(0);

  // Session checkpoints (per tab, in-memory restore points).
  const [historyMenuOpen, setHistoryMenuOpen] = useState(false);
  const [checkpoints, setCheckpoints] = useState<Record<string, Checkpoint[]>>({});

  // Comment author (persisted per device; "" = anonymous).
  const [author, setAuthorState] = useState(() => {
    try {
      return typeof localStorage === "undefined" ? "" : (localStorage.getItem(AUTHOR_KEY) ?? "");
    } catch {
      return "";
    }
  });
  const authorRef = useRef(author);
  authorRef.current = author;
  const setAuthor = useCallback((v: string) => {
    setAuthorState(v);
    try {
      localStorage.setItem(AUTHOR_KEY, v);
    } catch {
      /* private mode */
    }
  }, []);
  /** Comment metadata stamped onto newly created annotations. */
  const authorMeta = useCallback((): { author?: string; modified: string } => {
    const name = authorRef.current.trim();
    return { ...(name ? { author: name } : null), modified: new Date().toISOString() };
  }, []);

  // ── selection ──
  // ONE discriminated union covers everything selectable: overlay
  // annotations (+ which one has an open editor), select-mode text
  // ranges, content images / fills / marquee regions and
  // the in-progress content-text edit. See lib/selection.ts.
  const [sel, setSel] = useState<Selection>(NO_SELECTION);
  /** Event-handler mirror. Commit paths also null it SYNCHRONOUSLY
   *  (before React re-renders) so a commit double-fire — Enter plus the
   *  unmount blur it triggers — can't run twice. */
  const selRef = useRef(sel);
  selRef.current = sel;

  // Legacy-shaped views of the union: existing props and the contextual
  // toolbar read these exactly as they read the old per-channel state.
  const selectedId = sel.kind === "annots" ? (sel.ids[0] ?? null) : null;
  const editingId = sel.kind === "annots" ? sel.editingId : null;
  const textSel = sel.kind === "text" ? sel : null;
  const imageSel = sel.kind === "image" ? sel : null;
  const fillSel = sel.kind === "fill" ? sel : null;
  const regionSel = sel.kind === "region" ? sel : null;
  const textEdit = sel.kind === "textEdit" ? sel.edit : null;
  /** How many objects are selected, whatever they are — drives the
   *  align/distribute bar for both annotation-only and mixed sets. */
  const selCount =
    sel.kind === "annots" ? sel.ids.length : sel.kind === "region" ? sel.items.length : 0;

  /** Select overlay annotations; null clears annotation selection (and
   *  nothing else — a live image/fill selection is untouched).
   *  `additive` (shift-click) toggles membership. A plain click on a
   *  member of a multi-selection KEEPS the group, so dragging any
   *  member moves them all. Updates `selRef` synchronously because the
   *  drag that starts in the same pointerdown reads it immediately. */
  const lastSelectAdditive = useRef(false);
  const selectAnnot = useCallback((id: string | null, additive = false) => {
    lastSelectAdditive.current = additive;
    const s = selRef.current;
    let next: Selection;
    if (id === null) {
      next = clearKind(s, "annots");
    } else if (additive && s.kind === "annots") {
      const ids = s.ids.includes(id) ? s.ids.filter((i) => i !== id) : [...s.ids, id];
      next =
        ids.length === 0
          ? NO_SELECTION
          : {
              kind: "annots",
              ids,
              editingId: s.editingId !== null && ids.includes(s.editingId) ? s.editingId : null,
            };
    } else if (s.kind === "annots" && s.ids.includes(id)) {
      next = s;
    } else {
      next = {
        kind: "annots",
        ids: [id],
        editingId: s.kind === "annots" && s.editingId === id ? id : null,
      };
    }
    selRef.current = next;
    setSel(next);
  }, []);

  /** Lay the armed tool down again. Called after every one-shot creation
   *  — the whole point of a spring-loaded tool is that finishing with it
   *  is not a separate step you have to remember. `id` is the thing just
   *  made, which becomes the selection so its properties are to hand. */
  const afterCreate = useCallback(
    (id?: string) => {
      if (toolLockedRef.current) return;
      setTool("select");
      if (id) selectAnnot(id);
    },
    [selectAnnot]
  );

  /** Replace/extend the selection with several ids (marquee result). */
  const selectAnnots = useCallback((ids: string[], additive: boolean) => {
    setSel((s) => {
      const prev = additive && s.kind === "annots" ? s.ids : [];
      const merged = [...new Set([...prev, ...ids])];
      return merged.length === 0
        ? clearKind(s, "annots")
        : { kind: "annots", ids: merged, editingId: null };
    });
  }, []);

  /** Select an annotation AND open its editor (text box / note). */
  const startEditAnnot = useCallback((id: string) => {
    setSel({ kind: "annots", ids: [id], editingId: id });
  }, []);

  /** Close the open annotation editor (optionally only if it's `id`'s),
   *  keeping the selection. */
  const stopEditAnnot = useCallback((id?: string) => {
    setSel((s) =>
      s.kind === "annots" && s.editingId !== null && (id === undefined || s.editingId === id)
        ? { ...s, editingId: null }
        : s
    );
  }, []);

  /** Drop the annotation selection if `id` is (part of) it. */
  const clearAnnotIf = useCallback((id: string) => {
    setSel((s) => (s.kind === "annots" && s.ids.includes(id) ? NO_SELECTION : s));
  }, []);

  const onTextSel = useCallback(
    (t: { pageIndex: number; quads: Quad[]; text: string } | null) =>
      setSel((s) => (t ? { kind: "text", ...t } : clearKind(s, "text"))),
    []
  );

  const replaceImageInputRef = useRef<HTMLInputElement>(null);
  // The keyboard handler is declared before doImageOp — reach it by ref.
  const doImageOpRef = useRef<(action: ImageAction) => void>(() => {});
  const doFillOpRef = useRef<(action: FillAction, at?: number) => void>(() => {});
  const onRegionMoveRef = useRef<(dx: number, dy: number) => void>(() => {});

  // scrollToPage is declared below the link handlers — reach it by ref.
  const scrollToPageRef = useRef<(index: number) => void>(() => {});

  // The mixed-selection ops are declared with the rest of the region
  // code, below the commands and toolbar that trigger them.
  const deleteRegionRef = useRef<() => void>(() => {});
  const nudgeRegionRef = useRef<(dx: number, dy: number) => void>(() => {});
  const alignRegionRef = useRef<(mode: AlignMode) => void>(() => {});
  const distributeRegionRef = useRef<(axis: "h" | "v") => void>(() => {});

  const [doc, dispatch] = useReducer(docReducer, EMPTY_DOC);
  // The visible page list is the current baseline's — null while no doc
  // is open (loading, or locked behind the password prompt).
  const pages = doc.baseline?.pages ?? null;
  // Live AcroForm values for the active tab (name → value). Not part
  // of undo history — form filling has its own mental model.
  const [formValues, setFormValues] = useState<FormValues>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [saving, setSaving] = useState(false);
  const [openMenuOpen, setOpenMenuOpen] = useState(false);
  const [zoomMenuOpen, setZoomMenuOpen] = useState(false);
  // Security & permissions: the dialog, plus the setting every export
  // applies. Per tab and session-only — it is never written to the
  // IndexedDB session (the rule the document's own password follows),
  // and one document's password must not follow the user into the next
  // file they open.
  const [securityOpen, setSecurityOpen] = useState(false);
  const [certifyOpen, setCertifyOpen] = useState(false);
  const [certifyBusy, setCertifyBusy] = useState(false);
  const [certifyStatus, setCertifyStatus] = useState<{
    kind: "info" | "error" | "ok";
    text: string;
  } | null>(null);
  const [security, setSecurity] = useState<SecurityOptions>({ mode: "keep" });
  const securityRef = useRef(security);
  securityRef.current = security;
  const securityByTab = useRef(new Map<string, SecurityOptions>());
  /** Document properties (Info dictionary + encryption state). */
  const [docInfo, setDocInfo] = useState<DocInfo | null>(null);
  const [a11yFacts, setA11yFacts] = useState<A11yFacts | null>(null);
  const [a11yError, setA11yError] = useState<string | null>(null);
  // Find & redact: the panel, the last scan, and which hits are ticked.
  const [findRedactOpen, setFindRedactOpen] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanResults, setScanResults] = useState<ScanResults | null>(null);
  const [scanSelected, setScanSelected] = useState<Set<number>>(new Set());
  const [scanError, setScanError] = useState<string | null>(null);
  // Strip Info/XMP metadata on every export (session toggle).
  const [stripMeta, setStripMeta] = useState(false);
  // Page-images export.
  const [imgExportOpen, setImgExportOpen] = useState(false);
  const [imgFormat, setImgFormat] = useState<ImageFormat>("png");
  const [imgDpi, setImgDpi] = useState(150);
  const [imgQuality, setImgQuality] = useState(0.92);
  const [imgScope, setImgScope] = useState<"all" | "current" | "range">("all");
  const [imgRange, setImgRange] = useState("");
  const [imgBusy, setImgBusy] = useState<{ done: number; total: number } | null>(null);
  // Optimise (reduce file size).
  const [optOpen, setOptOpen] = useState(false);
  const [optImages, setOptImages] = useState(true);
  const [optDpi, setOptDpi] = useState(150);
  const [optQuality, setOptQuality] = useState(75);
  const [optBusy, setOptBusy] = useState(false);
  const [optResult, setOptResult] = useState<{
    before: number;
    after: number;
    imagesRecompressed: number;
    imagesSeen: number;
  } | null>(null);
  const stripMetaRef = useRef(stripMeta);
  stripMetaRef.current = stripMeta;
  const [toast, setToast] = useState<string | null>(null);

  // Password prompt for encrypted documents (the tab stays "locked" —
  // active but pageless — until authentication succeeds).
  const [pwPrompt, setPwPrompt] = useState<{ tabId: string; name: string } | null>(null);
  const [pwValue, setPwValue] = useState("");
  const [pwError, setPwError] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  // Thumbnail drag-to-reorder (desktop rail only).
  const dragPage = useRef<number | null>(null);
  const [dragOverPage, setDragOverPage] = useState<number | null>(null);

  // Text search (Ctrl+F).
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchHits, setSearchHits] = useState<SearchHit[]>([]);
  const [searchCurrent, setSearchCurrent] = useState(0);
  const [searchBusy, setSearchBusy] = useState(false);
  const searchSeq = useRef(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Find & replace (second row of the search panel).
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceValue, setReplaceValue] = useState("");

  const mainRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Keyboard scope. Shortcuts are bound on `document` so they work
  // without anything focused — right for a full-page editor, hostile
  // in an embed where Ctrl+F or Ctrl+S belong to the host once the
  // user clicks elsewhere. So the editor is "armed" until the user
  // interacts outside it, and re-arms on the next interaction inside.
  const armedRef = useRef(true);
  useEffect(() => {
    const onInteract = (e: Event) => {
      const root = rootRef.current;
      if (root && e.target instanceof Node) armedRef.current = root.contains(e.target);
    };
    document.addEventListener("pointerdown", onInteract, true);
    document.addEventListener("focusin", onInteract, true);
    return () => {
      document.removeEventListener("pointerdown", onInteract, true);
      document.removeEventListener("focusin", onInteract, true);
    };
  }, []);
  const keyInScope = useCallback(
    (e: KeyboardEvent): boolean =>
      armedRef.current ||
      (e.target instanceof Node && !!rootRef.current?.contains(e.target)),
    []
  );
  const pageEls = useRef(new Map<number, HTMLDivElement>());
  const dragBase = useRef<Annotation[] | null>(null);
  const annotsRef = useRef(doc.annots);
  annotsRef.current = doc.annots;
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;
  const docRef = useRef(doc);
  docRef.current = doc;
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  const sessionReadyRef = useRef(sessionReady);
  sessionReadyRef.current = sessionReady;
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  // Guards overlapping tab activations (each open is a worker round-trip).
  const switchSeq = useRef(0);
  // Which baseline generation the worker currently has open — compared
  // against doc.baseline.gen to detect undo/redo across a page op.
  const engineGen = useRef(0);
  // Tab operations (open/switch/new/close) stash state and then await a
  // worker round-trip; two running concurrently could mis-attribute a
  // stash. Chain them so each starts after the previous settled.
  const opQueue = useRef<Promise<unknown>>(Promise.resolve());
  const enqueue = useCallback(<T,>(op: () => Promise<T>): Promise<T> => {
    const next = opQueue.current.then(op, op);
    opQueue.current = next.catch(() => {});
    return next;
  }, []);

  // Switching to a drawing tool drops the selection — otherwise the
  // contextual toolbar keeps showing the old selection's controls. The
  // pointer owns every selection kind now, so this is one rule instead
  // of the two it took when Select and Edit each owned half of them.
  // An open content-text edit is left alone: it owns an input whose blur
  // is what commits it, and clicking the toolbar has already fired that.
  // Dropping it here would silently discard whatever was typed.
  useEffect(() => {
    setSel((s) =>
      tool === "select" || s.kind === "none" || s.kind === "textEdit" ? s : NO_SELECTION
    );
  }, [tool]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2600);
  }, []);

  /** The side panels are mutually exclusive — toggle one, close the
   *  rest (single source for the toolbar buttons, rail buttons AND the
   *  command palette). Opening one also opens the column it lives in, so
   *  a panel picked while the column is hidden still appears. */
  const togglePanel = useCallback((id: PanelId) => {
    setPanel((cur) => {
      const next = cur === id ? null : id;
      if (next !== null) setPaneOpen(true);
      return next;
    });
  }, []);

  // Command palette (Ctrl+Shift+P).
  const [paletteOpen, setPaletteOpen] = useState(false);
  // In-editor user guide (? / F1 / toolbar button).
  const [helpOpen, setHelpOpen] = useState(false);

  // Two-page spread view.
  const [spread, setSpread] = useState(false);

  // Measure tool: display unit + calibration factor (real ÷ drawn) and
  // the last measured length for the Calibrate action.
  const [measureUnit, setMeasureUnit] = useState<"pt" | "mm" | "cm" | "in">("mm");
  const [measureFactor, setMeasureFactor] = useState(1);
  const lastMeasurePt = useRef(0);
  const onMeasure = useCallback((lengthPt: number) => {
    lastMeasurePt.current = lengthPt;
  }, []);
  /** Calibrate dialog: drawn length (read-only) + real length input. */
  const [calibrate, setCalibrate] = useState<{ drawnPt: number; value: string } | null>(null);
  const calibrateMeasure = useCallback(() => {
    const drawn = lastMeasurePt.current;
    if (drawn <= 0) {
      showToast("Drag a measurement over a known distance first");
      return;
    }
    setCalibrate({ drawnPt: drawn, value: "" });
  }, [showToast]);
  const commitCalibrate = useCallback(() => {
    setCalibrate((c) => {
      if (!c) return null;
      const real = Number(c.value);
      if (Number.isFinite(real) && real > 0) {
        setMeasureFactor(real / ptToUnit(c.drawnPt, measureUnit));
        showToast("Scale calibrated — measurements now use it");
      }
      return null;
    });
  }, [measureUnit, showToast]);

  useEffect(() => () => engineRef.current?.destroy(), []);

  // ── tab lifecycle ──

  /** Park the live reducer state on the currently active tab. Also
   *  syncs the tab's bytes to the current baseline so session autosave
   *  and reactivation see any page ops applied this session. */
  const stashActive = useCallback(() => {
    const cur = activeIdRef.current;
    if (!cur) return;
    const state = docRef.current;
    const form = formValuesRef.current;
    setTabs((prev) =>
      prev.map((t) =>
        t.id === cur
          ? {
              ...t,
              stash: state,
              formValues: form,
              bytes: state.baseline?.bytes ?? t.bytes,
            }
          : t
      )
    );
  }, []);

  /** Open a tab's document in the engine and make it the live one. */
  const activateTab = useCallback(
    async (tab: DocTab): Promise<void> => {
      const token = ++switchSeq.current;
      setOpening(true);
      try {
        // .slice(): open() transfers its buffer; the tab keeps the original.
        const result = await ensureEngine().open(tab.bytes.slice(0));
        if (switchSeq.current !== token) return;
        let openPages = result.pages;
        let openWidgets = result.widgets;
        let prompt = false;
        if (result.needsPassword) {
          // Re-use the password that unlocked this tab earlier in the
          // session; otherwise (or if it stopped working) ask.
          const auth = tab.password
            ? await ensureEngine().authenticate(tab.password)
            : null;
          if (switchSeq.current !== token) return;
          if (auth?.ok) {
            openPages = auth.pages;
            openWidgets = auth.widgets;
          } else {
            prompt = true;
          }
        }
        // Re-use the stashed baseline when it still matches the tab's
        // bytes — keeps undo history gens consistent across tab
        // round-trips; otherwise mint a fresh one from the open result.
        let baseline: Baseline | null = null;
        let annots = tab.stash.annots;
        let bytes = tab.bytes;
        if (!prompt) {
          if (tab.stash.baseline && tab.stash.baseline.bytes === tab.bytes) {
            baseline = tab.stash.baseline;
          } else {
            // First open of these bytes: adopt the document's own
            // annotations into the editable overlay (rebasing to the
            // stripped bytes when anything was imported).
            const imp = await ensureEngine().importAnnots();
            if (switchSeq.current !== token) return;
            if (imp.bytes) {
              bytes = imp.bytes;
              annots = [...annots, ...imp.annotations];
              baseline = { bytes, pages: imp.pages, widgets: imp.widgets, gen: nextGen() };
            } else {
              baseline = { bytes, pages: openPages, widgets: openWidgets, gen: nextGen() };
            }
          }
        }
        engineGen.current = baseline?.gen ?? 0;
        setActiveId(tab.id);
        if (bytes !== tab.bytes) {
          setTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, bytes } : t)));
        }
        setPwPrompt(prompt ? { tabId: tab.id, name: tab.name } : null);
        setPwError(false);
        setPwValue("");
        dispatch({ type: "hydrate", state: { ...tab.stash, annots, baseline } });
        setFormValues(tab.formValues);
        setSel(NO_SELECTION);
        setCurrentPage(0);
      } finally {
        if (switchSeq.current === token) setOpening(false);
      }
    },
    [ensureEngine]
  );

  const selectTab = useCallback(
    (id: string) => {
      void enqueue(async () => {
        if (id === activeIdRef.current) return;
        const target = tabsRef.current.find((t) => t.id === id);
        if (!target) return;
        const previous = tabsRef.current.find((t) => t.id === activeIdRef.current);
        stashActive();
        try {
          await activateTab(target);
        } catch (err) {
          showToast(`Could not open: ${err instanceof Error ? err.message : String(err)}`);
          if (previous) await activateTab(previous).catch(() => {});
        }
      });
    },
    [enqueue, stashActive, activateTab, showToast]
  );

  const newBlankTab = useCallback((): Promise<void> => {
    return enqueue(async () => {
      try {
        const bytes = await ensureEngine().blank(A4.width, A4.height);
        const tab: DocTab = {
          id: nextTabId(),
          name: untitledName(tabsRef.current),
          bytes,
          stash: EMPTY_DOC,
          formValues: {},
        };
        stashActive();
        setTabs((prev) => [...prev, tab]);
        await activateTab(tab);
      } catch (err) {
        showToast(
          `Could not create document: ${err instanceof Error ? err.message : String(err)}`
        );
      }
    });
  }, [enqueue, ensureEngine, stashActive, activateTab, showToast]);

  /** True when the only open tab is a pristine, untouched blank doc —
   *  the state the first-run overlay sits on top of; a file chosen from
   *  the overlay replaces it instead of piling up next to it. */
  const onlyUntouchedBlank = useCallback((): boolean => {
    const t = tabsRef.current;
    return (
      t.length === 1 &&
      /^Untitled( \d+)?\.pdf$/.test(t[0]!.name) &&
      docRef.current.annots.length === 0 &&
      docRef.current.past.length === 0
    );
  }, []);

  const openFiles = useCallback(
    (files: File[], opts?: { replaceBlank?: boolean }): Promise<void> => {
      return enqueue(async () => {
        const pdfs = files.filter(isPdfFile);
        if (pdfs.length === 0) {
          showToast("Drop a PDF file");
          return;
        }
        const replaceId =
          opts?.replaceBlank && onlyUntouchedBlank() ? tabsRef.current[0]!.id : null;
        const newTabs: DocTab[] = [];
        for (const f of pdfs) {
          newTabs.push({
            id: nextTabId(),
            name: f.name,
            bytes: await f.arrayBuffer(),
            stash: EMPTY_DOC,
            formValues: {},
          });
        }
        stashActive();
        setTabs((prev) => [
          ...prev.filter((t) => t.id !== replaceId),
          ...newTabs,
        ]);
        const target = newTabs[newTabs.length - 1]!;
        await activateTab(target).catch((err: unknown) => {
          showToast(`Could not open: ${err instanceof Error ? err.message : String(err)}`);
          setTabs((prev) => prev.filter((t) => t.id !== target.id));
          const fallback = tabsRef.current.find((t) => t.id !== target.id);
          if (fallback) void activateTab(fallback).catch(() => {});
        });
      });
    },
    [enqueue, onlyUntouchedBlank, stashActive, activateTab, showToast]
  );

  const closeTab = useCallback(
    (id: string) => {
      const tab = tabsRef.current.find((t) => t.id === id);
      if (!tab) return;
      const annots = id === activeIdRef.current ? docRef.current.annots : tab.stash.annots;
      if (
        annots.length > 0 &&
        !window.confirm(`Close ${tab.name}? Its annotations will be discarded.`)
      ) {
        return;
      }
      void enqueue(async () => {
        const idx = tabsRef.current.findIndex((t) => t.id === id);
        if (idx === -1) return;
        const remaining = tabsRef.current.filter((t) => t.id !== id);
        setTabs(remaining);
        if (id !== activeIdRef.current) return;
        const next = remaining[idx] ?? remaining[idx - 1];
        if (next) {
          await activateTab(next).catch(() => {});
        } else {
          setActiveId(null);
          engineGen.current = 0;
          dispatch({ type: "reset" });
        }
      });
      // Closing the last tab falls through to a fresh blank document
      // (the editor always shows something) — queued after the close.
      if (tabsRef.current.length === 1) void newBlankTab();
    },
    [enqueue, activateTab, newBlankTab]
  );

  const renameTab = useCallback((id: string, name: string) => {
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, name } : t)));
  }, []);

  /** Export security belongs to the document, not the editor: follow the
   *  active tab, and don't leave a password behind when it closes. */
  useEffect(() => {
    setSecurity(
      (activeId && securityByTab.current.get(activeId)) || { mode: "keep" }
    );
  }, [activeId]);
  useEffect(() => {
    const live = new Set(tabs.map((t) => t.id));
    for (const id of securityByTab.current.keys()) {
      if (!live.has(id)) securityByTab.current.delete(id);
    }
  }, [tabs]);
  const applySecurity = useCallback((next: SecurityOptions) => {
    const id = activeIdRef.current;
    if (id) securityByTab.current.set(id, next);
    setSecurity(next);
  }, []);

  // ── password prompt ──

  const submitPassword = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const prompt = pwPrompt;
      const password = pwValue;
      if (!prompt || !password || pwBusy) return;
      setPwBusy(true);
      setPwError(false);
      void enqueue(async () => {
        try {
          // The locked doc is only still open in the worker while its
          // tab is active — a concurrent tab switch invalidates this.
          if (activeIdRef.current !== prompt.tabId) return;
          const auth = await ensureEngine().authenticate(password);
          if (activeIdRef.current !== prompt.tabId) return;
          if (!auth.ok) {
            setPwError(true);
            return;
          }
          const tab = tabsRef.current.find((t) => t.id === prompt.tabId);
          let bytes = tab?.bytes ?? new ArrayBuffer(0);
          let annots = docRef.current.annots;
          let baseline: Baseline = {
            bytes,
            pages: auth.pages,
            widgets: auth.widgets,
            gen: nextGen(),
          };
          // Freshly unlocked: adopt the document's own annotations.
          const imp = await ensureEngine().importAnnots();
          if (activeIdRef.current !== prompt.tabId) return;
          if (imp.bytes) {
            bytes = imp.bytes;
            annots = [...annots, ...imp.annotations];
            baseline = { bytes, pages: imp.pages, widgets: imp.widgets, gen: nextGen() };
          }
          engineGen.current = baseline.gen;
          setTabs((prev) =>
            prev.map((t) => (t.id === prompt.tabId ? { ...t, password, bytes } : t))
          );
          dispatch({ type: "hydrate", state: { ...docRef.current, annots, baseline } });
          setPwPrompt(null);
          setPwValue("");
        } catch (err) {
          showToast(`Could not unlock: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
          setPwBusy(false);
        }
      });
    },
    [pwPrompt, pwValue, pwBusy, enqueue, ensureEngine, showToast]
  );

  const cancelPassword = useCallback(() => {
    const prompt = pwPrompt;
    setPwPrompt(null);
    setPwValue("");
    setPwError(false);
    // A locked tab is unusable — closing the prompt closes the tab
    // (closeTab falls through to a blank doc if it was the last one).
    if (prompt) closeTab(prompt.tabId);
  }, [pwPrompt, closeTab]);

  // ── page operations ──

  /** Undo/redo across a page op leaves the worker holding the wrong
   *  bytes — this effect notices the gen mismatch and re-opens the
   *  baseline the state says should be visible. */
  useEffect(() => {
    const b = doc.baseline;
    if (!b || b.gen === engineGen.current) return;
    const gen = b.gen;
    // Content selections are positional in the baseline they were made
    // against, so an undo/redo across a rebase invalidates them — drop
    // them instead of leaving a frame floating over the old geometry.
    setSel((s) => (hasObjectSelection(s) ? NO_SELECTION : s));
    void enqueue(async () => {
      if (docRef.current.baseline?.gen !== gen || engineGen.current === gen) return;
      try {
        const result = await ensureEngine().open(b.bytes.slice(0));
        if (docRef.current.baseline?.gen !== gen) return; // tab switched meanwhile
        if (result.needsPassword) {
          const tab = tabsRef.current.find((t) => t.id === activeIdRef.current);
          const auth = tab?.password
            ? await ensureEngine().authenticate(tab.password)
            : null;
          if (!auth?.ok) throw new Error("document password no longer accepted");
        }
        engineGen.current = gen;
        const cur = activeIdRef.current;
        setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes: b.bytes } : t)));
      } catch (err) {
        showToast(`Could not restore document: ${err instanceof Error ? err.message : String(err)}`);
      }
    });
  }, [doc.baseline, enqueue, ensureEngine, showToast]);

  /** Re-read the outline whenever the visible document changes. MUST be
   *  declared after the engine-sync effect above so its queued read runs
   *  against the re-opened bytes, not the ones undo just replaced. */
  useEffect(() => {
    if (panel !== "bookmarks" || !doc.baseline) return;
    let dead = false;
    void enqueue(async () => {
      const items = await ensureEngine().outline();
      if (!dead) setOutlineItems(items);
    }).catch(() => {
      if (!dead) setOutlineItems([]);
    });
    return () => {
      dead = true;
    };
  }, [panel, doc.baseline, enqueue, ensureEngine]);

  /** Font inventory — same ordering rule as the outline read. */
  useEffect(() => {
    if (panel !== "fonts" || !doc.baseline) return;
    let dead = false;
    void enqueue(async () => {
      const fonts = await ensureEngine().documentFonts();
      if (!dead) setDocFonts(fonts);
    }).catch(() => {
      if (!dead) setDocFonts([]);
    });
    return () => {
      dead = true;
    };
  }, [panel, doc.baseline, enqueue, ensureEngine]);

  /** Document properties — same ordering rule as the outline read. Also
   *  re-read when the security dialog opens, since it shows what the
   *  file's own encryption currently allows. */
  useEffect(() => {
    if ((panel !== "properties" && !securityOpen) || !doc.baseline) return;
    let dead = false;
    void enqueue(async () => {
      const info = await ensureEngine().documentInfo();
      if (!dead) setDocInfo(info);
    }).catch(() => {
      if (!dead) setDocInfo(null);
    });
    return () => {
      dead = true;
    };
  }, [panel, securityOpen, doc.baseline, enqueue, ensureEngine]);

  /** Accessibility facts — same ordering rule as the outline read. The
   *  audit itself is pure and runs in the panel; this is only the
   *  document half of it. */
  useEffect(() => {
    if (panel !== "accessibility" || !doc.baseline) return;
    let dead = false;
    setA11yError(null);
    void enqueue(async () => {
      const facts = await ensureEngine().accessibilityFacts();
      if (!dead) setA11yFacts(facts);
    }).catch((err: unknown) => {
      // A panel that says "checking…" forever is worse than one that
      // says why it couldn't.
      if (dead) return;
      setA11yFacts(null);
      setA11yError(err instanceof Error ? err.message : String(err));
    });
    return () => {
      dead = true;
    };
  }, [panel, doc.baseline, enqueue, ensureEngine]);

  /** Object list for the page in view — re-read on page change and on
   *  every document change (declared after the engine-sync effect). */
  useEffect(() => {
    if (panel !== "objects" || !doc.baseline) return;
    let dead = false;
    setSelectedObjId(null);
    void enqueue(async () => {
      // Built from the SAME structure sweep the inspector uses (safe
      // and proven). Vectors carry their real paint order; text and
      // images follow in reading order.
      const r = await ensureEngine().pageObjects(currentPage);
      const rows: ZObject[] = [];
      (r.fills ?? []).forEach((f, i) => {
        rows.push({
          id: `f:${i}`,
          index: rows.length,
          kind: f.gradient ? "shade" : f.stroke ? "stroke" : "fill",
          bbox: f.bbox,
          label: f.gradient ? "Gradient" : f.stroke ? "Outline" : "Filled shape",
          fillId: f.gradient ? undefined : `f${i}`,
        });
      });
      r.images.forEach((b) => {
        rows.push({ id: `i:${rows.length}`, index: rows.length, kind: "image", bbox: b, label: "Image" });
      });
      r.lines.forEach((b) => {
        rows.push({
          id: `t:${rows.length}`,
          index: rows.length,
          kind: "text",
          bbox: b,
          label: `Text line (${Math.round(b[2] - b[0])} pt wide)`,
        });
      });
      if (!dead) setPageObjects(rows);
    }).catch(() => {
      if (!dead) setPageObjects([]);
    });
    return () => {
      dead = true;
    };
  }, [panel, currentPage, doc.baseline, enqueue, ensureEngine]);

  /** Apply a page op in the worker, rebase the baseline, and remap the
   *  annotation overlay — one undoable history entry. */
  const doPageOp = useCallback(
    (op: PageOp) => {
      const before = docRef.current.baseline;
      if (!before || pageOpBusy) return;
      setPageOpBusy(true);
      void enqueue(async () => {
        try {
          if (docRef.current.baseline?.gen !== before.gen) return; // stale click
          const { bytes, pages: nextPages, widgets, offsets } = await ensureEngine().pageOp(op);
          const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
          engineGen.current = baseline.gen; // the worker re-opened during pageOp
          let annots = remapAnnots(annotsRef.current, op, before.pages);
          if (offsets?.length) {
            // Cropping moved the page origin — slide that page's
            // annotations by the same amount so they stay on their
            // content.
            const byPage = new Map(offsets.map(([i, dx, dy]) => [i, [dx, dy] as const]));
            annots = annots.map((a) => {
              const d = byPage.get(a.pageIndex);
              return d ? shiftAnnotWithPage(a, d[0], d[1]) : a;
            });
          }
          dispatch({ type: "commit", annots, baseline });
          selectAnnot(null);
          const cur = activeIdRef.current;
          setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
        } catch (err) {
          showToast(
            `Page operation failed: ${err instanceof Error ? err.message : String(err)}`
          );
        } finally {
          setPageOpBusy(false);
        }
      });
    },
    [pageOpBusy, enqueue, ensureEngine, showToast]
  );

  // ── document bootstrap: embedder document, restored session, or blank ──

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // An embedder-supplied document wins over anything on disk: the
      // host asked for THIS file, and silently showing last visit's
      // instead would be both wrong and a privacy leak.
      if (initialDocument) {
        await enqueue(async () => {
          try {
            const resolved = await resolveInitialDocument(initialDocument);
            if (cancelled) return;
            const tab: DocTab = {
              id: nextTabId(),
              name: resolved.name,
              bytes: resolved.bytes,
              stash: EMPTY_DOC,
              formValues: {},
            };
            setTabs([tab]);
            await activateTab(tab);
          } catch (err: unknown) {
            showToast(
              `Could not open: ${err instanceof Error ? err.message : String(err)}`
            );
          }
        });
        if (!cancelled) setSessionReady(true);
        return;
      }
      const data = sessionEnabled ? await loadSession() : null;
      if (cancelled) return;
      if (data) {
        const restored: DocTab[] = data.docs.map((d) => ({
          id: d.id,
          name: d.name,
          bytes: d.bytes,
          stash: { annots: d.annotations, baseline: null, past: [], future: [] },
          formValues: d.formValues ?? {},
        }));
        await enqueue(async () => {
          setTabs(restored);
          const target = restored.find((t) => t.id === data.activeId) ?? restored[0]!;
          await activateTab(target).catch(async () => {
            // e.g. the active doc got corrupted — fall back to any other.
            const fallback = restored.find((t) => t.id !== target.id);
            if (fallback) await activateTab(fallback).catch(() => {});
          });
        });
      } else {
        // First visit: start on a blank A4 page with the welcome overlay
        // (choose a PDF, or dismiss and use the blank document).
        await newBlankTab();
        if (!cancelled) setWelcome(true);
      }
      if (!cancelled) setSessionReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // Mount-only: restore (or bootstrap) exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildSession = useCallback((): SessionData | null => {
    const t = tabsRef.current;
    const a = activeIdRef.current;
    if (!a || t.length === 0) return null;
    return {
      version: 1,
      savedAt: Date.now(),
      activeId: a,
      docs: t
        .filter((tab) => tab.bytes.byteLength <= MAX_PERSIST_BYTES)
        .map((tab) => ({
          id: tab.id,
          name: tab.name,
          // The active tab's bytes live in the reducer baseline (page
          // ops rebase them there before setTabs catches up).
          bytes:
            tab.id === a
              ? (docRef.current.baseline?.bytes ?? tab.bytes)
              : tab.bytes,
          annotations: tab.id === a ? docRef.current.annots : tab.stash.annots,
          formValues: tab.id === a ? formValuesRef.current : tab.formValues,
        })),
    };
  }, []);

  useEffect(() => {
    if (!sessionReady || !sessionEnabled) return;
    const timer = window.setTimeout(() => {
      const data = buildSession();
      if (data) void saveSession(data);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [sessionReady, sessionEnabled, tabs, activeId, doc.annots, formValues, buildSession]);

  // Flush on tab-hide/navigation so the last edit isn't lost to the
  // autosave debounce (IDB writes are best-effort at this point).
  useEffect(() => {
    const flush = (): void => {
      if (!sessionReadyRef.current || !sessionEnabled) return;
      const data = buildSession();
      if (data) void saveSession(data);
    };
    window.addEventListener("pagehide", flush);
    return () => window.removeEventListener("pagehide", flush);
  }, [buildSession, sessionEnabled]);

  // Fit-width zoom when a document first becomes visible in a tab
  // (open, tab switch, password unlock). Keyed on the TAB, not on
  // `pages`: every content/page op rebases the baseline with a fresh
  // pages array, and refitting there would yank the user's zoom out
  // from under them mid-edit (e.g. resizing an image while zoomed in).
  const fittedTab = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (!pages || pages.length === 0 || !mainRef.current) return;
    if (fittedTab.current === activeId) return;
    fittedTab.current = activeId;
    const maxWidth = Math.max(...pages.map((p) => p.width));
    const available = mainRef.current.clientWidth - 48;
    setScale(Math.min(1.6, Math.max(MIN_SCALE, available / maxWidth)));
  }, [pages, activeId]);

  // ── annotation callbacks ──

  const onCreateMarkup = useCallback(
    (pageIndex: number, quads: Quad[]) => {
      if (!isMarkupTool(tool)) return;
      const id = nextAnnotId();
      dispatch({
        type: "commit",
        annots: [
          ...annotsRef.current,
          {
            id,
            kind: tool,
            pageIndex,
            color: tool === "highlight" ? highlightColor : markupColor,
            quads,
            ...authorMeta(),
          },
        ],
      });
      afterCreate(id);
    },
    [tool, highlightColor, markupColor, authorMeta, afterCreate]
  );

  const onCreateInk = useCallback(
    (pageIndex: number, points: number[]) => {
      const id = nextAnnotId();
      dispatch({
        type: "commit",
        annots: [
          ...annotsRef.current,
          {
            id,
            kind: "ink",
            pageIndex,
            color: inkColor,
            strokeWidth: inkWidth,
            points,
            ...authorMeta(),
          },
        ],
      });
      afterCreate(id);
    },
    [inkColor, inkWidth, authorMeta, afterCreate]
  );

  const onCreateShape = useCallback(
    (pageIndex: number, x1: number, y1: number, x2: number, y2: number) => {
      let shape: ShapeAnnotation | null = null;
      const id = nextAnnotId();
      if (tool === "whiteout") {
        // Whiteout is a styled rect: white fill, no border. It covers
        // content in every viewer but doesn't remove it — that's Redact.
        shape = {
          id,
          kind: "shape",
          shape: "rect",
          pageIndex,
          x1,
          y1,
          x2,
          y2,
          color: "#ffffff",
          strokeWidth: 0,
          fill: "#ffffff",
          ...authorMeta(),
        };
      } else if (isShapeTool(tool)) {
        shape = {
          id,
          kind: "shape",
          shape: tool,
          pageIndex,
          x1,
          y1,
          x2,
          y2,
          color: shapeColor,
          strokeWidth: shapeWidth,
          fill:
            (tool === "rect" || tool === "ellipse") && shapeFillOn
              ? shapeFillColor
              : null,
          dash: shapeDash,
          ...(tool === "line" || tool === "arrow"
            ? { headStart: shapeHeadStart, headEnd: shapeHeadEnd }
            : null),
          ...authorMeta(),
        };
      }
      if (!shape) return;
      dispatch({ type: "commit", annots: [...annotsRef.current, shape] });
      afterCreate(id);
    },
    [tool, shapeColor, shapeWidth, shapeFillOn, shapeFillColor, shapeDash, shapeHeadStart, shapeHeadEnd, authorMeta, afterCreate]
  );

  const onCreateRedact = useCallback(
    (pageIndex: number, quads: Quad[]) => {
      const id = nextAnnotId();
      dispatch({
        type: "commit",
        annots: [...annotsRef.current, { id, kind: "redact", pageIndex, quads }],
      });
      afterCreate(id);
    },
    [afterCreate]
  );

  const onCreateNote = useCallback(
    (pageIndex: number, x: number, y: number) => {
      const id = nextAnnotId();
      dispatch({
        type: "commit",
        annots: [
          ...annotsRef.current,
          { id, kind: "note", pageIndex, x, y, color: noteColor, value: "", ...authorMeta() },
        ],
      });
      startEditAnnot(id);
      afterCreate();
    },
    [noteColor, startEditAnnot, authorMeta, afterCreate]
  );

  const onCreateText = useCallback(
    (pageIndex: number, x: number, y: number) => {
      const id = nextAnnotId();
      dispatch({
        type: "commit",
        annots: [
          ...annotsRef.current,
          {
            id,
            kind: "text",
            pageIndex,
            x,
            // Click point reads as the text's visual anchor; nudge up so
            // the first line lands on the cursor rather than below it.
            y: Math.max(0, y - textSize * 0.6),
            width: 120,
            height: textSize * 1.3,
            size: textSize,
            color: textColor,
            value: "",
            ...(textFont !== "helv" ? { font: textFont } : null),
            // Only recorded when it differs from the default: an English
            // text box has no business carrying a CJK script setting.
            ...(textCjkScript !== "zh-Hans" ? { cjkScript: textCjkScript } : null),
            ...(textBold ? { bold: true } : null),
            ...(textItalic ? { italic: true } : null),
            ...(textAlign !== "left" ? { align: textAlign } : null),
            ...(textOpacity < 1 ? { opacity: textOpacity } : null),
            ...authorMeta(),
          },
        ],
      });
      startEditAnnot(id);
      afterCreate();
    },
    [textSize, textColor, textFont, textCjkScript, textBold, textItalic, textAlign, textOpacity, startEditAnnot, authorMeta, afterCreate]
  );

  const onCommitText = useCallback(
    (id: string, value: string, width: number, height: number) => {
      stopEditAnnot();
      const current = annotsRef.current;
      if (value.trim() === "") {
        // Empty text box → treat as cancelled; drop it without history.
        dispatch({ type: "transient", annots: current.filter((a) => a.id !== id) });
        clearAnnotIf(id);
        return;
      }
      dispatch({
        type: "commit",
        annots: current.map((a) =>
          a.id === id && a.kind === "text" ? { ...a, value, width, height } : a
        ),
      });
    },
    []
  );

  /** Live value of the text box being typed into.
   *
   *  Transient, so a keystroke is not an undo step — but it does mean
   *  the annotation model always matches what is on screen, which the
   *  session autosave and any export read without asking the user to
   *  click away first. Unlike onCommitText an empty value is KEPT: the
   *  box is still open, and clearing it mid-edit must not delete it. */
  const onInputText = useCallback(
    (id: string, value: string, width: number, height: number) => {
      dispatch({
        type: "transient",
        annots: annotsRef.current.map((a) =>
          a.id === id && a.kind === "text" ? { ...a, value, width, height } : a
        ),
      });
    },
    []
  );

  // TextAnnotView reports absolute positions — converted to deltas in
  // onMoveText (defined with the other drag plumbing below) so text
  // boxes share the group-move + snapping path.
  const textDragOrigin = useRef<{ x: number; y: number } | null>(null);

  const onMeasureText = useCallback((id: string, width: number, height: number) => {
    dispatch({
      type: "transient",
      annots: annotsRef.current.map((a) =>
        a.id === id && a.kind === "text" ? { ...a, width, height } : a
      ),
    });
  }, []);

  /** Enter placement mode with a chosen signature/image. */
  const startStampPlacement = useCallback((stamp: PendingStamp) => {
    setSigDialog(false);
    setPendingStamp(stamp);
    setTool("stamp");
  }, []);

  // Custom stamps load lazily when the menu opens.
  useEffect(() => {
    if (!stampMenuOpen) return;
    let dead = false;
    void listCustomStamps()
      .then((list) => {
        if (!dead) setCustomStamps(list);
      })
      .catch(() => {});
    return () => {
      dead = true;
    };
  }, [stampMenuOpen]);

  const onUploadStamp = useCallback(
    async (file: File) => {
      try {
        const { dataUrl, width, height } = await imageFileToDataUrl(file);
        const stamp: CustomStamp = {
          id: `stamp-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`,
          label: file.name.replace(/\.(png|jpe?g|webp)$/i, ""),
          dataUrl,
          width,
          height,
        };
        await saveCustomStamp(stamp);
        setCustomStamps((prev) => [...prev, stamp]);
        setStampMenuOpen(false);
        startStampPlacement(stamp);
      } catch {
        showToast("Could not read that image");
      }
    },
    [startStampPlacement, showToast]
  );

  const onPlaceStamp = useCallback(
    (pageIndex: number, x: number, y: number) => {
      const stamp = pendingStamp;
      const info = docRef.current.baseline?.pages[pageIndex];
      if (!stamp || !info) return;
      // Scale image pixels to points: readable but never dominating the page.
      const width = Math.min(180, info.width * 0.5, stamp.width);
      const height = width * (stamp.height / stamp.width);
      const id = nextAnnotId();
      dispatch({
        type: "commit",
        annots: [
          ...annotsRef.current,
          {
            id,
            kind: "stamp",
            pageIndex,
            x: Math.min(Math.max(x - width / 2, 0), Math.max(0, info.width - width)),
            y: Math.min(Math.max(y - height / 2, 0), Math.max(0, info.height - height)),
            width,
            height,
            dataUrl: stamp.dataUrl,
            ...authorMeta(),
          },
        ],
      });
      setPendingStamp(null);
      setTool("select");
      selectAnnot(id);
    },
    [pendingStamp, selectAnnot, authorMeta]
  );

  const onCommitNote = useCallback((id: string, value: string) => {
    // Only close the editor if it's still this note's — an unmount
    // commit may run right after another note's editor opened.
    stopEditAnnot(id);
    const current = annotsRef.current;
    const note = current.find((a) => a.id === id);
    if (!note || note.kind !== "note") return;
    if (value.trim() === "") {
      // Empty note → treat as cancelled; drop it without history.
      dispatch({ type: "transient", annots: current.filter((a) => a.id !== id) });
      clearAnnotIf(id);
      return;
    }
    if (note.value === value) return;
    dispatch({
      type: "commit",
      annots: current.map((a) => (a.id === id && a.kind === "note" ? { ...a, value } : a)),
    });
  }, []);

  /** Shared drag plumbing: transient updates are mapped from the
   *  annotation list captured at drag start; the end phase commits one
   *  history entry (or none, for a no-op click). */
  const dragDispatch = useCallback(
    (phase: DragPhase, map: (annots: Annotation[]) => Annotation[]) => {
      if (phase === "start") {
        dragBase.current = annotsRef.current;
        return;
      }
      const base = dragBase.current;
      if (!base) return;
      if (phase === "move") {
        dispatch({ type: "transient", annots: map(base) });
        return;
      }
      if (phase === "cancel") {
        // Interrupted drag: restore the pre-drag annotations WITHOUT a
        // commit, so it costs nothing and undo never sees it.
        dragBase.current = null;
        if (base !== annotsRef.current) dispatch({ type: "transient", annots: base });
        return;
      }
      dragBase.current = null;
      if (base !== annotsRef.current) {
        dispatch({ type: "commit", annots: annotsRef.current, base });
      }
    },
    []
  );

  // ── group move + snapping ──
  // Active snap guides while an annotation drag is in progress.
  const [snapGuides, setSnapGuides] = useState<{
    pageIndex: number;
    v: number | null;
    h: number | null;
  } | null>(null);
  /** Per-drag context: which ids move, their union bbox at drag start,
   *  and the candidate snap lines on that page. */
  const dragCtx = useRef<{
    ids: Set<string>;
    pageIndex: number;
    bounds: Box;
    targets: SnapTargets;
  } | null>(null);

  /** Dragging a member of the selection moves EVERY selected
   *  annotation; the union bbox snaps to page edges/centre and to the
   *  other annotations on the page (matched lines render as guides). */
  const onDragAnnot = useCallback(
    (id: string, dx: number, dy: number, phase: DragPhase) => {
      if (phase === "start") {
        const s = selRef.current;
        const ids = new Set(s.kind === "annots" && s.ids.includes(id) ? s.ids : [id]);
        const base = annotsRef.current;
        const movedAnnots = base.filter((a) => ids.has(a.id));
        const pageIndex = movedAnnots[0]?.pageIndex ?? 0;
        const info = docRef.current.baseline?.pages[pageIndex];
        let bounds: Box | null = null;
        for (const a of movedAnnots) {
          const b = annotBounds(a);
          bounds = bounds
            ? [
                Math.min(bounds[0], b[0]),
                Math.min(bounds[1], b[1]),
                Math.max(bounds[2], b[2]),
                Math.max(bounds[3], b[3]),
              ]
            : b;
        }
        dragCtx.current =
          bounds && info
            ? {
                ids,
                pageIndex,
                bounds,
                targets: buildSnapTargets(base, pageIndex, ids, info.width, info.height),
              }
            : null;
        dragDispatch("start", (a) => a);
        return;
      }
      const ctx = dragCtx.current;
      let adx = dx;
      let ady = dy;
      if (ctx) {
        const moved: Box = [
          ctx.bounds[0] + dx,
          ctx.bounds[1] + dy,
          ctx.bounds[2] + dx,
          ctx.bounds[3] + dy,
        ];
        const snap = computeSnap(moved, ctx.targets, 6 / scaleRef.current);
        adx += snap.dx;
        ady += snap.dy;
        if (phase === "move") {
          setSnapGuides(
            snap.vGuide !== null || snap.hGuide !== null
              ? { pageIndex: ctx.pageIndex, v: snap.vGuide, h: snap.hGuide }
              : null
          );
        }
      }
      if (phase === "end" || phase === "cancel") {
        setSnapGuides(null);
        dragCtx.current = null;
      }
      // Detect a pure tap BEFORE dragDispatch clears the base: no
      // transient ever fired ⇒ the pointer never moved.
      const tapped = phase === "end" && dragBase.current === annotsRef.current;
      dragDispatch(phase, (annots) =>
        annots.map((a) =>
          (ctx ? ctx.ids.has(a.id) : a.id === id) ? translateAnnot(a, adx, ady) : a
        )
      );
      // A plain tap on a member of a multi-selection collapses the
      // selection to just it (shift-taps already toggled at pointerdown).
      if (tapped && !lastSelectAdditive.current) {
        const s = selRef.current;
        if (s.kind === "annots" && s.ids.length > 1 && s.ids.includes(id)) {
          const next: Selection = { kind: "annots", ids: [id], editingId: null };
          selRef.current = next;
          setSel(next);
        }
      }
    },
    [dragDispatch]
  );

  /** TextAnnotView reports absolute positions; convert to deltas so
   *  text boxes go through the same group-move + snap path. */
  const onMoveText = useCallback(
    (id: string, x: number, y: number, phase: DragPhase) => {
      if (phase === "start") {
        textDragOrigin.current = { x, y };
        onDragAnnot(id, 0, 0, "start");
        return;
      }
      const o = textDragOrigin.current;
      if (!o) return;
      const dx = x - o.x;
      const dy = y - o.y;
      if (phase === "end" || phase === "cancel") textDragOrigin.current = null;
      onDragAnnot(id, dx, dy, phase);
    },
    [onDragAnnot]
  );

  const onResizeShape = useCallback(
    (id: string, end: 1 | 2, dx: number, dy: number, phase: DragPhase) => {
      dragDispatch(phase, (annots) =>
        annots.map((a) => {
          if (a.id !== id || a.kind !== "shape") return a;
          return end === 1
            ? { ...a, x1: a.x1 + dx, y1: a.y1 + dy }
            : { ...a, x2: a.x2 + dx, y2: a.y2 + dy };
        })
      );
    },
    [dragDispatch]
  );

  const onResizeStamp = useCallback(
    (id: string, dx: number, _dy: number, phase: DragPhase) => {
      dragDispatch(phase, (annots) =>
        annots.map((a) => {
          if (a.id !== id || a.kind !== "stamp") return a;
          const width = Math.max(24, a.width + dx);
          // Aspect stays locked to the image.
          return { ...a, width, height: width * (a.height / a.width) };
        })
      );
    },
    [dragDispatch]
  );

  const deleteSelected = useCallback(() => {
    const s = selRef.current;
    if (s.kind !== "annots" || s.ids.length === 0) return;
    const ids = new Set(s.ids);
    dispatch({
      type: "commit",
      annots: annotsRef.current.filter((a) => !ids.has(a.id)),
    });
    selectAnnot(null);
  }, [selectAnnot]);

  /** Patch the selected annotations' style — applied to every selected
   *  annotation compatible with the primary's kind (all four markup
   *  kinds count as one family for colour). Call sites gate their
   *  controls by the primary, so the keys always exist where they land. */
  const updateSelected = useCallback(
    (patch: AnnotStylePatch) => {
      const s = selRef.current;
      if (s.kind !== "annots" || s.ids.length === 0) return;
      const primary = annotsRef.current.find((a) => a.id === s.ids[0]);
      if (!primary) return;
      const ids = new Set(s.ids);
      const compatible = (a: Annotation) =>
        a.kind === primary.kind || (isMarkup(a) && isMarkup(primary));
      dispatch({
        type: "commit",
        annots: annotsRef.current.map((a) =>
          ids.has(a.id) && compatible(a) ? ({ ...a, ...patch } as Annotation) : a
        ),
      });
    },
    []
  );

  /** Align / distribute the multi-selection — one undo step each. A
   *  mixed selection routes to the region path, which runs the same
   *  geometry over page content and form fields too. */
  const applyAlign = useCallback((mode: AlignMode) => {
    const s = selRef.current;
    if (s.kind === "region") {
      alignRegionRef.current(mode);
      return;
    }
    if (s.kind !== "annots" || s.ids.length < 2) return;
    dispatch({
      type: "commit",
      annots: alignAnnots(annotsRef.current, new Set(s.ids), mode),
    });
  }, []);

  const applyDistribute = useCallback((axis: "h" | "v") => {
    const s = selRef.current;
    if (s.kind === "region") {
      distributeRegionRef.current(axis);
      return;
    }
    if (s.kind !== "annots" || s.ids.length < 3) return;
    dispatch({
      type: "commit",
      annots: distributeAnnots(annotsRef.current, new Set(s.ids), axis),
    });
  }, []);

  // ── clipboard (annotations) + duplicate + nudge ──

  /** In-memory fallback for when the OS clipboard is unavailable. */
  const clipboardRef = useRef<Annotation[] | null>(null);
  /** Consecutive pastes cascade so copies don't stack exactly. */
  const pasteCascade = useRef(0);

  const copySelection = useCallback(
    (cut: boolean) => {
      const s = selRef.current;
      if (s.kind !== "annots" || s.ids.length === 0) return;
      const ids = new Set(s.ids);
      const list = annotsRef.current.filter((a) => ids.has(a.id));
      if (list.length === 0) return;
      clipboardRef.current = structuredClone(list);
      pasteCascade.current = 0;
      // Tagged envelope on the OS clipboard → paste works across tabs
      // and sessions; failures fall back to the in-memory copy.
      void navigator.clipboard.writeText(serializeAnnots(list)).catch(() => {});
      if (cut) {
        dispatch({
          type: "commit",
          annots: annotsRef.current.filter((a) => !ids.has(a.id)),
        });
        setSel((v) => clearKind(v, "annots"));
      }
      showToast(
        `${cut ? "Cut" : "Copied"} ${list.length} annotation${list.length === 1 ? "" : "s"}`
      );
    },
    [showToast]
  );

  const pasteAnnotList = useCallback(
    (list: readonly Annotation[], pageIndex: number) => {
      const info = docRef.current.baseline?.pages[pageIndex];
      if (!info || list.length === 0) return;
      pasteCascade.current += 1;
      const cloned = cloneForPaste(
        list,
        pageIndex,
        12 * pasteCascade.current,
        info.width,
        info.height
      );
      dispatch({ type: "commit", annots: [...annotsRef.current, ...cloned] });
      setSel({ kind: "annots", ids: cloned.map((a) => a.id), editingId: null });
      setTool("select");
      showToast(`Pasted ${cloned.length} annotation${cloned.length === 1 ? "" : "s"}`);
    },
    [showToast]
  );

  /** Ctrl+D — clone the selection in place (each copy stays on its own
   *  page, offset a little). */
  const duplicateSelection = useCallback(() => {
    const s = selRef.current;
    if (s.kind !== "annots" || s.ids.length === 0) return;
    const ids = new Set(s.ids);
    const list = annotsRef.current.filter((a) => ids.has(a.id));
    if (list.length === 0) return;
    const cloned = list.map((src) => {
      const a: Annotation = { ...structuredClone(src), id: nextAnnotId() };
      const shifted = shiftAnnotWithPage(a, 12, 12);
      const info = docRef.current.baseline?.pages[src.pageIndex];
      return info ? clampAnnotToPage(shifted, info.width, info.height) : shifted;
    });
    dispatch({ type: "commit", annots: [...annotsRef.current, ...cloned] });
    setSel({ kind: "annots", ids: cloned.map((a) => a.id), editingId: null });
  }, []);

  /** Arrow-key nudge for whatever is selected. Annotations move as a
   *  group (one undo step per press); document objects reuse their move
   *  ops, whose busy guards drop repeats while a rebase is in flight. */
  /** Rotate the selected annotations about the selection's own centre.
   *
   *  Annotations only: rotating page CONTENT (an image, a colour fill, a
   *  region of text) would mean re-drawing it at an angle, which the
   *  erase-and-redraw machinery has no way to express — so those
   *  selections are left alone rather than half-rotated. */
  const rotateSelection = useCallback((deg: number) => {
    const s = selRef.current;
    if (s.kind !== "annots" || s.ids.length === 0) return;
    const ids = new Set(s.ids);
    const targets = annotsRef.current.filter((a) => ids.has(a.id) && canRotate(a));
    if (targets.length === 0) {
      showToast("Notes and links have no orientation to rotate.");
      return;
    }
    // One centre for the whole selection, so a group keeps its
    // arrangement instead of each piece spinning where it stands.
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const a of targets) {
      const b = annotBounds(a);
      x0 = Math.min(x0, b[0]);
      y0 = Math.min(y0, b[1]);
      x1 = Math.max(x1, b[2]);
      y1 = Math.max(y1, b[3]);
    }
    const cx = (x0 + x1) / 2;
    const cy = (y0 + y1) / 2;
    const rotatable = new Set(targets.map((a) => a.id));
    dispatch({
      type: "commit",
      annots: annotsRef.current.map((a) =>
        rotatable.has(a.id) ? rotateAnnot(a, cx, cy, deg) : a
      ),
      // A run of turns on the same selection is one undo step.
      coalesce: { key: `rotate:${[...s.ids].sort().join(",")}`, at: Date.now() },
    });
  }, [showToast]);

  /** The rotate grip's drag. Increments arrive during "move"; the
   *  start/end phases carry nothing to apply, and the coalesce key in
   *  rotateSelection already folds the whole drag into one undo step. */
  const onRotateSelectionDrag = useCallback(
    (deg: number, phase: DragPhase) => {
      if (phase === "move" && deg !== 0) rotateSelection(deg);
    },
    [rotateSelection]
  );

  const nudgeSelection = useCallback((dx: number, dy: number) => {
    const s = selRef.current;
    if (s.kind === "annots" && s.ids.length > 0) {
      const ids = new Set(s.ids);
      dispatch({
        type: "commit",
        annots: annotsRef.current.map((a) => (ids.has(a.id) ? translateAnnot(a, dx, dy) : a)),
        // A burst of arrow presses on the same selection = ONE undo step.
        coalesce: { key: `nudge:${[...s.ids].sort().join(",")}`, at: Date.now() },
      });
      return;
    }
    if (s.kind === "image") {
      doImageOpRef.current({
        kind: "move",
        to: [s.bbox[0] + dx, s.bbox[1] + dy, s.bbox[2] + dx, s.bbox[3] + dy],
      });
      return;
    }
    if (s.kind === "fill") {
      const b = s.fill.bbox;
      doFillOpRef.current({ kind: "move", to: [b[0] + dx, b[1] + dy, b[2] + dx, b[3] + dy] });
      return;
    }
    // Mixed selections nudge through their own debounced path: every
    // press would otherwise be a full page rewrite.
    if (s.kind === "region") nudgeRegionRef.current(dx, dy);
  }, []);

  /** Activate a shape tool and remember it as the Shape button's kind.
   *  Line/arrow presets reset the end decorations to match. */
  const pickShape = useCallback((s: ShapeKind) => {
    setShapeKind(s);
    setTool(s);
    if (s === "arrow") {
      setShapeHeadStart("none");
      setShapeHeadEnd("arrow");
    } else if (s === "line") {
      setShapeHeadStart("none");
      setShapeHeadEnd("none");
    }
  }, []);

  /** Arm a tool: it becomes the pointer's meaning for one use, or until
   *  you disarm it if it locks. Shapes route through pickShape so their
   *  head presets still apply. */
  const armTool = useCallback(
    (t: Tool, lock?: boolean) => {
      if (isShapeTool(t)) pickShape(t);
      else setTool(t);
      const g = groupOfTool(t);
      if (g) setLastPick((m) => ({ ...m, [g.id]: t }));
      setToolLocked(lock ?? (t === "select" || t === "hand" ? false : toolIsSticky(t)));
    },
    [pickShape]
  );

  // ── zoom ──

  const zoomBy = useCallback((direction: 1 | -1) => {
    setScale((s) => {
      const next =
        direction === 1
          ? ZOOM_STEPS.find((z) => z > s + 0.001)
          : [...ZOOM_STEPS].reverse().find((z) => z < s - 0.001);
      return Math.min(MAX_SCALE, Math.max(MIN_SCALE, next ?? s));
    });
  }, []);

  const fitWidth = useCallback(() => {
    if (!pages || !mainRef.current) return;
    const maxWidth = Math.max(...pages.map((p) => p.width));
    const avail = mainRef.current.clientWidth - 48 - (spread ? 24 : 0);
    setScale(Math.min(4, Math.max(MIN_SCALE, avail / (maxWidth * (spread ? 2 : 1)))));
  }, [pages, spread]);

  const fitPage = useCallback(() => {
    if (!pages || !mainRef.current) return;
    const maxW = Math.max(...pages.map((p) => p.width));
    const maxH = Math.max(...pages.map((p) => p.height));
    const el = mainRef.current;
    const availW = el.clientWidth - 48 - (spread ? 24 : 0);
    setScale(
      Math.min(
        MAX_SCALE,
        Math.max(
          MIN_SCALE,
          Math.min(availW / (maxW * (spread ? 2 : 1)), (el.clientHeight - 48) / maxH)
        )
      )
    );
  }, [pages, spread]);

  // Toggling the spread refits the width (skip the mount run).
  const spreadInit = useRef(true);
  useEffect(() => {
    if (spreadInit.current) {
      spreadInit.current = false;
      return;
    }
    fitWidth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spread]);

  // Ctrl+wheel (and trackpad pinch — browsers report a pinch as a
  // ctrl+wheel) zooms about the cursor. Non-passive: the browser's
  // whole-page zoom must be suppressed.
  const zoomAnchor = useRef<{ px: number; py: number; prev: number } | null>(null);
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const rect = main.getBoundingClientRect();
      const factor = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0022));
      setScale((prev) => {
        const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * factor));
        if (next !== prev) {
          zoomAnchor.current = { px: e.clientX - rect.left, py: e.clientY - rect.top, prev };
        }
        return next;
      });
    };
    // Two-finger pinch, the touch equivalent. A trackpad pinch already
    // arrives as a ctrl+wheel above; a real one has to be measured from
    // the touches, because the scroller's own pinch-zoom would scale the
    // whole UI rather than the document.
    let pinch: { dist: number; cx: number; cy: number } | null = null;
    const spread = (t: TouchList) =>
      Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY);
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      const t = e.touches;
      pinch = {
        dist: spread(t),
        cx: (t[0]!.clientX + t[1]!.clientX) / 2,
        cy: (t[0]!.clientY + t[1]!.clientY) / 2,
      };
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!pinch || e.touches.length !== 2) return;
      const dist = spread(e.touches);
      if (pinch.dist <= 0) return;
      const factor = dist / pinch.dist;
      // Ignore the jitter of two fingers resting; only a real spread or
      // squeeze should move the zoom.
      if (Math.abs(factor - 1) < 0.01) return;
      e.preventDefault();
      const rect = main.getBoundingClientRect();
      const { cx, cy } = pinch;
      pinch = { ...pinch, dist };
      setScale((prev) => {
        const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * factor));
        if (next !== prev) {
          zoomAnchor.current = { px: cx - rect.left, py: cy - rect.top, prev };
        }
        return next;
      });
    };
    const endPinch = () => {
      pinch = null;
    };
    main.addEventListener("wheel", onWheel, { passive: false });
    main.addEventListener("touchstart", onTouchStart, { passive: true });
    main.addEventListener("touchmove", onTouchMove, { passive: false });
    main.addEventListener("touchend", endPinch, { passive: true });
    main.addEventListener("touchcancel", endPinch, { passive: true });
    return () => {
      main.removeEventListener("wheel", onWheel);
      main.removeEventListener("touchstart", onTouchStart);
      main.removeEventListener("touchmove", onTouchMove);
      main.removeEventListener("touchend", endPinch);
      main.removeEventListener("touchcancel", endPinch);
    };
  }, []);
  // Scroll correction lands before paint, so the document point under
  // the cursor stays put while the zoom changes around it.
  useLayoutEffect(() => {
    const a = zoomAnchor.current;
    const main = mainRef.current;
    if (!a || !main) return;
    zoomAnchor.current = null;
    const ratio = scale / a.prev;
    main.scrollLeft = (main.scrollLeft + a.px) * ratio - a.px;
    main.scrollTop = (main.scrollTop + a.py) * ratio - a.py;
  }, [scale]);

  // ── pan (Hand tool / Space-hold / middle-drag) ──
  //
  // Panning is just scrolling the viewport: the drag never reaches a
  // page (PageView ignores pointer events under the Hand tool, and
  // annotations already opt out of every tool but select/text/link), so
  // it lands here and moves scrollLeft/scrollTop by the pointer delta.

  /** Hand behaviour is on: the tool is picked, or Space is held. */
  const panMode = tool === "hand" || spaceHeld;
  const panModeRef = useRef(panMode);
  panModeRef.current = panMode;

  const panDrag = useRef<{
    id: number;
    x: number;
    y: number;
    left: number;
    top: number;
  } | null>(null);

  const onPanPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const main = mainRef.current;
    if (!main) return;
    // Touch scrolls natively — hijacking it would break momentum.
    if (e.pointerType === "touch") return;
    // Middle-drag pans from ANY tool; left-drag only in hand mode.
    if (!(e.button === 1 || (e.button === 0 && panModeRef.current))) return;
    // Suppresses the middle-click autoscroll (the compatibility
    // mousedown never fires) and the text-drag ghost.
    e.preventDefault();
    main.setPointerCapture(e.pointerId);
    panDrag.current = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      left: main.scrollLeft,
      top: main.scrollTop,
    };
    setPanning(true);
  }, []);

  const onPanPointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const p = panDrag.current;
    const main = mainRef.current;
    if (!p || !main || e.pointerId !== p.id) return;
    main.scrollLeft = p.left - (e.clientX - p.x);
    main.scrollTop = p.top - (e.clientY - p.y);
  }, []);

  const endPan = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (panDrag.current?.id !== e.pointerId) return;
    panDrag.current = null;
    setPanning(false);
  }, []);

  // Space = hold-to-pan. Skipped while typing and while a button has
  // focus (Space activates it), and released on window blur so the
  // mode can't stick after an Alt+Tab.
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.key !== " " || e.repeat || !keyInScope(e)) return;
      const el = e.target as HTMLElement | null;
      if (isEditableTarget(el)) return;
      if (el?.closest("button, a, summary, [role='button'], [role='menuitem']")) return;
      e.preventDefault(); // no page-down scroll jump
      setSpaceHeld(true);
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key === " ") setSpaceHeld(false);
    };
    const clear = () => setSpaceHeld(false);
    document.addEventListener("keydown", onDown);
    document.addEventListener("keyup", onUp);
    window.addEventListener("blur", clear);
    return () => {
      document.removeEventListener("keydown", onDown);
      document.removeEventListener("keyup", onUp);
      window.removeEventListener("blur", clear);
    };
  }, [keyInScope]);

  // ── current page tracking + thumb navigation ──

  const registerEl = useCallback((index: number, el: HTMLDivElement | null) => {
    if (el) pageEls.current.set(index, el);
    else pageEls.current.delete(index);
  }, []);

  const onMainScroll = useCallback(() => {
    const main = mainRef.current;
    if (!main) return;
    // Current page = last page whose top edge sits above an anchor line
    // 35% down the viewport (viewport-relative, so it's independent of
    // what the pages' offsetParent happens to be).
    const anchorY = main.getBoundingClientRect().top + main.clientHeight * 0.35;
    let best = 0;
    for (const [index, el] of pageEls.current) {
      if (el.getBoundingClientRect().top <= anchorY) best = Math.max(best, index);
    }
    setCurrentPage((p) => (p === best ? p : best));
  }, []);

  const scrollToPage = useCallback((index: number) => {
    pageEls.current.get(index)?.scrollIntoView({ block: "start" });
  }, []);
  scrollToPageRef.current = scrollToPage;

  // ── text search ──

  /** Select hit `index` (wrapping) and scroll it to mid-viewport. */
  const goToHit = useCallback(
    (index: number, list: SearchHit[]) => {
      if (list.length === 0) return;
      const i = ((index % list.length) + list.length) % list.length;
      setSearchCurrent(i);
      const hit = list[i]!;
      const quad = hit.quads[0];
      const el = pageEls.current.get(hit.pageIndex);
      const main = mainRef.current;
      if (!quad || !el || !main) return;
      const b = quadBounds(quad);
      const pageTop =
        el.getBoundingClientRect().top - main.getBoundingClientRect().top + main.scrollTop;
      main.scrollTo({ top: pageTop + b.y * scale - main.clientHeight / 2 });
    },
    [scale]
  );

  // Debounced page-by-page search whenever the query (or document)
  // changes. Results stream in per page; a bumped token abandons the
  // rest of a stale sweep.
  useEffect(() => {
    const token = ++searchSeq.current;
    const needle = searchQuery.trim();
    if (!searchOpen || !needle || !pages || pages.length === 0) {
      setSearchHits([]);
      setSearchCurrent(0);
      setSearchBusy(false);
      return;
    }
    setSearchBusy(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        const engine = engineRef.current;
        if (!engine) return;
        const acc: SearchHit[] = [];
        try {
          for (let i = 0; i < pages.length; i += 1) {
            const hits = await engine.search(i, needle);
            if (searchSeq.current !== token) return;
            for (const quads of hits) acc.push({ pageIndex: i, quads });
            setSearchHits(acc.slice());
          }
        } catch {
          /* doc closed / switched mid-search — the next run recovers */
        } finally {
          if (searchSeq.current === token) {
            setSearchBusy(false);
            if (acc.length > 0) goToHit(0, acc);
            else setSearchCurrent(0);
          }
        }
      })();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchOpen, searchQuery, pages, goToHit]);

  const searchNext = useCallback(
    () => goToHit(searchCurrent + 1, searchHits),
    [goToHit, searchCurrent, searchHits]
  );
  const searchPrev = useCallback(
    () => goToHit(searchCurrent - 1, searchHits),
    [goToHit, searchCurrent, searchHits]
  );
  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery("");
  }, []);

  /** Replace the current hit (all=false) or every occurrence (all=true)
   *  by redrawing the affected lines — one undoable rebase. */
  const doReplace = useCallback(
    (all: boolean) => {
      const needle = searchQuery.trim();
      const before = docRef.current.baseline;
      if (!needle || !before || pageOpBusy) return;
      const hit = searchHits[searchCurrent];
      const only =
        all || !hit?.quads[0]
          ? undefined
          : { pageIndex: hit.pageIndex, quad: hit.quads[0] };
      if (!all && !only) return;
      const replacement = replaceValue;
      setPageOpBusy(true);
      void enqueue(async () => {
        try {
          if (docRef.current.baseline?.gen !== before.gen) return;
          const r = await ensureEngine().replaceText(needle, replacement, only);
          if (r.bytes) {
            const baseline: Baseline = {
              bytes: r.bytes,
              pages: r.pages,
              widgets: r.widgets,
              gen: nextGen(),
            };
            engineGen.current = baseline.gen;
            dispatch({ type: "commit", annots: annotsRef.current, baseline });
            setSel((s) => clearKind(s, "text"));
            const cur = activeIdRef.current;
            setTabs((prev) =>
              prev.map((t) => (t.id === cur ? { ...t, bytes: r.bytes! } : t))
            );
            showToast(`Replaced ${r.count} occurrence${r.count === 1 ? "" : "s"}`);
          } else {
            showToast("Nothing replaced — the match may span a line break");
          }
        } catch (err) {
          showToast(`Replace failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
          setPageOpBusy(false);
        }
      });
    },
    [searchQuery, replaceValue, searchHits, searchCurrent, pageOpBusy, enqueue, ensureEngine, showToast]
  );

  // Focus (and select) the query when the panel opens.
  useEffect(() => {
    if (searchOpen) searchInputRef.current?.select();
  }, [searchOpen]);

  // ── session checkpoints ──

  /** Snapshot the live doc state as an in-memory restore point (the
   *  baseline/annots references are immutable — nothing is copied). */
  const addCheckpoint = useCallback((label: string) => {
    const tabId = activeIdRef.current;
    const state = docRef.current;
    if (!tabId || !state.baseline) return;
    const cp: Checkpoint = {
      id: (checkpointSeq += 1),
      time: Date.now(),
      label,
      annots: state.annots,
      baseline: state.baseline,
      formValues: formValuesRef.current,
    };
    setCheckpoints((prev) => ({
      ...prev,
      [tabId]: [...(prev[tabId] ?? []), cp].slice(-12),
    }));
  }, []);

  /** Restore = one undoable commit; the engine-sync effect re-opens the
   *  checkpoint's bytes because the fresh gen mismatches the worker. */
  const restoreCheckpoint = useCallback(
    (cp: Checkpoint) => {
      dispatch({
        type: "commit",
        annots: cp.annots,
        baseline: { ...cp.baseline, gen: nextGen() },
      });
      setFormValues(cp.formValues);
      selectAnnot(null);
      showToast(
        `Restored the ${new Date(cp.time).toLocaleTimeString()} checkpoint (undo to go back)`
      );
    },
    [selectAnnot, showToast]
  );

  // ── download ──

  // Print: export through the normal save pipeline (annotations, form
  // values, redactions all applied), rasterise every page of the export
  // at 2× (144 DPI), and print those images via a print-only layer.
  // Self-contained: no reliance on the browser having a PDF viewer.
  const onPrint = useCallback(async () => {
    const engine = engineRef.current;
    const baseline = docRef.current.baseline;
    const tab = tabsRef.current.find((t) => t.id === activeIdRef.current);
    if (!engine || !baseline || !tab || saving) return;
    setSaving(true);
    try {
      const urls = await enqueue(async () => {
        const bytes = await engine.save(annotsRef.current, formValuesRef.current);
        // Rasterise the EXPORT (not the baseline — overlays must show).
        const opened = await engine.open(bytes.buffer as ArrayBuffer);
        let pageInfos = opened.pages;
        if (opened.needsPassword && tab.password) {
          const auth = await engine.authenticate(tab.password);
          if (auth.ok) pageInfos = auth.pages;
        }
        const out: string[] = [];
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d")!;
        for (let i = 0; i < pageInfos.length; i += 1) {
          const { width, height, pixels } = await engine.render(i, 2);
          canvas.width = width;
          canvas.height = height;
          ctx.putImageData(
            new ImageData(new Uint8ClampedArray(pixels), width, height),
            0,
            0
          );
          out.push(canvas.toDataURL("image/jpeg", 0.92));
        }
        // Put the live baseline back in the worker before anything else
        // (render/search/edits) can run.
        const restored = await engine.open(baseline.bytes.slice(0));
        if (restored.needsPassword && tab.password) {
          await engine.authenticate(tab.password);
        }
        return out;
      });
      document.querySelector(".pe-print-root")?.remove();
      const root = document.createElement("div");
      root.className = "pe-print-root";
      for (const u of urls) {
        const img = document.createElement("img");
        img.src = u;
        img.className = "pe-print-page";
        root.appendChild(img);
      }
      document.body.appendChild(root);
      const cleanup = () => root.remove();
      window.addEventListener("afterprint", cleanup, { once: true });
      await Promise.all(
        [...root.querySelectorAll("img")].map((img) =>
          (img as HTMLImageElement).decode().catch(() => {})
        )
      );
      window.print();
      // afterprint isn't universal — don't let the layer leak forever.
      window.setTimeout(cleanup, 120_000);
    } catch (err) {
      showToast(`Print failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  }, [saving, enqueue, showToast]);

  // ── form data interchange (XFDF) ──

  /** Every FILLABLE field the open document has, in document order.
   *  Signature fields are excluded: they hold a signature, not an
   *  answer, so form-data interchange has nothing to say about them. */
  const formFieldNames = useMemo(
    () => (doc.baseline?.widgets ?? []).filter((w) => w.type !== "sig").map((w) => w.name),
    [doc.baseline]
  );

  /** Write the current answers out as XFDF — the format Acrobat, Foxit
   *  and server-side tooling read. Exports every field the form has, not
   *  just the ones touched here, so the file describes the whole form. */
  const onExportFormData = useCallback(() => {
    const tab = tabsRef.current.find((t) => t.id === activeIdRef.current);
    const widgets = docRef.current.baseline?.widgets ?? [];
    if (widgets.length === 0) {
      showToast("This document has no form fields.");
      return;
    }
    const values: FormValues = {};
    for (const w of widgets) {
      if (w.type === "sig") continue;
      values[w.name] = formValuesRef.current[w.name] ?? w.value ?? "";
    }
    const xml = buildXfdf(values, tab?.name);
    downloadBlob(
      new Blob([xml], { type: "application/vnd.adobe.xfdf" }),
      pdfFilename(tab?.name ?? "form.pdf", "data", "xfdf")
    );
    showToast(`Exported ${Object.keys(values).length} field values as XFDF`);
  }, [showToast]);

  /** Write the annotation overlay out as XFDF — the review half of the
   *  format. The document doesn't travel with it, which is the point:
   *  two people can mark up the same PDF separately and merge the
   *  results. Kinds with no XFDF form are named in the toast rather
   *  than dropped quietly (see xfdf-annots for why each is out). */
  const onExportComments = useCallback(() => {
    const tab = tabsRef.current.find((t) => t.id === activeIdRef.current);
    const { annots, baseline } = docRef.current;
    if (!baseline) return;
    const built = buildAnnotXfdf(annots, baseline.pages, tab?.name);
    const missed = Object.entries(built.skipped)
      .map(([kind, n]) => `${n} ${kind}${n === 1 ? "" : "s"}`)
      .join(", ");
    if (built.exported === 0) {
      showToast(
        missed
          ? `Nothing here can travel as an XFDF comment (${missed}).`
          : "There are no comments to export."
      );
      return;
    }
    downloadBlob(
      new Blob([built.xml], { type: "application/vnd.adobe.xfdf" }),
      pdfFilename(tab?.name ?? "document.pdf", "comments", "xfdf")
    );
    showToast(
      `Exported ${built.exported} comment${built.exported === 1 ? "" : "s"} as XFDF` +
        (missed ? ` — ${missed} can't travel in XFDF and stayed here` : "")
    );
  }, [showToast]);

  /** One picker for both halves of XFDF. The extension says nothing
   *  about which half a file holds, and a file may legally hold both, so
   *  this reads whatever is in there and reports what it did — rather
   *  than making the user guess the right menu item first and then
   *  wonder why the comments they can see in Acrobat didn't arrive. */
  const onImportXfdf = useCallback(
    async (file: File) => {
      const baseline = docRef.current.baseline;
      if (!baseline) return;
      let text: string;
      try {
        text = await file.text();
      } catch (err) {
        showToast(`Could not read that file: ${err instanceof Error ? err.message : String(err)}`);
        return;
      }

      const notes: string[] = [];
      const did: string[] = [];

      // ── form answers ──
      const fields = parseXfdf(text);
      if (Object.keys(fields).length > 0) {
        const { applied, unmatched } = applyXfdfValues(
          fields,
          baseline.widgets.map((w) => w.name)
        );
        const filled = Object.keys(applied).length;
        if (filled > 0) {
          setFormValues((prev) => ({ ...prev, ...applied }));
          did.push(`${filled} field value${filled === 1 ? "" : "s"}`);
        }
        if (unmatched.length > 0) {
          notes.push(
            `${unmatched.length} field${unmatched.length === 1 ? "" : "s"} didn't match this form`
          );
        }
      }

      // ── comments ──
      const comments = parseAnnotXfdf(text, baseline.pages);
      if (comments.annots.length > 0) {
        // One undo step for the whole file: an import is a single act,
        // however many comments it brought.
        dispatch({ type: "commit", annots: [...annotsRef.current, ...comments.annots] });
        did.push(`${comments.annots.length} comment${comments.annots.length === 1 ? "" : "s"}`);
      }
      if (comments.offPage > 0) {
        notes.push(
          comments.offPage === 1
            ? "1 on a page this document doesn't have"
            : `${comments.offPage} on pages this document doesn't have`
        );
      }
      if (comments.unsupported > 0) {
        notes.push(`${comments.unsupported} of a kind this editor can't draw`);
      }

      if (did.length === 0) {
        showToast(
          notes.length > 0
            ? `Nothing imported — ${notes.join("; ")}.`
            : "No comments or field data found in that file — is it an XFDF export?"
        );
        return;
      }
      showToast(
        `Imported ${did.join(" and ")} from ${file.name}` +
          (notes.length > 0 ? ` — ${notes.join("; ")}` : "")
      );
    },
    [showToast]
  );

  /** Reduce file size, applied to the open document as one undoable
   *  rebase. Works on the BASELINE, not a save-with-annotations: the
   *  annotation overlay is separate state and must stay that way, or the
   *  same highlights would end up both baked in and still editable. */
  const onOptimize = useCallback(async () => {
    const before = docRef.current.baseline;
    if (!before || pageOpBusy || optBusy) return;
    setOptBusy(true);
    setOptResult(null);
    try {
      const r = await enqueue(async () => {
        if (docRef.current.baseline?.gen !== before.gen) return null;
        return ensureEngine().optimize(
          before.bytes.slice(0),
          optImages ? { maxDpi: optDpi, quality: optQuality } : null
        );
      });
      if (!r) return;
      if (r.after >= r.before) {
        setOptResult({
          before: r.before,
          after: r.before,
          imagesRecompressed: r.imagesRecompressed,
          imagesSeen: r.imagesSeen,
        });
        showToast("Already as small as this pass can make it — nothing changed.");
        return;
      }
      const baseline: Baseline = {
        bytes: r.bytes,
        pages: r.pages,
        widgets: r.widgets,
        gen: nextGen(),
      };
      engineGen.current = baseline.gen;
      dispatch({ type: "commit", annots: annotsRef.current, baseline });
      const cur = activeIdRef.current;
      setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes: r.bytes } : t)));
      setOptResult({
        before: r.before,
        after: r.after,
        imagesRecompressed: r.imagesRecompressed,
        imagesSeen: r.imagesSeen,
      });
      showToast(
        `Optimised: ${formatBytes(r.before)} → ${formatBytes(r.after)} (${Math.round((1 - r.after / r.before) * 100)}% smaller, undoable)`
      );
    } catch (err) {
      showToast(`Optimise failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setOptBusy(false);
    }
  }, [pageOpBusy, optBusy, optImages, optDpi, optQuality, enqueue, ensureEngine, showToast]);

  /** Export pages as images. Renders a FLATTENED copy, not the live
   *  baseline — annotations are an overlay, so rasterising the baseline
   *  would drop every highlight and note without saying so. */
  const onExportImages = useCallback(async () => {
    const engine = engineRef.current;
    const tab = tabsRef.current.find((t) => t.id === activeIdRef.current);
    const count = docRef.current.baseline?.pages.length ?? 0;
    if (!engine || !tab || !count || imgBusy) return;
    const indices =
      imgScope === "current"
        ? [currentPageRef.current]
        : imgScope === "range"
          ? parsePageRange(imgRange, count)
          : parsePageRange("", count);
    setImgBusy({ done: 0, total: indices.length });
    try {
      const bytes = await enqueue(() =>
        engine.save(annotsRef.current, formValuesRef.current, {
          flatten: "all",
          security: { mode: "keep" },
          stripMetadata: stripMetaRef.current,
        })
      );
      const images = await exportPageImages(engine, {
        // save() hands back a fresh, exactly-sized Uint8Array, so its
        // buffer is the whole PDF and is safe to transfer to the worker.
        bytes: bytes.buffer as ArrayBuffer,
        pageIndices: indices,
        format: imgFormat,
        dpi: imgDpi,
        quality: imgQuality,
        baseName: tab.name.replace(/\.pdf$/i, "") || "page",
        onProgress: (done, total) => setImgBusy({ done, total }),
      });
      const stem = tab.name.replace(/\.pdf$/i, "") || "pages";
      const packed = await packImages(images, `${stem}-images.zip`);
      downloadBlob(packed.blob, packed.name);
      showToast(
        images.length === 1
          ? `Exported 1 page as ${formatOf(imgFormat).label}`
          : `Exported ${images.length} pages as a zip of ${formatOf(imgFormat).label} files`
      );
      setImgExportOpen(false);
    } catch (err) {
      showToast(`Image export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setImgBusy(null);
    }
  }, [enqueue, imgBusy, imgScope, imgRange, imgFormat, imgDpi, imgQuality, showToast]);

  /** Sign the exported file with the user's certificate.
   *
   *  The split of work is deliberate. The worker owns the document, so it
   *  produces the finished bytes with a signature dictionary and a hole in
   *  them. The crypto happens HERE, in the page: the private key is never
   *  posted to a worker, let alone a server, and the digest is taken over
   *  the file's own /ByteRange so signer and verifier cannot disagree
   *  about what was signed.
   *
   *  The result downloads rather than becoming the open document. A signed
   *  file that stays editable is a trap — the next edit silently
   *  invalidates the signature. */
  const onCertify = useCallback(
    async (req: CertifyRequest) => {
      const engine = engineRef.current;
      const tab = tabsRef.current.find((t) => t.id === activeIdRef.current);
      const pages = docRef.current.baseline?.pages;
      if (!engine || !tab || !pages) return;
      setCertifyBusy(true);
      setCertifyStatus({ kind: "info", text: "Reading the certificate…" });
      try {
        // Lazily loaded: the ASN.1 and X.509 code is only needed by the
        // handful of people who sign, and it is not small.
        const [{ loadPem, loadPkcs12, certificateWarnings }, { buildDetachedCms }, signPdf] =
          await Promise.all([
            import("./lib/sign-material.ts"),
            import("./lib/sign-cms.ts"),
            import("./lib/sign-pdf.ts"),
          ]);

        const material =
          req.source.kind === "p12"
            ? await loadPkcs12(await req.source.file.arrayBuffer(), req.source.password)
            : await loadPem(req.source.cert, req.source.key);

        const warnings = certificateWarnings(material);
        setCertifyStatus({
          kind: "info",
          text: `Signing as ${material.subject}${warnings.length ? ` — ${warnings.join(" ")}` : ""}`,
        });

        const page = pages[req.pageIndex] ?? pages[0]!;
        const signedAt = new Date().toISOString();
        const prep = await enqueue(() =>
          engine.prepareSign(
            {
              annotations: annotsRef.current,
              formValues: formValuesRef.current,
              flatten: "none",
              security: securityRef.current,
              stripMetadata: stripMetaRef.current,
            },
            {
              name: req.name.trim() || undefined,
              reason: req.reason.trim() || undefined,
              location: req.location.trim() || undefined,
              contactInfo: req.contactInfo.trim() || undefined,
              signedAt,
              visible:
                req.placement === "invisible"
                  ? null
                  : {
                      pageIndex: req.pageIndex,
                      rect: placementRect(req.placement, page),
                    },
            }
          )
        );

        const bytes = new Uint8Array(prep.bytes);
        // Read the range back out of the file rather than trusting the
        // number that came with it: this is exactly what a verifier does.
        const range = signPdf.readByteRange(bytes);
        if (!range) throw new Error("the prepared document has no /ByteRange");
        const digest = new Uint8Array(
          await crypto.subtle.digest("SHA-256", signPdf.signedBytes(bytes, range))
        );
        const der = await buildDetachedCms({
          messageDigest: digest,
          material,
          signingTime: new Date(signedAt),
        });
        signPdf.patchContents(bytes, prep.slot, der);

        downloadBlob(bytesToPdfBlob(bytes), pdfFilename(tab.name, "signed"));
        setCertifyStatus({
          kind: "ok",
          text: `Signed and downloaded${warnings.length ? ` — ${warnings.join(" ")}` : "."}`,
        });
        showToast(`Signed as ${material.subject}`);
      } catch (err) {
        setCertifyStatus({
          kind: "error",
          text: err instanceof Error ? err.message : String(err),
        });
      } finally {
        setCertifyBusy(false);
      }
    },
    [enqueue, showToast]
  );

  /** Produce the fully-applied PDF plus the filename it should carry.
   *  Shared by Save/Download and the imperative `ref.save()`. */
  const exportBytes = useCallback(
    async (
      opts: { flatten?: "none" | "forms" | "all"; security?: SecurityOptions } = {}
    ): Promise<{ bytes: Uint8Array; filename: string }> => {
      const engine = engineRef.current;
      const tab = tabsRef.current.find((t) => t.id === activeIdRef.current);
      if (!engine || !tab) throw new Error("No document is open");
      const sec = opts.security ?? securityRef.current;
      // Through the op queue: a pending baseline re-open (undo of a
      // page op) must land in the worker before the save runs.
      const bytes = await enqueue(() =>
        engine.save(annotsRef.current, formValuesRef.current, {
          flatten: opts.flatten ?? "none",
          security: sec,
          stripMetadata: stripMetaRef.current,
        })
      );
      const filename = pdfFilename(
        tab.name,
        sec.mode === "encrypt"
          ? "protected"
          : opts.flatten && opts.flatten !== "none"
            ? "flattened"
            : "annotated"
      );
      return { bytes, filename };
    },
    [enqueue]
  );

  const onDownload = useCallback(
    async (
      opts: { flatten?: "none" | "forms" | "all"; security?: SecurityOptions } = {}
    ) => {
      const engine = engineRef.current;
      const tab = tabsRef.current.find((t) => t.id === activeIdRef.current);
      if (!engine || !tab || saving) return;
      // The dialog can hand its setting straight to the download it
      // triggers — state wouldn't have committed yet.
      const sec = opts.security ?? securityRef.current;
      setSaving(true);
      try {
        const { bytes, filename } = await exportBytes({ ...opts, security: sec });
        // An embedder that supplied onSave owns the result: hand it the
        // bytes instead of dropping a file in the user's Downloads,
        // which in an embedded context is rarely where they want it.
        if (onSave) await onSave(bytes, filename);
        else downloadBlob(bytesToPdfBlob(bytes), filename);
        addCheckpoint(onSave ? "Save" : "Download");
        if (sec.mode === "encrypt") {
          showToast(
            sec.userPassword
              ? "Downloaded — the file now requires that password to open"
              : "Downloaded — the file opens freely but restricts what viewers may do"
          );
        } else if (sec.mode === "none") {
          showToast("Downloaded — password protection removed");
        }
        if (annotsRef.current.some((a) => a.kind === "redact")) {
          showToast("Redacted content was permanently removed from the downloaded file");
        }
      } catch (err) {
        showToast(`Save failed: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setSaving(false);
      }
    },
    [saving, exportBytes, showToast, addCheckpoint, onSave]
  );

  // ── commands + keyboard shortcuts ──
  //
  // Every keyboard action is an EditorCommand: the tool table in
  // lib/commands.ts provides the tool-activation entries
  // (the same table renders the toolbar buttons), and the app-level
  // actions are declared here. dispatchShortcut() applies the matching
  // rules (input focus, modifiers, per-command `when` guards).

  /** Tool groups with disallowed members removed, and empty groups
   *  dropped — an embedder that turns off every shape shouldn't be left
   *  with a menu button that opens onto nothing. `lastPick` is
   *  re-validated at the render site for the same reason: the
   *  remembered tool may be one that is now gone. */
  const visibleToolGroups = useMemo(
    () =>
      TOOL_GROUPS.map((g) => ({
        ...g,
        members: g.members.filter((m) => toolAllowed(m.tool)),
      })).filter((g) => g.members.length > 0),
    [toolAllowed]
  );

  const commands = useMemo<EditorCommand[]>(() => {
    // Rotation applies to annotations only — see rotateSelection.
    const hasAnnotSelection = sel.kind === "annots" && sel.ids.length > 0;
    /** Panel commands are ids of the form "panel.<PanelId>". */
    const panelAllowed_ok = (c: EditorCommand): boolean =>
      !c.id.startsWith("panel.") || panelAllowed(c.id.slice(6) as PanelId);
    const nudgeable = () => {
      const k = selRef.current.kind;
      return k === "annots" || k === "image" || k === "fill" || k === "region";
    };
    const all: EditorCommand[] = [
      {
        id: "view.search",
        group: "document",
        priority: 100,
        label: "Find in document",
        icon: "ri-search-line",
        shortcut: { key: "f", mod: true },
        // From anywhere — including inputs — instead of the browser bar.
        global: true,
        prevent: true,
        run: () => {
          setSearchOpen(true);
          searchInputRef.current?.select();
        },
      },
      {
        id: "app.palette",
        group: "help",
        priority: 60,
        label: "Command palette",
        icon: "ri-command-line",
        shortcut: { key: "p", mod: true, shift: true },
        global: true,
        prevent: true,
        inPalette: false,
        run: () => setPaletteOpen(true),
      },
      {
        id: "app.help",
        group: "help",
        priority: 100,
        label: "Editor guide & keyboard shortcuts",
        icon: "ri-question-line",
        shortcut: { key: ["?", "F1"] },
        prevent: true,
        run: () => setHelpOpen(true),
      },
      {
        id: "doc.print",
        group: "file",
        priority: 85,
        label: "Print",
        icon: "ri-printer-line",
        // shift: false — Ctrl+Shift+P belongs to the command palette.
        shortcut: { key: "p", mod: true, shift: false },
        // Prints the document, not the app chrome.
        global: true,
        prevent: true,
        run: () => void onPrint(),
      },
      {
        id: "edit.copyTextSelection",
        label: "Copy selected text",
        icon: "ri-file-copy-line",
        shortcut: { key: "c", mod: true },
        // Global so it beats the input guard, but only when a text
        // selection exists and focus isn't in a real input.
        global: true,
        prevent: true,
        when: (e) => selRef.current.kind === "text" && (!e || !isEditableTarget(e.target)),
        run: () => {
          const s = selRef.current;
          if (s.kind !== "text") return;
          const text = s.text;
          void navigator.clipboard
            .writeText(text)
            .then(() =>
              showToast(`Copied ${text.length} character${text.length === 1 ? "" : "s"}`)
            )
            .catch(() => showToast("Could not access the clipboard"));
        },
      },
      {
        id: "edit.copyAnnots",
        group: "edit",
        priority: 50,
        label: "Copy",
        icon: "ri-file-copy-line",
        shortcut: { key: "c", mod: true },
        prevent: true,
        when: () => selRef.current.kind === "annots",
        run: () => copySelection(false),
      },
      {
        id: "edit.cutAnnots",
        group: "edit",
        priority: 45,
        label: "Cut",
        icon: "ri-scissors-line",
        shortcut: { key: "x", mod: true },
        prevent: true,
        when: () => selRef.current.kind === "annots",
        run: () => copySelection(true),
      },
      {
        id: "edit.duplicate",
        group: "edit",
        priority: 60,
        label: "Duplicate",
        icon: "ri-file-copy-2-line",
        shortcut: { key: "d", mod: true },
        prevent: true,
        when: () => selRef.current.kind === "annots",
        run: duplicateSelection,
      },
      {
        id: "edit.selectAllOnPage",
        group: "edit",
        priority: 40,
        label: "Select all annotations on this page",
        icon: "ri-checkbox-multiple-line",
        shortcut: { key: "a", mod: true },
        prevent: true,
        run: () => {
          const ids = annotsRef.current
            .filter((a) => a.pageIndex === currentPageRef.current)
            .map((a) => a.id);
          if (ids.length === 0) return;
          setTool("select");
          selectAnnots(ids, false);
        },
      },
      {
        id: "edit.undo",
        group: "edit",
        priority: 100,
        label: "Undo",
        icon: "ri-arrow-go-back-line",
        shortcut: { key: "z", mod: true, shift: false },
        prevent: true,
        run: () => {
          dispatch({ type: "undo" });
          selectAnnot(null);
        },
      },
      {
        id: "edit.redo",
        group: "edit",
        priority: 90,
        label: "Redo",
        icon: "ri-arrow-go-forward-line",
        shortcut: [
          { key: "z", mod: true, shift: true },
          { key: "y", mod: true },
        ],
        prevent: true,
        run: () => {
          dispatch({ type: "redo" });
          selectAnnot(null);
        },
      },
      // Tool activation (v / e / t / h / …) from the shared tool table.
      ...[...PRIMARY_TOOLS, ...SECONDARY_TOOLS]
        .filter((d) => d.key)
        .map<EditorCommand>((d) => ({
          id: `tool.${d.tool}`,
          label: d.label,
          icon: d.icon,
          shortcut: { key: d.key! },
          run: () => armTool(d.tool),
        })),
      // Shape tools (r / o / l / a) — also remembered by the Add menu.
      ...(Object.keys(SHAPE_META) as ShapeKind[]).map<EditorCommand>((k) => ({
        id: `shape.${k}`,
        label: SHAPE_META[k].label,
        icon: SHAPE_META[k].icon,
        shortcut: { key: SHAPE_META[k].shortcut.toLowerCase() },
        run: () => armTool(k),
      })),
      {
        id: "edit.deleteSelection",
        group: "edit",
        priority: 70,
        label: "Delete selection",
        icon: "ri-delete-bin-line",
        shortcut: { key: ["Delete", "Backspace"] },
        prevent: true,
        run: () => {
          const s = selRef.current;
          if (s.kind === "image") doImageOpRef.current({ kind: "delete" });
          else if (s.kind === "fill") doFillOpRef.current({ kind: "delete" });
          else if (s.kind === "region") deleteRegionRef.current();
          else deleteSelected();
        },
      },
      {
        id: "app.dismiss",
        label: "Deselect / close overlays",
        shortcut: { key: "Escape" },
        inPalette: false,
        run: () => {
          // Sync ref clear: an open text edit is CANCELLED, and the
          // blur that follows must not commit it.
          selRef.current = NO_SELECTION;
          setSel(NO_SELECTION);
          setWelcome(false);
          setSigDialog(false);
          setPendingStamp(null);
          setTool((t) => (t === "stamp" ? "select" : t));
          closeSearch();
        },
      },
      // Arrow-key nudge: 1 pt, Shift = 10 pt.
      ...(
        [
          ["ArrowLeft", -1, 0],
          ["ArrowRight", 1, 0],
          ["ArrowUp", 0, -1],
          ["ArrowDown", 0, 1],
        ] as const
      ).flatMap<EditorCommand>(([key, dx, dy]) => [
        {
          id: `edit.nudge.${key}`,
          label: "Nudge selection",
          shortcut: { key, shift: false },
          prevent: true,
          when: nudgeable,
          inPalette: false,
          run: () => nudgeSelection(dx, dy),
        },
        {
          id: `edit.nudge.${key}.big`,
          label: "Nudge selection ×10",
          shortcut: { key, shift: true },
          prevent: true,
          when: nudgeable,
          inPalette: false,
          run: () => nudgeSelection(dx * 10, dy * 10),
        },
      ]),
      {
        id: "edit.rotateRight",
        group: "edit",
        priority: 44,
        label: "Rotate selection 90° right",
        icon: "ri-clockwise-line",
        shortcut: { key: "]", mod: true },
        disabled: !hasAnnotSelection,
        run: () => rotateSelection(90),
      },
      {
        id: "edit.rotateLeft",
        group: "edit",
        priority: 43,
        label: "Rotate selection 90° left",
        icon: "ri-anticlockwise-line",
        shortcut: { key: "[", mod: true },
        disabled: !hasAnnotSelection,
        run: () => rotateSelection(-90),
      },
      {
        id: "view.zoomIn",
        group: "view",
        priority: 100,
        label: "Zoom in",
        icon: "ri-zoom-in-line",
        shortcut: { key: ["+", "="] },
        run: () => zoomBy(1),
      },
      {
        id: "view.zoomOut",
        group: "view",
        priority: 95,
        label: "Zoom out",
        icon: "ri-zoom-out-line",
        shortcut: { key: "-" },
        run: () => zoomBy(-1),
      },
      // Ctrl+= / Ctrl+- zoom the DOCUMENT, overriding the browser zoom.
      {
        id: "view.zoomInMod",
        label: "Zoom in",
        shortcut: { key: ["+", "="], mod: true },
        prevent: true,
        global: true,
        inPalette: false,
        run: () => zoomBy(1),
      },
      {
        id: "view.zoomOutMod",
        label: "Zoom out",
        shortcut: { key: "-", mod: true },
        prevent: true,
        global: true,
        inPalette: false,
        run: () => zoomBy(-1),
      },
      {
        id: "view.fitWidth",
        group: "view",
        priority: 90,
        label: "Fit width",
        icon: "ri-aspect-ratio-line",
        shortcut: { key: "0", mod: true },
        prevent: true,
        global: true,
        run: fitWidth,
      },
      {
        id: "view.actualSize",
        group: "view",
        priority: 80,
        label: "Actual size (100%)",
        shortcut: { key: "1", mod: true },
        prevent: true,
        global: true,
        run: () => setScale(1),
      },
      // ── palette-only commands (no shortcuts): panels, doc + page ops ──
      { id: "view.fitPage", group: "view", priority: 85, label: "Fit page", icon: "ri-artboard-line", run: fitPage },
      {
        id: "view.spread",
        group: "view",
        priority: 60,
        label: "Toggle two-page view",
        icon: "ri-book-open-line",
        run: () => setSpread((v) => !v),
      },
      {
        id: "view.inspector",
        group: "view",
        priority: 40,
        label: "Toggle inspector",
        icon: "ri-focus-2-line",
        run: () => setInspect((v) => !v),
      },
      {
        id: "panel.comments",
        group: "panel",
        priority: 40,
        active: panel === "comments",
        label: "Comments panel",
        icon: "ri-chat-3-line",
        run: () => togglePanel("comments"),
      },
      {
        // The column can be hidden from its own header, so there has to
        // be a way back that doesn't depend on it being visible.
        id: "view.pane",
        group: "panel",
        priority: 60,
        active: paneOpen,
        label: paneOpen ? "Hide side panel" : "Show side panel",
        icon: "ri-layout-right-line",
        run: () => {
          if (paneOpen) setPanel(null);
          setPaneOpen(!paneOpen);
        },
      },
      {
        id: "panel.accessibility",
        group: "panel",
        priority: 33,
        active: panel === "accessibility",
        label: "Accessibility check",
        icon: "ri-wheelchair-line",
        run: () => togglePanel("accessibility"),
      },
      {
        id: "panel.properties",
        group: "panel",
        priority: 35,
        active: panel === "properties",
        label: "Document properties",
        icon: "ri-file-info-line",
        run: () => togglePanel("properties"),
      },
      {
        id: "panel.bookmarks",
        group: "panel",
        priority: 30,
        active: panel === "bookmarks",
        label: "Bookmarks panel",
        icon: "ri-bookmark-line",
        run: () => togglePanel("bookmarks"),
      },
      {
        id: "panel.objects",
        group: "panel",
        priority: 50,
        active: panel === "objects",
        label: "Objects panel",
        icon: "ri-stack-line",
        run: () => togglePanel("objects"),
      },
      {
        id: "panel.fonts",
        group: "panel",
        priority: 20,
        active: panel === "fonts",
        label: "Fonts panel",
        icon: "ri-font-family",
        run: () => togglePanel("fonts"),
      },
      {
        id: "panel.pageSetup",
        group: "panel",
        priority: 10,
        active: panel === "pageSetup",
        label: "Page setup…",
        icon: "ri-artboard-2-line",
        run: () => togglePanel("pageSetup"),
      },
      {
        id: "panel.decor",
        group: "panel",
        priority: 5,
        active: panel === "decor",
        label: "Headers, footers & watermark…",
        icon: "ri-layout-top-line",
        run: () => togglePanel("decor"),
      },
      {
        id: "doc.openFile",
        group: "file",
        priority: 100,
        label: "Open PDF…",
        icon: "ri-folder-open-line",
        run: () => fileInputRef.current?.click(),
      },
      {
        id: "doc.newTab",
        group: "file",
        priority: 90,
        label: "New blank document",
        icon: "ri-file-add-line",
        run: () => void newBlankTab(),
      },
      {
        id: "doc.download",
        group: "file",
        priority: 95,
        label: "Download PDF",
        icon: "ri-download-2-line",
        run: () => void onDownload(),
      },
      {
        id: "doc.downloadFlat",
        group: "file",
        priority: 40,
        label: "Download flattened PDF (annotations baked in)",
        icon: "ri-stack-line",
        run: () => void onDownload({ flatten: "all" }),
      },
      {
        id: "form.exportData",
        group: "file",
        priority: 26,
        label: "Export form data (XFDF)…",
        icon: "ri-file-download-line",
        disabled: formFieldNames.length === 0,
        run: onExportFormData,
      },
      {
        id: "annots.exportXfdf",
        group: "file",
        priority: 24,
        label: "Export comments (XFDF)…",
        icon: "ri-chat-download-line",
        disabled: !pages || doc.annots.length === 0,
        run: onExportComments,
      },
      {
        // One entry for both halves — the file decides which it is.
        id: "doc.importXfdf",
        group: "file",
        priority: 23,
        label: "Import XFDF (comments or form data)…",
        icon: "ri-file-upload-line",
        disabled: !pages,
        run: () => xfdfInputRef.current?.click(),
      },
      {
        id: "doc.certify",
        group: "file",
        priority: 22,
        label: "Digitally sign with a certificate…",
        icon: "ri-shield-check-line",
        disabled: !pages,
        run: () => {
          setCertifyStatus(null);
          setCertifyOpen(true);
        },
      },
      {
        id: "doc.combine",
        group: "file",
        priority: 52,
        label: "Combine PDFs…",
        icon: "ri-file-copy-2-line",
        run: () => setCombineOpen(true),
      },
      {
        id: "doc.optimize",
        group: "file",
        priority: 45,
        label: "Reduce file size…",
        icon: "ri-compasses-2-line",
        run: () => setOptOpen(true),
      },
      {
        id: "doc.exportImages",
        group: "file",
        priority: 80,
        label: "Export pages as images…",
        icon: "ri-image-2-line",
        run: () => setImgExportOpen(true),
      },
      {
        id: "doc.findRedact",
        group: "document",
        priority: 45,
        label: "Find & redact…",
        icon: "ri-shield-flash-line",
        run: () => setFindRedactOpen(true),
      },
      {
        id: "doc.protect",
        group: "file",
        priority: 30,
        label: "Security & permissions…",
        icon: "ri-shield-keyhole-line",
        run: () => setSecurityOpen(true),
      },
      {
        id: "doc.insertPdf",
        group: "file",
        priority: 50,
        label: "Insert pages from another PDF…",
        icon: "ri-file-add-line",
        run: () => insertPdfInputRef.current?.click(),
      },
      {
        id: "doc.importMd",
        group: "file",
        priority: 60,
        label: "Import Markdown as PDF…",
        icon: "ri-markdown-line",
        run: () => mdInputRef.current?.click(),
      },
      {
        id: "doc.importSheet",
        group: "file",
        priority: 56,
        label: "Import spreadsheet as PDF… (CSV, Excel, ODS)",
        icon: "ri-table-line",
        run: () => sheetInputRef.current?.click(),
      },
      {
        id: "doc.importPhotos",
        group: "file",
        priority: 54,
        label: "Import images as PDF… (JPG, PNG, HEIC, WebP)",
        icon: "ri-image-line",
        run: () => photoInputRef.current?.click(),
      },
      {
        id: "doc.importDocx",
        group: "file",
        priority: 62,
        label: "Import Word document as PDF… (.docx)",
        icon: "ri-file-word-line",
        run: () => docxInputRef.current?.click(),
      },
      {
        id: "doc.importHtml",
        group: "file",
        priority: 58,
        label: "Import HTML file as PDF…",
        icon: "ri-html5-line",
        run: () => htmlInputRef.current?.click(),
      },
      {
        id: "doc.ocr",
        group: "document",
        priority: 60,
        label: "Recognise text (OCR)…",
        icon: "ri-character-recognition-line",
        run: () => setOcrOpen(true),
      },
      {
        id: "doc.compare",
        group: "file",
        priority: 20,
        label: "Compare with another PDF…",
        icon: "ri-contrast-2-line",
        run: () => comparePdfInputRef.current?.click(),
      },
      {
        id: "annotate.sign",
        group: "document",
        priority: 90,
        label: "Add signature…",
        icon: "ri-quill-pen-line",
        run: () => setSigDialog(true),
      },
      {
        id: "annotate.image",
        group: "document",
        priority: 85,
        label: "Insert image…",
        icon: "ri-image-add-line",
        run: () => imageInputRef.current?.click(),
      },
      {
        id: "edit.checkpointNow",
        group: "edit",
        priority: 20,
        label: "Checkpoint now",
        icon: "ri-bookmark-2-line",
        run: () => {
          addCheckpoint("Checkpoint");
          showToast("Checkpoint added");
        },
      },
      {
        id: "page.addBlank",
        group: "document",
        priority: 80,
        label: "Add blank page after current",
        icon: "ri-add-line",
        run: () => {
          const info = docRef.current.baseline?.pages[currentPageRef.current] ?? A4;
          doPageOp({
            kind: "insertBlank",
            at: currentPageRef.current + 1,
            width: info.width,
            height: info.height,
          });
        },
      },
      {
        id: "page.rotateRight",
        group: "document",
        priority: 50,
        label: "Rotate current page clockwise",
        icon: "ri-clockwise-line",
        run: () =>
          doPageOp({ kind: "rotate", pageIndex: currentPageRef.current, clockwise: true }),
      },
      {
        id: "page.rotateLeft",
        group: "document",
        priority: 45,
        label: "Rotate current page anti-clockwise",
        icon: "ri-anticlockwise-line",
        run: () =>
          doPageOp({ kind: "rotate", pageIndex: currentPageRef.current, clockwise: false }),
      },
    ];
    // Filtering HERE, once, is what makes the gate airtight: commands
    // feed the palette, the menus AND the keyboard map, so a command
    // that never enters this list cannot be reached by any of them.
    return all.filter(
      (c) =>
        groupAllowed(c.group) &&
        // "tool.<id>" entries activate a tool; drop the ones whose tool
        // the embedder turned off.
        (!c.id.startsWith("tool.") || toolAllowed(c.id.slice(5) as Tool)) &&
        (downloadEnabled || !DOWNLOAD_COMMANDS.has(c.id)) &&
        (openEnabled || !OPEN_COMMANDS.has(c.id)) &&
        (panelAllowed_ok(c))
    );
  }, [
    groupAllowed,
    toolAllowed,
    panelAllowed,
    downloadEnabled,
    openEnabled,
    deleteSelected,
    zoomBy,
    pickShape,
    closeSearch,
    onPrint,
    showToast,
    selectAnnot,
    copySelection,
    duplicateSelection,
    nudgeSelection,
    fitWidth,
    fitPage,
    togglePanel,
    rotateSelection,
    sel,
    paneOpen,
    setPaneOpen,
    armTool,
    onExportFormData,
    onExportComments,
    formFieldNames,
    pages,
    doc.annots.length,
    newBlankTab,
    onDownload,
    addCheckpoint,
    doPageOp,
    panel,
  ]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (!keyInScope(e)) return;
      dispatchShortcut(e, commands);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [commands, keyInScope]);

  // ── paste (Ctrl+V + context menu) ──

  /** Shared paste dispatcher. Precedence: our annotation envelope →
   *  OS image (screenshot → stamp) → plain text (→ text box) →
   *  in-memory annotation clipboard. Returns true when handled. */
  const doPastePayload = useCallback(
    async (
      payload: { text?: string | null; imageBlob?: Blob | null },
      pageIndex: number
    ): Promise<boolean> => {
      const info = docRef.current.baseline?.pages[pageIndex];
      if (!info) return false;
      const envelope = payload.text ? parseAnnotClipboard(payload.text) : null;
      if (envelope) {
        pasteAnnotList(envelope, pageIndex);
        return true;
      }
      if (payload.imageBlob) {
        try {
          const { dataUrl, width, height } = await imageFileToDataUrl(payload.imageBlob);
          const w = Math.min(180, info.width * 0.5, width);
          const h = w * (height / width);
          const id = nextAnnotId();
          dispatch({
            type: "commit",
            annots: [
              ...annotsRef.current,
              {
                id,
                kind: "stamp",
                pageIndex,
                x: Math.max(0, (info.width - w) / 2),
                y: Math.max(0, (info.height - h) / 2),
                width: w,
                height: h,
                dataUrl,
              },
            ],
          });
          selectAnnot(id);
          setTool("select");
          showToast("Image pasted from clipboard");
        } catch {
          showToast("Could not read that image");
        }
        return true;
      }
      const text = payload.text?.replace(/\r\n/g, "\n") ?? "";
      if (text.trim()) {
        const id = nextAnnotId();
        dispatch({
          type: "commit",
          annots: [
            ...annotsRef.current,
            {
              id,
              kind: "text",
              pageIndex,
              x: Math.max(0, info.width * 0.5 - 100),
              y: Math.max(0, info.height * 0.4),
              width: 200,
              height: textSize * 1.3,
              size: textSize,
              color: textColor,
              value: text,
            },
          ],
        });
        selectAnnot(id);
        setTool("select");
        showToast("Text pasted as a text box");
        return true;
      }
      if (clipboardRef.current?.length) {
        pasteAnnotList(clipboardRef.current, pageIndex);
        return true;
      }
      return false;
    },
    [pasteAnnotList, selectAnnot, showToast, textSize, textColor]
  );

  // Ctrl+V arrives as the native paste event — the only way to read
  // clipboardData without a permission prompt. Inputs keep their own
  // paste untouched.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (isEditableTarget(e.target)) return;
      const dt = e.clipboardData;
      if (!dt) return;
      const imageItem = [...dt.items].find(
        (i) => i.kind === "file" && i.type.startsWith("image/")
      );
      e.preventDefault();
      void doPastePayload(
        { text: dt.getData("text/plain") || null, imageBlob: imageItem?.getAsFile() ?? null },
        currentPageRef.current
      );
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [doPastePayload]);

  /** Context-menu Paste — no ClipboardEvent there, so go through the
   *  async clipboard API (may prompt once) with the in-memory fallback. */
  const pasteFromMenu = useCallback(
    async (pageIndex: number) => {
      let text: string | null = null;
      let imageBlob: Blob | null = null;
      try {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          const imgType = item.types.find((t) => t.startsWith("image/"));
          if (imgType && !imageBlob) imageBlob = await item.getType(imgType);
          if (item.types.includes("text/plain") && text === null) {
            text = await (await item.getType("text/plain")).text();
          }
        }
      } catch {
        /* permission denied / unsupported — fallback below */
      }
      const handled = await doPastePayload({ text, imageBlob }, pageIndex);
      if (!handled) showToast("Nothing to paste");
    },
    [doPastePayload, showToast]
  );

  // ── context menu ──

  const [ctxMenu, setCtxMenu] = useState<{
    x: number;
    y: number;
    pageIndex: number;
    annotId: string | null;
  } | null>(null);

  /** Topmost annotation whose (padded) bounds contain a page point —
   *  the coarse hit-test behind right-click and shift+click, used when
   *  an annotation's own painted hit area wasn't what got pressed. */
  const annotAt = useCallback(
    (pageIndex: number, x: number, y: number): Annotation | null => {
      const pad = 4 / scaleRef.current;
      let hit: Annotation | null = null;
      for (const a of annotsRef.current) {
        if (a.pageIndex !== pageIndex) continue;
        const b = annotBounds(a);
        if (x >= b[0] - pad && x <= b[2] + pad && y >= b[1] - pad && y <= b[3] + pad) hit = a;
      }
      return hit;
    },
    []
  );

  /** Right-click: bounds-hit the topmost annotation under the pointer
   *  (selecting it, unless it's already part of the selection) and open
   *  the menu; empty space gets the paste/bookmark menu. */
  const onContextMenuAt = useCallback(
    (pageIndex: number, x: number, y: number, clientX: number, clientY: number) => {
      const hit = annotAt(pageIndex, x, y);
      if (hit) {
        const s = selRef.current;
        if (!(s.kind === "annots" && s.ids.includes(hit.id))) selectAnnot(hit.id);
      }
      setCtxMenu({ x: clientX, y: clientY, pageIndex, annotId: hit?.id ?? null });
    },
    [annotAt, selectAnnot]
  );

  /** Apply an erase/draw content edit and rebase — one undoable entry,
   *  exactly like a page op (annotations don't need remapping). */
  const doContentEdit = useCallback(
    (pageIndex: number, erase: ContentErase | null, draw: ContentDraw | null) => {
      const before = docRef.current.baseline;
      if (!before || pageOpBusy) return;
      setPageOpBusy(true);
      void enqueue(async () => {
        try {
          if (docRef.current.baseline?.gen !== before.gen) return;
          const { bytes, pages: nextPages, widgets } = await ensureEngine().editContent(
            pageIndex,
            erase,
            draw
          );
          const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
          engineGen.current = baseline.gen;
          dispatch({ type: "commit", annots: annotsRef.current, baseline });
          const cur = activeIdRef.current;
          setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
        } catch (err) {
          showToast(`Edit failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
          setPageOpBusy(false);
        }
      });
    },
    [pageOpBusy, enqueue, ensureEngine, showToast]
  );

  const onCommitTextEdit = useCallback(
    (value: string) => {
      // Consume via the ref — Enter triggers a commit AND an unmount
      // blur; the second call must be a no-op.
      const open = selRef.current;
      if (open.kind !== "textEdit") return;
      const edit = open.edit;
      selRef.current = NO_SELECTION;
      setSel(NO_SELECTION);
      const next = value.replace(/\s+$/, "");
      // Font changes (family / bold / italic) force the Base-14 variant —
      // the document's own face only exists in its original weight/slant.
      // Colour / size / background changes keep the face but must still
      // commit even when the text itself is untouched.
      const fontChanged =
        edit.family !== "original" ||
        edit.bold !== edit.origBold ||
        edit.italic !== edit.origItalic;
      const restyled =
        fontChanged ||
        edit.color !== edit.origColor ||
        edit.size !== edit.origSize ||
        edit.background !== null;
      const moved = edit.moveDx !== 0 || edit.moveDy !== 0;
      if (next === edit.text && !restyled && !moved) return;
      // A brand-new text box (nothing to erase) with nothing typed is a no-op.
      if (next.trim() === "" && edit.quads.length === 0) return;
      const customFam = isLibraryFamily(edit.family) || isCustomFamily(edit.family);
      const root =
        edit.family === "original" || customFam
          ? familyOfBase14(edit.base14)
          : (edit.family as "helv" | "times" | "cour");
      // Scaling a paragraph keeps its SHAPE: wrap width and leading
      // grow with the font so the same words break on the same lines.
      const k = edit.origSize > 0 ? edit.size / edit.origSize : 1;
      void (async () => {
        // Library/uploaded face: fetch its file (cached) and embed it.
        let customFont: { name: string; bytes: ArrayBuffer } | undefined;
        if (customFam) {
          try {
            if (isLibraryFamily(edit.family)) {
              const lib = libraryEntry(edit.family);
              if (!lib) throw new Error("unknown font");
              const v = pickVariant(lib, edit.bold, edit.italic);
              customFont = {
                name: `${lib.label.replace(/\s+/g, "")}-${v.variant}`,
                bytes: await fetchFontBytes(v.url),
              };
            } else {
              const cf = customFontsRef.current.find((c) => c.id === edit.family);
              if (!cf) throw new Error("uploaded font is gone");
              customFont = {
                name: cf.label.replace(/[^A-Za-z0-9-]/g, "") || "Uploaded",
                bytes: cf.bytes.slice(0),
              };
            }
          } catch (err) {
            showToast(
              `Could not load that font: ${err instanceof Error ? err.message : String(err)}`
            );
            return;
          }
        }
        doContentEdit(
          edit.pageIndex,
          { quads: edit.quads, what: "text" },
          next.trim() === ""
            ? null
            : {
                text: next,
                x: edit.origin[0] + edit.moveDx,
                y: edit.origin[1] + edit.moveDy,
                size: edit.size,
                font: fontChanged ? base14Name(root, edit.bold, edit.italic) : edit.base14,
                rawFont: fontChanged ? undefined : edit.rawFont,
                color: edit.color,
                background: edit.background,
                customFont,
                wrap: edit.block
                  ? {
                      maxWidth: edit.block.maxWidth * k,
                      lineHeight: edit.block.lineHeight * k,
                    }
                  : undefined,
              }
        );
      })();
    },
    [doContentEdit, showToast]
  );

  const onCancelTextEdit = useCallback(() => {
    // Sync ref clear so the unmount blur can't commit the cancelled edit.
    selRef.current = clearKind(selRef.current, "textEdit");
    setSel((s) => clearKind(s, "textEdit"));
  }, []);

  /** Apply an image op (delete/move/replace) against the current
   *  selection and rebase; keeps the selection in sync afterwards. */
  const doImageOp = useCallback(
    (action: ImageAction) => {
      const cur = selRef.current;
      const sel = cur.kind === "image" ? cur : null;
      const before = docRef.current.baseline;
      if (!sel || !before || pageOpBusy) return;
      setPageOpBusy(true);
      void enqueue(async () => {
        try {
          if (docRef.current.baseline?.gen !== before.gen) return;
          const { bytes, pages: nextPages, widgets } = await ensureEngine().imageOp(
            sel.pageIndex,
            sel.bbox,
            action
          );
          const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
          engineGen.current = baseline.gen;
          dispatch({ type: "commit", annots: annotsRef.current, baseline });
          const cur = activeIdRef.current;
          setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
          if (action.kind === "move") {
            setSel({ kind: "image", pageIndex: sel.pageIndex, bbox: action.to });
          } else if (action.kind === "replace") {
            setSel({ kind: "image", pageIndex: sel.pageIndex, bbox: sel.bbox });
          } else {
            setSel((s) => clearKind(s, "image"));
          }
        } catch (err) {
          showToast(`Image edit failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
          setPageOpBusy(false);
        }
      });
    },
    [pageOpBusy, enqueue, ensureEngine, showToast]
  );
  doImageOpRef.current = doImageOp;

  /** Apply a fill-block action. For "add", `at` names the page (no
   *  selection exists yet); everything else uses the selected block. */
  const doFillOp = useCallback(
    (action: FillAction, at?: number) => {
      const cur = selRef.current;
      const sel = cur.kind === "fill" ? cur : null;
      const pageIndex = action.kind === "add" ? at : sel?.pageIndex;
      const id = action.kind === "add" ? "" : sel?.fill.id;
      const before = docRef.current.baseline;
      if (pageIndex === undefined || id === undefined || !before || pageOpBusy) return;
      setPageOpBusy(true);
      void enqueue(async () => {
        try {
          if (docRef.current.baseline?.gen !== before.gen) return;
          const { bytes, pages: nextPages, widgets, fill } = await ensureEngine().fillOp(
            pageIndex,
            id,
            action
          );
          const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
          engineGen.current = baseline.gen;
          dispatch({ type: "commit", annots: annotsRef.current, baseline });
          const cur = activeIdRef.current;
          setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
          setSel((s) => (fill ? { kind: "fill", pageIndex, fill } : clearKind(s, "fill")));
        } catch (err) {
          showToast(`Block edit failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
          setPageOpBusy(false);
        }
      });
    },
    [pageOpBusy, enqueue, ensureEngine, showToast]
  );
  doFillOpRef.current = doFillOp;

  // ── compare mode ──
  const comparePdfInputRef = useRef<HTMLInputElement>(null);
  const [compare, setCompare] = useState<{ name: string; countA: number; countB: number } | null>(
    null
  );

  const onPickComparePdf = useCallback(
    async (file: File) => {
      try {
        const bytes = await file.arrayBuffer();
        const { pageCount } = await enqueue(async () => ensureEngine().compareOpen(bytes));
        setCompare({
          name: file.name,
          countA: docRef.current.baseline?.pages.length ?? 0,
          countB: pageCount,
        });
      } catch (err) {
        showToast(`Could not compare: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
    [enqueue, ensureEngine, showToast]
  );

  const compareRenderA = useCallback<
    (pageIndex: number, scale: number) => Promise<RenderResult>
  >(
    (pageIndex, scale) => enqueue(async () => ensureEngine().render(pageIndex, scale)),
    [enqueue, ensureEngine]
  );
  const compareRenderB = useCallback<
    (pageIndex: number, scale: number) => Promise<RenderResult>
  >(
    (pageIndex, scale) => enqueue(async () => ensureEngine().compareRender(pageIndex, scale)),
    [enqueue, ensureEngine]
  );
  const closeCompare = useCallback(() => {
    setCompare(null);
    void enqueue(async () => ensureEngine().compareClose()).catch(() => {});
  }, [enqueue, ensureEngine]);

  // ── markdown import (Word and HTML convert into this same flow) ──
  const mdInputRef = useRef<HTMLInputElement>(null);
  const docxInputRef = useRef<HTMLInputElement>(null);
  const htmlInputRef = useRef<HTMLInputElement>(null);
  const [mdImport, setMdImport] = useState<{
    name: string;
    text: string;
    kind: "md" | "docx" | "html";
    notes: string[];
  } | null>(null);
  const [converting, setConverting] = useState(false);

  const onPickMarkdown = useCallback(
    async (file: File) => {
      if (file.size > 2 * 1024 * 1024) {
        showToast("That markdown file is too large (2 MB max).");
        return;
      }
      try {
        setMdImport({ name: file.name, text: await file.text(), kind: "md", notes: [] });
      } catch (err) {
        showToast(`Could not read that file: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
    [showToast]
  );

  /** Word document → HTML (mammoth) → markdown → the markdown flow. */
  const onPickDocx = useCallback(
    async (file: File) => {
      if (file.size > 25 * 1024 * 1024) {
        showToast("That Word document is too large (25 MB max).");
        return;
      }
      setConverting(true);
      try {
        const { markdown, messages } = await docxToMarkdown(await file.arrayBuffer());
        if (!markdown.trim()) {
          showToast("That Word document has no text to import.");
          return;
        }
        setMdImport({ name: file.name, text: markdown, kind: "docx", notes: messages });
      } catch (err) {
        showToast(
          `Could not convert that Word document: ${err instanceof Error ? err.message : String(err)}`
        );
      } finally {
        setConverting(false);
      }
    },
    [showToast]
  );

  /** HTML file → markdown → the markdown flow. */
  const onPickHtml = useCallback(
    async (file: File) => {
      if (file.size > 8 * 1024 * 1024) {
        showToast("That HTML file is too large (8 MB max).");
        return;
      }
      setConverting(true);
      try {
        const markdown = await htmlToMarkdown(await file.text());
        if (!markdown.trim()) {
          showToast("That HTML file has no text to import.");
          return;
        }
        setMdImport({ name: file.name, text: markdown, kind: "html", notes: [] });
      } catch (err) {
        showToast(
          `Could not convert that HTML file: ${err instanceof Error ? err.message : String(err)}`
        );
      } finally {
        setConverting(false);
      }
    },
    [showToast]
  );

  const mdBuild = useCallback(
    (args: Parameters<PdfEngine["importMd"]>[0]) =>
      enqueue(async () => ensureEngine().importMd(args)),
    [enqueue, ensureEngine]
  );
  const mdRenderPage = useCallback(
    (pageIndex: number, scale: number) =>
      enqueue(async () => ensureEngine().importMdRender(pageIndex, scale)),
    [enqueue, ensureEngine]
  );
  const mdCommit = useCallback(
    () => enqueue(async () => ensureEngine().importMdCommit()),
    [enqueue, ensureEngine]
  );
  const cancelMdImport = useCallback(() => {
    setMdImport(null);
    void enqueue(async () => ensureEngine().importMdClose()).catch(() => {});
  }, [enqueue, ensureEngine]);

  /** A confirmed import opens as a new tab (replacing a pristine blank
   *  one, same as the welcome-overlay file pick). */
  const onMdImported = useCallback(
    (bytes: ArrayBuffer, pageCount: number) => {
      const md = mdImport;
      setMdImport(null);
      setWelcome(false);
      const base = docBaseName(
        (md?.name ?? "Imported").replace(/\.(md|markdown|mdown|txt)$/i, "")
      );
      void enqueue(async () => {
        const tab: DocTab = {
          id: nextTabId(),
          name: `${base}.pdf`,
          bytes,
          stash: EMPTY_DOC,
          formValues: {},
        };
        const replaceId = onlyUntouchedBlank() ? tabsRef.current[0]!.id : null;
        stashActive();
        setTabs((prev) => [...prev.filter((t) => t.id !== replaceId), tab]);
        try {
          await activateTab(tab);
          showToast(
            `Imported ${md?.name ?? "document"} — ${pageCount} page${pageCount === 1 ? "" : "s"}.`
          );
        } catch (err) {
          showToast(
            `Could not open the imported PDF: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      });
    },
    [mdImport, enqueue, onlyUntouchedBlank, stashActive, activateTab, showToast]
  );

  // ── spreadsheet import (shares the worker's import scratch doc —
  //    render/commit/close are the importMd messages) ──
  const sheetInputRef = useRef<HTMLInputElement>(null);
  const [sheetImport, setSheetImport] = useState<{
    name: string;
    sheets: SheetData[];
    truncated: boolean;
  } | null>(null);

  const onPickSheet = useCallback(
    async (file: File) => {
      if (file.size > 20 * 1024 * 1024) {
        showToast("That spreadsheet is too large (20 MB max).");
        return;
      }
      try {
        const { sheets, truncated } = await parseWorkbook(await file.arrayBuffer());
        if (sheets.every((s) => s.rows.length === 0)) {
          showToast("That spreadsheet has no data to import.");
          return;
        }
        setSheetImport({ name: file.name, sheets, truncated });
      } catch (err) {
        showToast(
          `Could not read that spreadsheet: ${err instanceof Error ? err.message : String(err)}`
        );
      }
    },
    [showToast]
  );

  const sheetBuild = useCallback(
    (args: Parameters<PdfEngine["importSheet"]>[0]) =>
      enqueue(async () => ensureEngine().importSheet(args)),
    [enqueue, ensureEngine]
  );
  const cancelSheetImport = useCallback(() => {
    setSheetImport(null);
    void enqueue(async () => ensureEngine().importMdClose()).catch(() => {});
  }, [enqueue, ensureEngine]);

  const onSheetImported = useCallback(
    (bytes: ArrayBuffer, pageCount: number) => {
      const src = sheetImport;
      setSheetImport(null);
      setWelcome(false);
      const base = sheetBaseName(src?.name ?? "Imported");
      void enqueue(async () => {
        const tab: DocTab = {
          id: nextTabId(),
          name: `${base}.pdf`,
          bytes,
          stash: EMPTY_DOC,
          formValues: {},
        };
        const replaceId = onlyUntouchedBlank() ? tabsRef.current[0]!.id : null;
        stashActive();
        setTabs((prev) => [...prev.filter((t) => t.id !== replaceId), tab]);
        try {
          await activateTab(tab);
          showToast(
            `Imported ${src?.name ?? "spreadsheet"} — ${pageCount} page${pageCount === 1 ? "" : "s"}.`
          );
        } catch (err) {
          showToast(
            `Could not open the imported PDF: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      });
    },
    [sheetImport, enqueue, onlyUntouchedBlank, stashActive, activateTab, showToast]
  );

  // ── image import (photos → PDF; shares the import scratch doc) ──
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoImport, setPhotoImport] = useState<{
    name: string;
    images: ResolvedImportImage[];
    failed: string[];
  } | null>(null);

  const onPickImages = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      try {
        const { images, failed } = await resolveImageFiles(files);
        if (images.length === 0) {
          showToast(
            failed.length > 0
              ? `Could not read those images: ${failed[0]}`
              : "No usable images in that selection."
          );
          return;
        }
        const name =
          files.length === 1
            ? files[0]!.name
            : `${imageBaseName(files[0]!.name)} + ${files.length - 1} more`;
        setPhotoImport({ name, images, failed });
      } catch (err) {
        showToast(
          `Could not read those images: ${err instanceof Error ? err.message : String(err)}`
        );
      }
    },
    [showToast]
  );

  const photoBuild = useCallback(
    (args: Parameters<PdfEngine["importPhotos"]>[0]) =>
      enqueue(async () => ensureEngine().importPhotos(args)),
    [enqueue, ensureEngine]
  );
  const cancelPhotoImport = useCallback(() => {
    setPhotoImport(null);
    void enqueue(async () => ensureEngine().importMdClose()).catch(() => {});
  }, [enqueue, ensureEngine]);

  const onPhotosImported = useCallback(
    (bytes: ArrayBuffer, pageCount: number) => {
      const src = photoImport;
      setPhotoImport(null);
      setWelcome(false);
      const base = imageBaseName(src?.images[0]?.name ?? "Images");
      void enqueue(async () => {
        const tab: DocTab = {
          id: nextTabId(),
          name: `${base}.pdf`,
          bytes,
          stash: EMPTY_DOC,
          formValues: {},
        };
        const replaceId = onlyUntouchedBlank() ? tabsRef.current[0]!.id : null;
        stashActive();
        setTabs((prev) => [...prev.filter((t) => t.id !== replaceId), tab]);
        try {
          await activateTab(tab);
          const n = src?.images.length ?? 0;
          showToast(
            `Imported ${n} image${n === 1 ? "" : "s"} — ${pageCount} page${pageCount === 1 ? "" : "s"}.`
          );
        } catch (err) {
          showToast(
            `Could not open the imported PDF: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      });
    },
    [photoImport, enqueue, onlyUntouchedBlank, stashActive, activateTab, showToast]
  );

  // ── form-field creation ──
  const [fieldDraft, setFieldDraft] = useState<{
    pageIndex: number;
    rect: [number, number, number, number];
    kind: "text" | "checkbox" | "combo" | "radio" | "signature";
    name: string;
    multiline: boolean;
    options: string;
    /** Radio: the value THIS button stands for. */
    onValue: string;
  } | null>(null);

  const onCreateFieldRegion = useCallback(
    (pageIndex: number, x1: number, y1: number, x2: number, y2: number) => {
      const rect: [number, number, number, number] = [
        Math.min(x1, x2),
        Math.min(y1, y2),
        Math.max(x1, x2),
        Math.max(y1, y2),
      ];
      // A tall box almost certainly wants to be a multi-line text area.
      const multiline = rect[3] - rect[1] > 40;
      setFieldDraft({
        pageIndex,
        rect,
        kind: "text",
        name: "",
        multiline,
        options: "",
        onValue: "",
      });
    },
    []
  );

  /** Move or resize a field's widget. A rebase, like creating one, so
   *  it lands in undo history — which is why only the drag's END commits
   *  (the overlay previews the rest locally). */
  const doMoveField = useCallback(
    (
      pageIndex: number,
      widgetIndex: number,
      rect: [number, number, number, number],
      phase: DragPhase
    ) => {
      if (phase !== "end") return;
      const before = docRef.current.baseline;
      if (!before || pageOpBusy) return;
      setPageOpBusy(true);
      void enqueue(async () => {
        try {
          if (docRef.current.baseline?.gen !== before.gen) return;
          const {
            bytes,
            pages: nextPages,
            widgets,
          } = await ensureEngine().moveField(pageIndex, widgetIndex, rect);
          const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
          engineGen.current = baseline.gen;
          dispatch({ type: "commit", annots: annotsRef.current, baseline });
          const cur = activeIdRef.current;
          setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
        } catch (err) {
          showToast(`Could not move that field: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
          setPageOpBusy(false);
        }
      });
    },
    [pageOpBusy, enqueue, ensureEngine, showToast]
  );

  const commitFieldDraft = useCallback(() => {
    const d = fieldDraft;
    const before = docRef.current.baseline;
    if (!d || !before || pageOpBusy) return;
    setFieldDraft(null);
    setPageOpBusy(true);
    void enqueue(async () => {
      try {
        if (docRef.current.baseline?.gen !== before.gen) return;
        const { bytes, pages: nextPages, widgets, name } = await ensureEngine().createField(
          d.pageIndex,
          {
            kind: d.kind,
            name: d.name,
            rect: d.rect,
            multiline: d.kind === "text" ? d.multiline : undefined,
            options:
              d.kind === "combo"
                ? d.options.split("\n").map((s) => s.trim()).filter(Boolean)
                : undefined,
            onValue: d.kind === "radio" ? d.onValue : undefined,
          }
        );
        const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
        engineGen.current = baseline.gen;
        dispatch({ type: "commit", annots: annotsRef.current, baseline });
        const cur = activeIdRef.current;
        setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
        setTool(d.kind === "radio" ? "field" : "select");
        showToast(
          d.kind === "radio"
            ? `Added a button to “${name}” — drag another box with the same name to add the next choice.`
            : d.kind === "signature"
              ? `Added signature field “${name}” — sign it in any PDF reader, or use File → Digitally sign.`
              : `Added field “${name}” — fill it in here or in any PDF reader (undoable).`
        );
      } catch (err) {
        showToast(`Could not add field: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setPageOpBusy(false);
      }
    });
  }, [fieldDraft, pageOpBusy, enqueue, ensureEngine, showToast]);

  // ── crop (trim the visible page box) ──
  const [cropDraft, setCropDraft] = useState<{
    pageIndex: number;
    rect: [number, number, number, number];
    target: "current" | "all";
  } | null>(null);

  const onCropRegion = useCallback(
    (pageIndex: number, x1: number, y1: number, x2: number, y2: number) => {
      setCropDraft({
        pageIndex,
        rect: [Math.min(x1, x2), Math.min(y1, y2), Math.max(x1, x2), Math.max(y1, y2)],
        target: "current",
      });
    },
    []
  );

  // ── insert pages from another PDF ──
  const insertPdfInputRef = useRef<HTMLInputElement>(null);
  const combinePdfInputRef = useRef<HTMLInputElement>(null);
  const xfdfInputRef = useRef<HTMLInputElement>(null);
  const [insertDraft, setInsertDraft] = useState<{
    name: string;
    bytes: ArrayBuffer;
    position: "afterCurrent" | "start" | "end";
    range: "all" | "custom";
    from: number;
    to: number;
    /** Null until the probe returns. */
    pageCount: number | null;
    thumbs: { pageIndex: number; width: number; height: number; pixels: ArrayBuffer }[];
  } | null>(null);

  const onPickInsertPdf = useCallback(
    async (file: File) => {
      const bytes = await file.arrayBuffer();
      setInsertDraft({
        name: file.name,
        bytes,
        position: "afterCurrent",
        range: "all",
        from: 1,
        to: 1,
        pageCount: null,
        thumbs: [],
      });
      try {
        const { pageCount, thumbs } = await enqueue(async () =>
          ensureEngine().probePdf(bytes)
        );
        setInsertDraft((d) =>
          d && d.bytes === bytes ? { ...d, pageCount, thumbs, from: 1, to: pageCount } : d
        );
      } catch (err) {
        setInsertDraft(null);
        showToast(`Could not read that PDF: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
    [enqueue, ensureEngine, showToast]
  );

  /** Graft a page range from another PDF at `at`, remapping the overlay
   *  so annotations and internal link targets follow their pages, and
   *  commit as one undoable step. Returns how many pages landed.
   *
   *  Shared by the single-file insert and the multi-file combine — the
   *  remap is the fiddly part and must not exist twice.
   *
   *  Caller supplies the op queue: combine needs ONE queue slot around
   *  its whole loop, not one per file. */
  const graftPagesAt = useCallback(
    async (at: number, bytes: ArrayBuffer, from: number, to: number): Promise<number> => {
      const before = docRef.current.baseline;
      if (!before) return 0;
      const {
        bytes: nextBytes,
        pages: nextPages,
        widgets,
        count,
      } = await ensureEngine().insertPages(at, bytes, from, to);
      const baseline: Baseline = { bytes: nextBytes, pages: nextPages, widgets, gen: nextGen() };
      engineGen.current = baseline.gen;
      // Everything at/after the insertion point slides down `count`
      // pages — including internal link targets.
      const remapped = annotsRef.current.map((a) => {
        const pageIndex = a.pageIndex >= at ? a.pageIndex + count : a.pageIndex;
        if (a.kind === "link") {
          const page = a.page !== undefined && a.page >= at ? a.page + count : a.page;
          if (pageIndex === a.pageIndex && page === a.page) return a;
          return { ...a, pageIndex, page };
        }
        return pageIndex === a.pageIndex ? a : { ...a, pageIndex };
      });
      dispatch({ type: "commit", annots: remapped, baseline });
      const cur = activeIdRef.current;
      setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes: nextBytes } : t)));
      return count;
    },
    [ensureEngine]
  );

  const runInsertPages = useCallback(() => {
    const d = insertDraft;
    const before = docRef.current.baseline;
    if (!d || !before || pageOpBusy) return;
    const at =
      d.position === "start"
        ? 0
        : d.position === "end"
          ? before.pages.length
          : Math.min(currentPage + 1, before.pages.length);
    const last = (d.pageCount ?? 1) - 1;
    const from = d.range === "all" ? 0 : Math.min(Math.max(0, d.from - 1), last);
    const to = d.range === "all" ? last : Math.min(Math.max(from, d.to - 1), last);
    setInsertDraft(null);
    setPageOpBusy(true);
    void enqueue(async () => {
      try {
        if (docRef.current.baseline?.gen !== before.gen) return;
        const count = await graftPagesAt(at, d.bytes, from, to);
        showToast(
          `Inserted ${count} page${count === 1 ? "" : "s"} from ${d.name} (undoable).`
        );
      } catch (err) {
        showToast(`Insert failed: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setPageOpBusy(false);
      }
    });
  }, [insertDraft, pageOpBusy, currentPage, enqueue, graftPagesAt, showToast]);

  // ── combine several PDFs into this one ──
  const [combineOpen, setCombineOpen] = useState(false);
  const [combineAt, setCombineAt] = useState<"end" | "start">("end");
  const [combineBusy, setCombineBusy] = useState<{ done: number; total: number } | null>(null);
  const [combineList, setCombineList] = useState<
    { id: string; name: string; bytes: ArrayBuffer; size: number; pageCount: number | null }[]
  >([]);

  /** Read and probe the chosen PDFs, appending them to the list. Probing
   *  is what gives each row a page count, so the user can see what they
   *  are about to add rather than trusting the file name. */
  const onPickCombinePdfs = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const bytes = await file.arrayBuffer();
        const id = `${file.name}:${file.size}:${Math.random().toString(36).slice(2, 7)}`;
        setCombineList((prev) => [
          ...prev,
          { id, name: file.name, bytes, size: file.size, pageCount: null },
        ]);
        try {
          const { pageCount } = await enqueue(async () => ensureEngine().probePdf(bytes));
          setCombineList((prev) =>
            prev.map((f) => (f.id === id ? { ...f, pageCount } : f))
          );
        } catch (err) {
          setCombineList((prev) => prev.filter((f) => f.id !== id));
          showToast(
            `Could not read ${file.name}: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      }
    },
    [enqueue, ensureEngine, showToast]
  );

  const moveCombineItem = useCallback((id: string, delta: -1 | 1) => {
    setCombineList((prev) => {
      const i = prev.findIndex((f) => f.id === id);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  }, []);

  /** Graft every listed file, in list order, in one queue slot. Each file
   *  is its own undo step — undoing peels the last one back off. */
  const runCombine = useCallback(() => {
    const files = combineList;
    const before = docRef.current.baseline;
    if (files.length === 0 || !before || pageOpBusy || combineBusy) return;
    setPageOpBusy(true);
    setCombineBusy({ done: 0, total: files.length });
    void enqueue(async () => {
      let added = 0;
      try {
        // Track the insertion point locally and advance it by what each
        // graft actually added. It CANNOT be re-read from docRef inside
        // this loop: the ref only catches up once React commits the
        // dispatch, so every file would land at the same offset and the
        // list order would come out reversed.
        let at = combineAt === "start" ? 0 : before.pages.length;
        for (const [n, f] of files.entries()) {
          const last = (f.pageCount ?? 1) - 1;
          const count = await graftPagesAt(at, f.bytes, 0, Math.max(0, last));
          at += count;
          added += count;
          setCombineBusy({ done: n + 1, total: files.length });
        }
        setCombineOpen(false);
        setCombineList([]);
        showToast(
          `Combined ${files.length} file${files.length === 1 ? "" : "s"} — ${added} page${added === 1 ? "" : "s"} added.`
        );
      } catch (err) {
        showToast(`Combine failed: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setCombineBusy(null);
        setPageOpBusy(false);
      }
    });
  }, [combineList, combineAt, combineBusy, pageOpBusy, enqueue, graftPagesAt, showToast]);

  // ── OCR (invisible text layer for scanned pages) ──
  const [ocrOpen, setOcrOpen] = useState(false);
  const [ocrScope, setOcrScope] = useState<"all" | "current">("all");
  const [ocrSkip, setOcrSkip] = useState(true);
  const [ocrState, setOcrState] = useState<{ page: number; total: number; progress: number } | null>(null);

  const runOcr = useCallback(async () => {
    const before = docRef.current.baseline;
    if (!before || pageOpBusy || ocrState) return;
    const targets =
      ocrScope === "current"
        ? [currentPage]
        : [...Array(before.pages.length).keys()];
    setOcrState({ page: 0, total: targets.length, progress: 0 });
    try {
      // The engine (~15 MB of wasm + language data, self-hosted under
      // /ocr) loads lazily on first run and stays local — no uploads.
      const { createWorker } = await import("tesseract.js");
      const tess = await createWorker("eng", 1, {
        workerPath: "/ocr/worker.min.js",
        corePath: "/ocr",
        langPath: "/ocr",
        gzip: true,
        cacheMethod: "none",
        logger: (m) => {
          if (m.status === "recognizing text") {
            setOcrState((s) => (s ? { ...s, progress: m.progress } : s));
          }
        },
      });
      const OCR_SCALE = 2.5; // ≈180 dpi — accuracy/speed compromise
      const results: { pageIndex: number; words: OcrWord[] }[] = [];
      let skipped = 0;
      let wordCount = 0;
      try {
        for (let i = 0; i < targets.length; i += 1) {
          const pageIndex = targets[i]!;
          setOcrState((s) => (s ? { ...s, page: i + 1, progress: 0 } : s));
          if (ocrSkip) {
            const objs = await enqueue(async () => ensureEngine().pageObjects(pageIndex));
            if (objs.lines.length > 0) {
              skipped += 1;
              continue;
            }
          }
          const { width, height, pixels } = await enqueue(async () =>
            ensureEngine().render(pageIndex, OCR_SCALE)
          );
          // tesseract's worker wants an encodable image, not raw
          // ImageData — round-trip through a canvas → PNG blob.
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          canvas
            .getContext("2d")!
            .putImageData(new ImageData(new Uint8ClampedArray(pixels), width, height), 0, 0);
          const blob = await new Promise<Blob>((resolve, reject) =>
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("raster encode failed"))), "image/png")
          );
          const rr = await tess.recognize(blob, {}, { blocks: true, text: false });
          const words: OcrWord[] = [];
          for (const b of rr.data.blocks ?? []) {
            for (const par of b.paragraphs) {
              for (const line of par.lines) {
                for (const w of line.words) {
                  if (!w.text.trim() || (w.confidence ?? 0) < 35) continue;
                  words.push({
                    text: w.text,
                    bbox: [
                      w.bbox.x0 / OCR_SCALE,
                      w.bbox.y0 / OCR_SCALE,
                      w.bbox.x1 / OCR_SCALE,
                      w.bbox.y1 / OCR_SCALE,
                    ],
                  });
                }
              }
            }
          }
          if (words.length > 0) {
            results.push({ pageIndex, words });
            wordCount += words.length;
          }
        }
      } finally {
        void tess.terminate();
      }
      if (docRef.current.baseline?.gen !== before.gen) {
        showToast("Document changed while OCR was running — nothing applied.");
        return;
      }
      if (results.length === 0) {
        showToast(
          skipped > 0
            ? `No text recognised (${skipped} page${skipped === 1 ? "" : "s"} already had text).`
            : "No text recognised."
        );
        return;
      }
      setPageOpBusy(true);
      try {
        const { bytes, pages: nextPages, widgets } = await enqueue(async () =>
          ensureEngine().textLayer(results)
        );
        const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
        engineGen.current = baseline.gen;
        dispatch({ type: "commit", annots: annotsRef.current, baseline });
        const cur = activeIdRef.current;
        setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
        setOcrOpen(false);
        showToast(
          `Recognised ${wordCount} words on ${results.length} page${results.length === 1 ? "" : "s"} — text is now selectable (undoable).`
        );
      } finally {
        setPageOpBusy(false);
      }
    } catch (err) {
      showToast(`OCR failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setOcrState(null);
    }
  }, [pageOpBusy, ocrState, ocrScope, ocrSkip, currentPage, enqueue, ensureEngine, showToast]);

  /** Write the whole bookmark tree back (rebases → undoable). */
  const doSetOutline = useCallback(
    (items: OutlineItem[]) => {
      setOutlineItems(items); // optimistic; the re-read below confirms it
      const before = docRef.current.baseline;
      if (!before || pageOpBusy) return;
      setPageOpBusy(true);
      void enqueue(async () => {
        try {
          if (docRef.current.baseline?.gen !== before.gen) return;
          const { bytes, pages: nextPages, widgets } = await ensureEngine().setOutline(items);
          const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
          engineGen.current = baseline.gen;
          dispatch({ type: "commit", annots: annotsRef.current, baseline });
          const cur = activeIdRef.current;
          setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
        } catch (err) {
          showToast(`Bookmark change failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
          setPageOpBusy(false);
        }
      });
    },
    [pageOpBusy, enqueue, ensureEngine, showToast]
  );

  // ── find & redact ──

  /** Scan every page for the requested phrase / patterns. Read-only:
   *  the document isn't touched until the user marks hits. */
  const runScan = useCallback(
    (req: ScanRequest) => {
      let patterns;
      try {
        patterns = compilePatterns(req);
      } catch {
        setScanError("That regular expression isn't valid.");
        return;
      }
      if (patterns.length === 0) {
        setScanError("Enter some text to find, or tick a pattern.");
        return;
      }
      setScanError(null);
      setScanBusy(true);
      void enqueue(async () => {
        try {
          const r = await ensureEngine().scanText(patterns);
          setScanResults(r);
          // Everything ticked by default: the common case is "redact all
          // of these", and unticking a few is less work than ticking 200.
          setScanSelected(new Set(r.hits.map((_, i) => i)));
        } catch (err) {
          setScanError(err instanceof Error ? err.message : String(err));
          setScanResults(null);
        } finally {
          setScanBusy(false);
        }
      });
    },
    [enqueue, ensureEngine]
  );

  /** Turn the ticked hits into redaction marks — ONE undo step. */
  const markScanHits = useCallback(() => {
    const hits = scanResults?.hits ?? [];
    const chosen = [...scanSelected].sort((a, b) => a - b).map((i) => hits[i]!);
    if (chosen.length === 0) return;
    dispatch({
      type: "commit",
      annots: [
        ...annotsRef.current,
        ...chosen.map(
          (h): Annotation => ({
            id: nextAnnotId(),
            kind: "redact",
            pageIndex: h.pageIndex,
            quads: h.quads,
          })
        ),
      ],
    });
    setFindRedactOpen(false);
    setScanResults(null);
    setScanSelected(new Set());
    setTool("select");
    showToast(
      `${chosen.length} redaction mark${chosen.length === 1 ? "" : "s"} added — review them, then download to remove the content for good`
    );
  }, [scanResults, scanSelected, showToast]);

  /** Write document properties (rebases → undoable). The panel's own
   *  re-read effect refreshes `docInfo` from the new baseline. */
  const doSetDocInfo = useCallback(
    (fields: Partial<DocInfoFields>) => {
      const before = docRef.current.baseline;
      if (!before || pageOpBusy) return;
      setPageOpBusy(true);
      void enqueue(async () => {
        try {
          if (docRef.current.baseline?.gen !== before.gen) return;
          const {
            bytes,
            pages: nextPages,
            widgets,
          } = await ensureEngine().setDocumentInfo(fields);
          const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
          engineGen.current = baseline.gen;
          dispatch({ type: "commit", annots: annotsRef.current, baseline });
          const cur = activeIdRef.current;
          setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
          showToast("Document properties updated");
        } catch (err) {
          showToast(
            `Properties change failed: ${err instanceof Error ? err.message : String(err)}`
          );
        } finally {
          setPageOpBusy(false);
        }
      });
    },
    [pageOpBusy, enqueue, ensureEngine, showToast]
  );

  /** Apply accessibility repairs (rebases → undoable). Only the flags
   *  the file can carry honestly: tagging and alternate text are content
   *  decisions and deliberately have no button. */
  const doSetAccessibility = useCallback(
    (patch: A11yPatch) => {
      const before = docRef.current.baseline;
      if (!before || pageOpBusy) return;
      setPageOpBusy(true);
      void enqueue(async () => {
        try {
          if (docRef.current.baseline?.gen !== before.gen) return;
          const {
            bytes,
            pages: nextPages,
            widgets,
          } = await ensureEngine().setAccessibility(patch);
          const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
          engineGen.current = baseline.gen;
          dispatch({ type: "commit", annots: annotsRef.current, baseline });
          const cur = activeIdRef.current;
          setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
          // Re-read rather than patching the facts by hand: the audit
          // must describe the document, not what we think we just did.
          const facts = await ensureEngine().accessibilityFacts();
          setA11yFacts(facts);
          showToast("Accessibility updated");
        } catch (err) {
          showToast(
            `Accessibility change failed: ${err instanceof Error ? err.message : String(err)}`
          );
        } finally {
          setPageOpBusy(false);
        }
      });
    },
    [pageOpBusy, enqueue, ensureEngine, showToast]
  );

  /** Context menu "Add bookmark here" — reads the live outline first
   *  (the bookmarks panel may be closed, leaving `outlineItems` stale). */
  const addBookmarkAt = useCallback(
    (pageIndex: number) => {
      void enqueue(async () => {
        const items = await ensureEngine().outline();
        doSetOutline([
          ...items,
          { title: `Page ${pageIndex + 1}`, page: pageIndex, level: 0, open: true },
        ]);
        showToast(`Bookmark added for page ${pageIndex + 1}`);
      }).catch(() => showToast("Could not read the bookmarks"));
    },
    [enqueue, ensureEngine, doSetOutline, showToast]
  );

  /** Burn headers/footers/watermark into the doc (decorate op). */
  const doDecorate = useCallback(
    (cfg: { headerFooter?: HeaderFooterConfig; watermark?: WatermarkConfig }) => {
      const before = docRef.current.baseline;
      if (!before || pageOpBusy) return;
      setPageOpBusy(true);
      void enqueue(async () => {
        try {
          if (docRef.current.baseline?.gen !== before.gen) return;
          const { bytes, pages: nextPages, widgets } = await ensureEngine().decorate(cfg);
          const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
          engineGen.current = baseline.gen;
          dispatch({ type: "commit", annots: annotsRef.current, baseline });
          const cur = activeIdRef.current;
          setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
        } catch (err) {
          showToast(`Could not apply: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
          setPageOpBusy(false);
        }
      });
    },
    [pageOpBusy, enqueue, ensureEngine, showToast]
  );

  /** Link create/edit dialog state. `id` null = creating a new link at
   *  `rect` on `pageIndex`; otherwise editing that link. */
  const [linkDraft, setLinkDraft] = useState<{
    id: string | null;
    pageIndex: number;
    rect: [number, number, number, number];
    mode: "url" | "page";
    url: string;
    page: number;
  } | null>(null);

  const onCreateLink = useCallback(
    (pageIndex: number, x1: number, y1: number, x2: number, y2: number) => {
      setLinkDraft({
        id: null,
        pageIndex,
        rect: [Math.min(x1, x2), Math.min(y1, y2), Math.max(x1, x2), Math.max(y1, y2)],
        mode: "url",
        url: "https://",
        page: Math.min(pageIndex + 1, (docRef.current.baseline?.pages.length ?? 1) - 1),
      });
    },
    []
  );

  const onEditLink = useCallback((a: LinkAnnotation) => {
    setLinkDraft({
      id: a.id,
      pageIndex: a.pageIndex,
      rect: a.rect,
      mode: a.url ? "url" : "page",
      url: a.url ?? "https://",
      page: a.page ?? 0,
    });
  }, []);

  const onFollowLink = useCallback(
    (a: LinkAnnotation) => {
      if (a.page !== undefined) {
        scrollToPageRef.current(a.page);
        return;
      }
      if (!a.url) return;
      // Opening an external URL is the user leaving the page — confirm
      // it, and show what they're actually about to visit.
      if (window.confirm(`Open this link in a new tab?\n\n${a.url}`)) {
        window.open(a.url, "_blank", "noopener,noreferrer");
      }
    },
    []
  );

  const commitLinkDraft = useCallback(() => {
    const d = linkDraft;
    if (!d) return;
    const target =
      d.mode === "url"
        ? { url: d.url.trim() ? d.url.trim() : "https://example.com" }
        : { page: Math.max(0, d.page) };
    if (d.id) {
      dispatch({
        type: "commit",
        annots: annotsRef.current.map((a) =>
          a.id === d.id && a.kind === "link"
            ? { id: a.id, kind: "link", pageIndex: a.pageIndex, rect: a.rect, ...target }
            : a
        ),
      });
    } else {
      const id = nextAnnotId();
      dispatch({
        type: "commit",
        annots: [
          ...annotsRef.current,
          { id, kind: "link", pageIndex: d.pageIndex, rect: d.rect, ...target },
        ],
      });
      selectAnnot(id);
      setTool("select");
    }
    setLinkDraft(null);
  }, [linkDraft, selectAnnot]);

  const onCreateBgBlock = useCallback(
    (pageIndex: number, x1: number, y1: number, x2: number, y2: number) => {
      doFillOp(
        {
          kind: "add",
          bbox: [Math.min(x1, x2), Math.min(y1, y2), Math.max(x1, x2), Math.max(y1, y2)],
          color: "#e9ecef",
        },
        pageIndex
      );
      setTool("select"); // the new block lands selected — recolour right away
    },
    [doFillOp]
  );

  // ── unified object selection (annotations + fields + page content) ──
  //
  // One selection can hold any mix of them. Annotations live in the
  // overlay and move client-side; page content and form fields live in
  // the document and move through the worker. A mixed edit commits BOTH
  // halves in one dispatch, so the group is one undo step.
  //
  // Annotation-only sets keep the classic `annots` shape — it drives the
  // annotation toolbar, styling, clipboard and duplicate. Anything mixed
  // becomes a `region`.

  /** This page's form fields as selection items, indexed the way the
   *  worker indexes them ("w:<n>" = n-th fillable widget on the page). */
  const widgetItemsOn = useCallback((pageIndex: number): RegionItem[] => {
    const out: RegionItem[] = [];
    let n = 0;
    for (const w of docRef.current.baseline?.widgets ?? []) {
      if (w.pageIndex !== pageIndex) continue;
      out.push({ id: `w:${n}`, kind: "widget", bbox: [...w.rect] as Box });
      n += 1;
    }
    return out;
  }, []);

  /** This page's annotations as selection items. */
  const annotItemsOn = useCallback(
    (pageIndex: number): RegionItem[] =>
      annotsRef.current
        .filter((a) => a.pageIndex === pageIndex)
        .map((a) => ({
          id: annotItemId(a.id),
          kind: "annot" as const,
          bbox: annotBounds(a),
        })),
    []
  );

  const selectionFor = useCallback(
    (pageIndex: number, items: RegionItem[]): Selection => {
      if (items.length === 0) return NO_SELECTION;
      if (items.every(isAnnotItem)) {
        return { kind: "annots", ids: items.map(annotIdOf), editingId: null };
      }
      return { kind: "region", pageIndex, items };
    },
    []
  );

  /** The current selection as items on `pageIndex` — the base a marquee
   *  or modifier+click extends. Selections on another page don't carry
   *  over: a region is one page's worth of objects. */
  const currentItemsOn = useCallback(
    (pageIndex: number): RegionItem[] => {
      const s = selRef.current;
      if (s.kind === "region") return s.pageIndex === pageIndex ? [...s.items] : [];
      if (s.kind === "annots") {
        const byId = new Map(annotsRef.current.map((a) => [a.id, a]));
        return s.ids
          .map((id) => byId.get(id))
          .filter((a): a is Annotation => !!a && a.pageIndex === pageIndex)
          .map((a) => ({
            id: annotItemId(a.id),
            kind: "annot" as const,
            bbox: annotBounds(a),
          }));
      }
      if (s.kind === "fill" && s.pageIndex === pageIndex) {
        return [{ id: `f:${s.fill.id}`, kind: "fill", bbox: s.fill.bbox }];
      }
      return [];
    },
    []
  );

  /** Everything a marquee covers, in one pass: annotations and fields
   *  are hit-tested here (the UI holds both lists); page content is a
   *  worker sweep. An image/fill selection is promoted through the
   *  worker so it keeps a movable id. */
  const gatherItems = useCallback(
    async (pageIndex: number, rect: Box, base: RegionItem[]): Promise<RegionItem[]> => {
      const engine = ensureEngine();
      const overlay = [
        ...annotItemsOn(pageIndex),
        ...widgetItemsOn(pageIndex),
      ].filter((i) => boxesIntersect(i.bbox, rect));
      let content: RegionItem[] = [];
      try {
        content = await engine.regionSelect(pageIndex, rect);
      } catch {
        /* content sweep unavailable — the overlay still selects */
      }
      return mergeRegionItems(base, [...overlay, ...content]);
    },
    [ensureEngine, annotItemsOn, widgetItemsOn]
  );

  /** Modifier+drag marquee — the one marquee there is now. It takes
   *  everything the rect covers, annotations and page content alike, and
   *  ADDS to whatever is already selected on that page (so with nothing
   *  selected it simply selects, and dragging again extends). There used
   *  to be two: an annotations-only one on the Select tool and a
   *  content-only one on Edit, which is exactly the split the merged
   *  pointer removes. */
  const onMarqueeSelect = useCallback(
    (pageIndex: number, rect: [number, number, number, number], additive: boolean) => {
      // An open text edit is DISCARDED (a marquee is new intent, not a
      // click-away commit) — sync ref clear so its blur can't commit.
      selRef.current = clearKind(selRef.current, "textEdit");
      // Plain drag starts a fresh selection; a modifier drag adds to what
      // is already selected on this page.
      const base = additive ? currentItemsOn(pageIndex) : [];
      if (!additive) setSel((cur) => (cur.kind === "region" ? cur : NO_SELECTION));
      void enqueue(async () => {
        try {
          const items = await gatherItems(pageIndex, rect, base);
          if (items.length === 0) {
            showToast("Nothing selectable in that area.");
            return;
          }
          setSel(selectionFor(pageIndex, items));
        } catch (err) {
          showToast(`Selection failed: ${err instanceof Error ? err.message : String(err)}`);
        }
      });
    },
    [enqueue, gatherItems, currentItemsOn, selectionFor, showToast]
  );

  /** Displace a mixed set of objects. Annotations are translated in the
   *  overlay, page content and fields go to the worker, and both land in
   *  ONE commit — so a mixed drag, nudge or align is a single undo step.
   *  `delta` is per item, which is what align/distribute need. */
  const moveRegionItems = useCallback(
    (
      pageIndex: number,
      items: readonly RegionItem[],
      delta: (item: RegionItem) => [number, number]
    ) => {
      const before = docRef.current.baseline;
      if (!before || pageOpBusy) return;
      const { annots: annotItems, doc: docItems } = splitRegionItems(items);
      const annotDeltas = new Map<string, [number, number]>();
      for (const i of annotItems) annotDeltas.set(annotIdOf(i), delta(i));
      const nextAnnots = annotsRef.current.map((a) => {
        const d = annotDeltas.get(a.id);
        return d && (d[0] !== 0 || d[1] !== 0) ? translateAnnot(a, d[0], d[1]) : a;
      });
      const shifted = items.map((i) => {
        const [dx, dy] = delta(i);
        return shiftRegionItem(i, dx, dy);
      });
      // Every still-selected document object is sent, moving or not:
      // rewriting the page renumbers them all, and the worker returns
      // fresh ids only for what it was told about.
      const moves: RegionMove[] = docItems.map((i) => {
        const [dx, dy] = delta(i);
        return { id: i.id, dx, dy };
      });
      const touchesDoc = moves.some((m) => m.dx !== 0 || m.dy !== 0);
      if (!touchesDoc) {
        // Overlay-only change — no rebase, so document ids stay valid.
        if ([...annotDeltas.values()].some(([dx, dy]) => dx !== 0 || dy !== 0)) {
          dispatch({ type: "commit", annots: nextAnnots });
        }
        setSel(selectionFor(pageIndex, shifted));
        return;
      }
      setPageOpBusy(true);
      void enqueue(async () => {
        try {
          if (docRef.current.baseline?.gen !== before.gen) return;
          const {
            bytes,
            pages: nextPages,
            widgets,
            items: fresh,
          } = await ensureEngine().regionMove(pageIndex, moves);
          const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
          engineGen.current = baseline.gen;
          // Overlay + document in ONE history entry.
          dispatch({ type: "commit", annots: nextAnnots, baseline });
          const cur = activeIdRef.current;
          setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
          // Ids are positional and the rewrite renumbers them, so the
          // selection adopts the worker's re-resolved items. Keeping the
          // old ids (with shifted boxes) made a SECOND move erase the
          // wrong objects and leave the dragged ones behind.
          setSel(selectionFor(pageIndex, [...fresh, ...shifted.filter(isAnnotItem)]));
        } catch (err) {
          showToast(`Move failed: ${err instanceof Error ? err.message : String(err)}`);
          setSel((s) => clearKind(s, "region"));
        } finally {
          setPageOpBusy(false);
        }
      });
    },
    [pageOpBusy, enqueue, ensureEngine, selectionFor, showToast]
  );

  /** Drag the selection frame: everything moves by the same delta. */
  const onRegionMove = useCallback(
    (dx: number, dy: number) => {
      const s = selRef.current;
      if (s.kind !== "region") return;
      moveRegionItems(s.pageIndex, s.items, () => [dx, dy]);
    },
    [moveRegionItems]
  );
  onRegionMoveRef.current = onRegionMove;

  /** Delete a mixed selection: annotations drop out of the overlay,
   *  content is erased and fields are removed from the form — one
   *  commit, one undo step. */
  const deleteRegion = useCallback(() => {
    const s = selRef.current;
    if (s.kind !== "region") return;
    const before = docRef.current.baseline;
    if (!before || pageOpBusy) return;
    const { annots: annotItems, doc: docItems } = splitRegionItems(s.items);
    const goneIds = new Set(annotItems.map(annotIdOf));
    const nextAnnots = annotsRef.current.filter((a) => !goneIds.has(a.id));
    if (docItems.length === 0) {
      dispatch({ type: "commit", annots: nextAnnots });
      setSel(NO_SELECTION);
      return;
    }
    setPageOpBusy(true);
    void enqueue(async () => {
      try {
        if (docRef.current.baseline?.gen !== before.gen) return;
        const { bytes, pages: nextPages, widgets } = await ensureEngine().regionDelete(
          s.pageIndex,
          docItems.map((i) => i.id)
        );
        const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
        engineGen.current = baseline.gen;
        dispatch({ type: "commit", annots: nextAnnots, baseline });
        const cur = activeIdRef.current;
        setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
        setSel(NO_SELECTION);
      } catch (err) {
        showToast(`Delete failed: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setPageOpBusy(false);
      }
    });
  }, [pageOpBusy, enqueue, ensureEngine, showToast]);
  deleteRegionRef.current = deleteRegion;

  /** Align / distribute a mixed selection — the geometry runs on plain
   *  boxes, so annotations, content and fields all take part. */
  const alignRegion = useCallback(
    (mode: AlignMode) => {
      const s = selRef.current;
      if (s.kind !== "region" || s.items.length < 2) return;
      const deltas = alignBoxes(s.items, mode);
      moveRegionItems(s.pageIndex, s.items, (i) => deltas.get(i.id) ?? [0, 0]);
    },
    [moveRegionItems]
  );

  const distributeRegion = useCallback(
    (axis: "h" | "v") => {
      const s = selRef.current;
      if (s.kind !== "region" || s.items.length < 3) return;
      const deltas = distributeBoxes(s.items, axis);
      moveRegionItems(s.pageIndex, s.items, (i) => deltas.get(i.id) ?? [0, 0]);
    },
    [moveRegionItems]
  );
  alignRegionRef.current = alignRegion;
  distributeRegionRef.current = distributeRegion;

  /** Arrow-key nudge of a mixed selection. Each rebase is a full page
   *  rewrite, so presses accumulate into a live preview offset and flush
   *  as ONE move once the key goes quiet. */
  const nudgePending = useRef<{ pageIndex: number; dx: number; dy: number } | null>(null);
  const nudgeTimer = useRef<number | null>(null);
  const [nudgePreview, setNudgePreview] = useState<[number, number]>([0, 0]);
  const flushNudge = useCallback(() => {
    if (nudgeTimer.current !== null) {
      window.clearTimeout(nudgeTimer.current);
      nudgeTimer.current = null;
    }
    const p = nudgePending.current;
    nudgePending.current = null;
    setNudgePreview([0, 0]);
    const s = selRef.current;
    if (!p || s.kind !== "region" || s.pageIndex !== p.pageIndex) return;
    if (p.dx === 0 && p.dy === 0) return;
    moveRegionItems(p.pageIndex, s.items, () => [p.dx, p.dy]);
  }, [moveRegionItems]);

  const nudgeRegion = useCallback(
    (dx: number, dy: number) => {
      const s = selRef.current;
      if (s.kind !== "region") return;
      const p = nudgePending.current;
      const next =
        p && p.pageIndex === s.pageIndex
          ? { pageIndex: s.pageIndex, dx: p.dx + dx, dy: p.dy + dy }
          : { pageIndex: s.pageIndex, dx, dy };
      nudgePending.current = next;
      setNudgePreview([next.dx, next.dy]);
      if (nudgeTimer.current !== null) window.clearTimeout(nudgeTimer.current);
      nudgeTimer.current = window.setTimeout(flushNudge, NUDGE_FLUSH_MS);
    },
    [flushNudge]
  );
  nudgeRegionRef.current = nudgeRegion;
  // A held arrow key must not be left un-flushed if the selection or the
  // document changes underneath it.
  useEffect(
    () => () => {
      if (nudgeTimer.current !== null) window.clearTimeout(nudgeTimer.current);
    },
    []
  );

  /** Modifier+click: add or
   *  remove ONE object — annotation, form field or page content —
   *  whichever sits under the pointer, topmost first. */
  const onToggleObjectAt = useCallback(
    (pageIndex: number, x: number, y: number) => {
      if (pageOpBusy) return;
      // An open text edit commits first, exactly like clicking away.
      const open = selRef.current;
      if (open.kind === "textEdit") {
        const el = document.querySelector<HTMLInputElement | HTMLTextAreaElement>(
          ".pe-textedit"
        );
        onCommitTextEdit(el ? el.value : open.edit.text);
      }
      const base = currentItemsOn(pageIndex);
      // Overlay first: an annotation or field under the pointer wins
      // over the page content beneath it.
      const overlayHit = annotAt(pageIndex, x, y);
      const hitItem: RegionItem | null = overlayHit
        ? { id: annotItemId(overlayHit.id), kind: "annot", bbox: annotBounds(overlayHit) }
        : (widgetItemsOn(pageIndex).find(
            (w) => x >= w.bbox[0] && x <= w.bbox[2] && y >= w.bbox[1] && y <= w.bbox[3]
          ) ?? null);
      if (hitItem) {
        setSel(selectionFor(pageIndex, toggleRegionItem(base, hitItem)));
        return;
      }
      void enqueue(async () => {
        try {
          const engine = ensureEngine();
          const item = await engine.regionHit(pageIndex, x, y);
          if (!item) {
            showToast("Nothing selectable there.");
            return;
          }
          // The image selection carries no id — re-resolve it by
          // hit-testing its own centre, images only.
          let start = base;
          const cur = selRef.current;
          if (cur.kind === "image" && cur.pageIndex === pageIndex) {
            const [ix0, iy0, ix1, iy1] = cur.bbox;
            const prev = await engine.regionHit(
              pageIndex,
              (ix0 + ix1) / 2,
              (iy0 + iy1) / 2,
              "image"
            );
            start = prev ? [prev] : [];
          }
          setSel(selectionFor(pageIndex, toggleRegionItem(start, item)));
        } catch (err) {
          showToast(`Selection failed: ${err instanceof Error ? err.message : String(err)}`);
        }
      });
    },
    [
      pageOpBusy,
      enqueue,
      ensureEngine,
      onCommitTextEdit,
      showToast,
      annotAt,
      currentItemsOn,
      widgetItemsOn,
      selectionFor,
    ]
  );

  /** Is `item` exactly what's selected right now — nothing more, nothing
   *  less? That's the test for "pressed the same thing twice", which is
   *  what turns a select into an edit. */
  const isSoleSelection = useCallback((item: RegionItem, pageIndex: number): boolean => {
    const s = selRef.current;
    if (s.kind === "region") {
      return s.pageIndex === pageIndex && s.items.length === 1 && s.items[0]!.id === item.id;
    }
    if (s.kind === "annots") {
      return s.ids.length === 1 && isAnnotItem(item) && s.ids[0] === annotIdOf(item);
    }
    if (s.kind === "image") return item.kind === "image" && s.pageIndex === pageIndex;
    if (s.kind === "fill") {
      return item.kind === "fill" && s.pageIndex === pageIndex && `f:${s.fill.id}` === item.id;
    }
    return false;
  }, []);


  /** Plain click INSIDE a selection frame: collapse to the one object
   *  under the pointer (same feel as tapping one member of a
   *  multi-selection of annotations). */
  const onRegionTapAt = useCallback(
    (pageIndex: number, x: number, y: number) => {
      const s = selRef.current;
      if (s.kind !== "region") return;
      const inside = s.items.filter(
        (i) => x >= i.bbox[0] && x <= i.bbox[2] && y >= i.bbox[1] && y <= i.bbox[3]
      );
      if (inside.length === 0) return;
      // Smallest wins — the same rule the worker's point hit-test uses.
      const one = inside.reduce((best, i) =>
        (i.bbox[2] - i.bbox[0]) * (i.bbox[3] - i.bbox[1]) <
        (best.bbox[2] - best.bbox[0]) * (best.bbox[3] - best.bbox[1])
          ? i
          : best
      );
      setSel(selectionFor(pageIndex, [one]));
    },
    [selectionFor]
  );

  // ── font library / uploaded fonts ──
  const [customFonts, setCustomFonts] = useState<CustomFont[]>([]);
  const customFontsRef = useRef(customFonts);
  customFontsRef.current = customFonts;
  const fontUploadInputRef = useRef<HTMLInputElement>(null);
  /** ensureFontFace results: variant key → CSS family name. */
  const [fontFaces, setFontFaces] = useState<Record<string, string>>({});

  const onUploadFont = useCallback(
    async (file: File) => {
      try {
        const bytes = await file.arrayBuffer();
        const id = `upload:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
        const label = file.name.replace(/\.(ttf|otf)$/i, "");
        const css = await ensureFontFace(id, bytes); // also validates the file parses
        setFontFaces((m) => ({ ...m, [id]: css }));
        setCustomFonts((prev) => [...prev, { id, label, bytes }]);
        if (selRef.current.kind === "textEdit") patchTextEdit({ family: id });
        showToast(`Font “${label}” added for this session.`);
      } catch {
        showToast("That file doesn't look like a usable TTF/OTF font.");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showToast]
  );

  // ── Google Fonts (session fonts, like uploads) ──
  const [googleFontOpen, setGoogleFontOpen] = useState(false);
  const [googleFontBusy, setGoogleFontBusy] = useState(false);
  const [googleFontError, setGoogleFontError] = useState<string | null>(null);

  const onAddGoogleFont = useCallback(
    async (family: string) => {
      setGoogleFontBusy(true);
      setGoogleFontError(null);
      try {
        const { fetchGoogleFont } = await import("./lib/google-fonts.ts");
        const f = await fetchGoogleFont(family);
        const id = `upload:gf-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
        const css = await ensureFontFace(id, f.bytes); // also proves the file parses
        setFontFaces((m) => ({ ...m, [id]: css }));
        setCustomFonts((prev) => [...prev, { id, label: f.label, bytes: f.bytes }]);
        // Aim the new face at whatever the user is doing: an open text
        // edit, a selected text box, or the armed Text tool.
        if (selRef.current.kind === "textEdit") patchTextEdit({ family: id });
        else {
          setTextFont(id);
          updateSelected({ font: id });
        }
        setGoogleFontOpen(false);
        showToast(`Font “${f.label}” added for this session.`);
      } catch (err) {
        setGoogleFontError(err instanceof Error ? err.message : String(err));
      } finally {
        setGoogleFontBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showToast]
  );

  /** The variant key + source for the open edit's library/uploaded face. */
  const editFaceSource = (
    family: string,
    bold: boolean,
    italic: boolean
  ): { key: string; src: string | ArrayBuffer; fallback: string } | null => {
    if (isLibraryFamily(family)) {
      const lib = libraryEntry(family);
      if (!lib) return null;
      const v = pickVariant(lib, bold, italic);
      return { key: `${family}:${v.variant}`, src: v.url, fallback: lib.cssFallback };
    }
    if (isCustomFamily(family)) {
      const cf = customFontsRef.current.find((c) => c.id === family);
      return cf ? { key: family, src: cf.bytes, fallback: "sans-serif" } : null;
    }
    return null;
  };

  // Load the FontFace for the open edit's face so typing is WYSIWYG.
  useEffect(() => {
    const fam = textEdit?.family;
    if (!fam) return;
    const source = editFaceSource(fam, textEdit.bold, textEdit.italic);
    if (!source || fontFaces[source.key]) return;
    let dead = false;
    void ensureFontFace(source.key, source.src)
      .then((css) => {
        if (!dead) setFontFaces((m) => (m[source.key] ? m : { ...m, [source.key]: css }));
      })
      .catch(() => {});
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textEdit?.family, textEdit?.bold, textEdit?.italic]);

  /** Worker-registered face ids, so registration happens once each. */
  const registeredFontsRef = useRef(new Set<string>());

  // Text-box faces: whatever the annotations use plus the armed choice.
  // Two jobs per face — a FontFace so the overlay is WYSIWYG, and a
  // worker registration so the save can embed it. Library faces can be
  // (re-)registered any time from their static files, which is what
  // makes a RESTORED session's lib: text boxes still export correctly;
  // upload:/Google faces are registered when added and are simply gone
  // after a reload (the worker falls back to Helvetica).
  useEffect(() => {
    // Each (family, weight, slant) the document actually uses. The
    // variant matters: a bold library face is a DIFFERENT file, and the
    // worker looks it up under a variant key.
    const isStandard = (f: string) => f === "helv" || f === "times" || f === "cour";
    const wanted = new Map<string, { fam: string; bold: boolean; italic: boolean }>();
    const want = (fam: string, bold: boolean, italic: boolean) => {
      if (isStandard(fam)) return; // Base-14 needs no file
      wanted.set(variantKey(fam, bold, italic), { fam, bold, italic });
    };
    for (const a of doc.annots) {
      if (a.kind === "text" && a.font) want(a.font, !!a.bold, !!a.italic);
    }
    want(textFont, textBold, textItalic);

    let dead = false;
    for (const [key, { fam, bold, italic }] of wanted) {
      const lib = isLibraryFamily(fam) ? libraryEntry(fam) : null;
      const cf = isCustomFamily(fam) ? customFontsRef.current.find((c) => c.id === fam) : null;
      if (!lib && !cf) continue;
      // A library family has a file per variant; an uploaded or fetched
      // face is the single file the user gave us, so its "variants" all
      // resolve to the same bytes (the browser slants it on screen, and
      // the PDF shows it upright — better than a fake).
      const url = lib ? pickVariant(lib, bold, italic).url : null;
      if (!fontFaces[key]) {
        void ensureFontFace(key, url ?? cf!.bytes)
          .then((css) => {
            if (!dead) setFontFaces((m) => (m[key] ? m : { ...m, [key]: css }));
          })
          .catch(() => {});
      }
      if (!registeredFontsRef.current.has(key)) {
        registeredFontsRef.current.add(key);
        void (async () => {
          try {
            const bytes = url ? await fetchFontBytes(url) : cf!.bytes;
            const name = (lib ? lib.label : cf!.label).replace(/[^A-Za-z0-9-]/g, "") || "Face";
            await ensureEngine().registerFont(key, name, bytes.slice(0));
          } catch {
            // Registration failed → the save falls back to the family's
            // regular file, or Helvetica; retrying next run costs nothing.
            registeredFontsRef.current.delete(key);
          }
        })();
      }
    }
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.annots, textFont, textBold, textItalic, fontFaces]);

  /** Typeface id → CSS family for the text-box overlays. Base-14 faces
   *  map to their stacks; session faces appear once their FontFace has
   *  loaded (falling back to the library's approximation meanwhile). */
  const annotFontCss = useMemo(() => {
    const out: Record<string, string> = {
      helv: "Helvetica, Arial, sans-serif",
      times: "'Times New Roman', Times, serif",
      cour: "'Courier New', Courier, monospace",
    };
    for (const f of FONT_LIBRARY) {
      // The regular file is what the overlay shows; the browser
      // synthesises the bold/italic look, and the export uses the real
      // variant file.
      const css = fontFaces[variantKey(`lib:${f.id}`, false, false)];
      out[`lib:${f.id}`] = css ? `${css}, ${f.cssFallback}` : f.cssFallback;
    }
    for (const cf of customFonts) {
      const css = fontFaces[variantKey(cf.id, false, false)] ?? fontFaces[cf.id];
      if (css) out[cf.id] = `${css}, sans-serif`;
    }
    return out;
  }, [fontFaces, customFonts]);

  const editFontCss = useMemo(() => {
    if (!textEdit) return undefined;
    const source = editFaceSource(textEdit.family, textEdit.bold, textEdit.italic);
    if (!source) return undefined;
    const css = fontFaces[source.key];
    return css ? `"${css}", ${source.fallback}` : source.fallback;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textEdit?.family, textEdit?.bold, textEdit?.italic, fontFaces]);

  const editUid = useRef(0);
  const onEditTextAt = useCallback(
    (pageIndex: number, x: number, y: number, mode: "line" | "block", quiet = false) => {
      if (pageOpBusy) return;
      // Clicking a new target while an edit is open commits the open one
      // first (same semantics as clicking away). The page's pointerdown
      // preventDefault suppresses the blur that would otherwise do this.
      const open = selRef.current;
      if (open.kind === "textEdit") {
        const el = document.querySelector<HTMLInputElement | HTMLTextAreaElement>(
          ".pe-textedit"
        );
        onCommitTextEdit(el ? el.value : open.edit.text);
      }
      void enqueue(async () => {
        try {
          const hit = await ensureEngine().textHit(pageIndex, x, y, mode);
          if (hit.kind === "line") {
            const bold = /Bold/.test(hit.base14);
            const italic = /Italic|Oblique/.test(hit.base14);
            setSel({
              kind: "textEdit",
              edit: {
                pageIndex,
                uid: (editUid.current += 1),
                ...hit,
                family: "original",
                bold,
                italic,
                origBold: bold,
                origItalic: italic,
                origColor: hit.color,
                origSize: hit.size,
                background: null,
                moveDx: 0,
                moveDy: 0,
              },
            });
          } else if (hit.kind === "image") {
            setSel({ kind: "image", pageIndex, bbox: hit.bbox });
          } else if (hit.kind === "fill") {
            setSel({ kind: "fill", pageIndex, fill: hit.fill });
          } else if (!quiet) {
            // Empty space = add NEW content text at the
            // click point (quiet select-mode clicks just deselect).
            const size = 12;
            setSel({
              kind: "textEdit",
              edit: {
                pageIndex,
                uid: (editUid.current += 1),
                text: "",
                quads: [],
                bbox: [x, y - size * 0.85, x + 60, y + size * 0.35],
                origin: [x, y],
                size,
                base14: "Helvetica",
                rawFont: "",
                color: "#111111",
                fontBytes: null,
                family: "original",
                bold: false,
                italic: false,
                origBold: false,
                origItalic: false,
                origColor: "#111111",
                origSize: size,
                background: null,
                moveDx: 0,
                moveDy: 0,
              },
            });
          } else {
            // Quiet (select-mode) click on empty space: clear any Edit-
            // tool selection; annotation/text selections are handled by
            // the caller.
            setSel((s) =>
              s.kind === "region" || s.kind === "image" || s.kind === "fill"
                ? NO_SELECTION
                : s
            );
          }
        } catch (err) {
          if (!quiet) {
            showToast(`Could not inspect: ${err instanceof Error ? err.message : String(err)}`);
          }
        }
      });
    },
    [pageOpBusy, enqueue, ensureEngine, onCommitTextEdit, showToast]
  );

  /** Live restyle of the open content-text edit (formatting bar). */
  const patchTextEdit = useCallback(
    (patch: Partial<
      Pick<
        ActiveTextEdit,
        "family" | "bold" | "italic" | "size" | "color" | "background" | "moveDx" | "moveDy"
      >
    >) => {
      setSel((s) =>
        s.kind === "textEdit"
          ? { kind: "textEdit", edit: { ...s.edit, ...patch } }
          : s
      );
    },
    []
  );

  /** A plain press with the pointer — the one gesture that both selects
   *  and edits.
   *
   *  Content text opens its editor on the first press — click a word and
   *  type. Everything else selects on the first press, filling the
   *  contextual toolbar with its properties (which is where most
   *  "editing" happens — colour, size, font, alignment), and pressing the
   *  SAME object again opens whatever inline editor it has: the note
   *  body, a text annotation's caret.
   *
   *  This used to be two tools. Select could edit document text but only
   *  by opening the caret on the first click, with no way to just point
   *  at a line; Edit could select content but blanked every annotation
   *  while it was active. One tool, one hit-test, no mode to remember.
   */
  const onTapAt = useCallback(
    (pageIndex: number, x: number, y: number, mode: "line" | "block") => {
      if (pageOpBusy) return;
      // Pressing anywhere new commits an open edit, exactly as clicking
      // away does — including when the new target is the page itself.
      const open = selRef.current;
      if (open.kind === "textEdit") {
        const el = document.querySelector<HTMLInputElement | HTMLTextAreaElement>(
          ".pe-textedit"
        );
        onCommitTextEdit(el ? el.value : open.edit.text);
      }
      // The overlay wins over the page beneath it: an annotation or form
      // field under the point is what you meant, not the text behind it.
      // (Annotations with their own hit areas never reach this handler —
      // they stop the press themselves. This catches the misses: thin
      // strokes, gaps between markup quads.)
      const overlayHit = annotAt(pageIndex, x, y);
      if (overlayHit) {
        const item: RegionItem = {
          id: annotItemId(overlayHit.id),
          kind: "annot",
          bbox: annotBounds(overlayHit),
        };
        if (isSoleSelection(item, pageIndex)) startEditAnnot(overlayHit.id);
        else setSel({ kind: "annots", ids: [overlayHit.id], editingId: null });
        return;
      }
      void enqueue(async () => {
        try {
          const item = await ensureEngine().regionHit(pageIndex, x, y);
          if (!item) {
            setSel(NO_SELECTION);
            return;
          }
          // Text edits in place on the FIRST press. Making it a second
          // press (as this briefly did) reads as the editor being broken:
          // clicking a word and typing is the whole point, and a text
          // object can still be moved — by its edit grip, or by taking it
          // in a marquee. Images and fills have no editor deeper than
          // their frame, so for them a press just selects.
          if (item.kind === "text") {
            onEditTextAt(pageIndex, x, y, mode);
            return;
          }
          setSel(selectionFor(pageIndex, [item]));
        } catch {
          // Hit-testing needs the worker; a press that can't resolve
          // shouldn't leave the previous selection looking live.
          setSel(NO_SELECTION);
        }
      });
    },
    [
      pageOpBusy,
      enqueue,
      ensureEngine,
      onCommitTextEdit,
      onEditTextAt,
      annotAt,
      isSoleSelection,
      selectionFor,
      startEditAnnot,
    ]
  );

  const deletePage = useCallback(
    (i: number) => {
      const count = annotsRef.current.filter((a) => a.pageIndex === i).length;
      if (
        count > 0 &&
        !window.confirm(
          `Delete page ${i + 1}? Its ${count} annotation${count === 1 ? "" : "s"} will be removed (undoable).`
        )
      ) {
        return;
      }
      doPageOp({ kind: "delete", pageIndex: i });
    },
    [doPageOp]
  );

  const rotatePage = useCallback(
    (i: number, clockwise: boolean) => doPageOp({ kind: "rotate", pageIndex: i, clockwise }),
    [doPageOp]
  );

  // ── thumbnail multi-selection + batch page ops ──

  /** Plain click navigates (and clears the selection); Ctrl/Cmd
   *  toggles; Shift selects the anchor..here range. */
  const onThumbClick = useCallback(
    (i: number, e: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => {
      if (e.ctrlKey || e.metaKey) {
        setPageSel((prev) => {
          const next = new Set(prev);
          if (next.has(i)) next.delete(i);
          else next.add(i);
          return next;
        });
        pageSelAnchor.current = i;
        return;
      }
      if (e.shiftKey) {
        const a = Math.min(pageSelAnchor.current, i);
        const b = Math.max(pageSelAnchor.current, i);
        setPageSel((prev) => {
          const next = new Set(prev);
          for (let pg = a; pg <= b; pg += 1) next.add(pg);
          return next;
        });
        return;
      }
      pageSelAnchor.current = i;
      setPageSel(new Set());
      scrollToPage(i);
    },
    [scrollToPage]
  );

  // The selection dies when the page list changes shape or tab switches.
  useEffect(() => {
    setPageSel(new Set());
  }, [pages?.length, activeId]);

  /** Extract the selected pages into a standalone PDF — overlay
   *  annotations applied live, form values filled — as a download or a
   *  new editor tab. Never touches the open document. */
  const doExtractPages = useCallback(
    (mode: "tab" | "download") => {
      const indices = [...pageSel].sort((a, b) => a - b);
      const tab = tabsRef.current.find((t) => t.id === activeIdRef.current);
      if (indices.length === 0 || !tab || saving) return;
      setSaving(true);
      void enqueue(async () => {
        try {
          const { bytes } = await ensureEngine().extractPages(
            indices,
            annotsRef.current,
            formValuesRef.current
          );
          if (mode === "download") {
            downloadBlob(
              bytesToPdfBlob(new Uint8Array(bytes)),
              pdfFilename(tab.name, "pages")
            );
            showToast(
              `Downloaded ${indices.length} page${indices.length === 1 ? "" : "s"} as a new PDF`
            );
          } else {
            const newTab: DocTab = {
              id: nextTabId(),
              name: `${tab.name.replace(/\.pdf$/i, "")} (pages).pdf`,
              bytes,
              stash: EMPTY_DOC,
              formValues: {},
            };
            stashActive();
            setTabs((prev) => [...prev, newTab]);
            await activateTab(newTab);
            showToast(
              `Extracted ${indices.length} page${indices.length === 1 ? "" : "s"} to a new tab`
            );
          }
        } catch (err) {
          showToast(`Extract failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
          setSaving(false);
        }
      });
    },
    [pageSel, saving, enqueue, ensureEngine, stashActive, activateTab, showToast]
  );

  /** Delete the selected pages in ONE rebase (one undo step). */
  const doDeletePagesBatch = useCallback(() => {
    const before = docRef.current.baseline;
    const indices = [...pageSel].sort((a, b) => a - b);
    if (!before || indices.length === 0 || pageOpBusy) return;
    if (indices.length >= before.pages.length) {
      showToast("Cannot delete every page");
      return;
    }
    const annotCount = annotsRef.current.filter((a) => pageSel.has(a.pageIndex)).length;
    if (
      annotCount > 0 &&
      !window.confirm(
        `Delete ${indices.length} page${indices.length === 1 ? "" : "s"}? ${annotCount} annotation${annotCount === 1 ? "" : "s"} will be removed (undoable).`
      )
    ) {
      return;
    }
    setPageOpBusy(true);
    void enqueue(async () => {
      try {
        if (docRef.current.baseline?.gen !== before.gen) return;
        const { bytes, pages: nextPages, widgets } = await ensureEngine().deletePages(indices);
        const baseline: Baseline = { bytes, pages: nextPages, widgets, gen: nextGen() };
        engineGen.current = baseline.gen;
        const del = new Set(indices);
        const newIndex = new Map<number, number>();
        let out = 0;
        for (let i = 0; i < before.pages.length; i += 1) {
          if (!del.has(i)) newIndex.set(i, out++);
        }
        const annots = annotsRef.current
          .filter((a) => newIndex.has(a.pageIndex))
          .filter(
            (a) => !(a.kind === "link" && a.page !== undefined && !newIndex.has(a.page))
          )
          .map((a) => {
            const pageIndex = newIndex.get(a.pageIndex)!;
            if (a.kind === "link" && a.page !== undefined) {
              return { ...a, pageIndex, page: newIndex.get(a.page)! };
            }
            return pageIndex === a.pageIndex ? a : { ...a, pageIndex };
          });
        dispatch({ type: "commit", annots, baseline });
        selectAnnot(null);
        setPageSel(new Set());
        const cur = activeIdRef.current;
        setTabs((prev) => prev.map((t) => (t.id === cur ? { ...t, bytes } : t)));
      } catch (err) {
        showToast(`Delete failed: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setPageOpBusy(false);
      }
    });
  }, [pageSel, pageOpBusy, enqueue, ensureEngine, selectAnnot, showToast]);

  const rotateSelectedPages = useCallback(
    (clockwise: boolean) => {
      for (const i of [...pageSel].sort((a, b) => a - b)) rotatePage(i, clockwise);
    },
    [pageSel, rotatePage]
  );

  // ── comment threads (replies + review status) ──

  const onReplyToAnnot = useCallback((target: Annotation, text: string) => {
    const t = text.trim();
    if (!t) return;
    const name = authorRef.current.trim();
    dispatch({
      type: "commit",
      annots: annotsRef.current.map((a) =>
        a.id === target.id && a.kind !== "link" && a.kind !== "redact"
          ? {
              ...a,
              replies: [
                ...(a.replies ?? []),
                { ...(name ? { author: name } : null), text: t, date: new Date().toISOString() },
              ],
            }
          : a
      ),
    });
  }, []);

  const onSetAnnotStatus = useCallback(
    (target: Annotation, status: AnnotationStatus | null) => {
      dispatch({
        type: "commit",
        annots: annotsRef.current.map((a) => {
          if (a.id !== target.id || a.kind === "link" || a.kind === "redact") return a;
          if (!status) {
            const { status: _drop, ...rest } = a;
            return rest as Annotation;
          }
          return { ...a, status };
        }),
      });
    },
    []
  );

  /** The annotation's own comment — for the kinds that have no visible
   *  text of their own, this IS what a screen reader announces. */
  const onSetAnnotContents = useCallback((target: Annotation, contents: string) => {
    dispatch({
      type: "commit",
      annots: annotsRef.current.map((a) => {
        if (a.id !== target.id || a.kind === "link" || a.kind === "redact") return a;
        if (!contents) {
          const { contents: _drop, ...rest } = a;
          return rest as Annotation;
        }
        return { ...a, contents };
      }),
    });
  }, []);

  // ── accessibility ──

  /** The overlay's half of the audit. Annotations the user has added
   *  are UI state, so the worker's facts can't see them. A note or text
   *  box describes itself; everything else needs a comment. */
  const a11yOverlay = useMemo<A11yOverlayFacts>(() => {
    let describable = 0;
    let withoutDescription = 0;
    for (const a of doc.annots) {
      if (a.kind === "redact" || a.kind === "link") continue;
      describable += 1;
      const words = a.kind === "note" || a.kind === "text" ? a.value : (a.contents ?? "");
      if (!words.trim()) withoutDescription += 1;
    }
    return { describable, withoutDescription };
  }, [doc.annots]);

  /** A title worth offering when the document has none: the file name
   *  without its extension. Better than nothing, worse than a real one
   *  — which is why the button quotes it rather than acting silently. */
  const suggestedTitle = useMemo(() => {
    const name = tabs.find((t) => t.id === activeId)?.name ?? "";
    return name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim();
  }, [tabs, activeId]);

  /** Apply one accessibility repair. The title goes through the normal
   *  properties path so the XMP mirror stays in step; the rest are
   *  catalog flags the worker sets directly. */
  const onA11yFix = useCallback(
    (fix: A11yFixId, value?: string) => {
      if (fix === "title") {
        if (value) doSetDocInfo({ title: value });
        return;
      }
      const patch =
        fix === "lang"
          ? { lang: value ?? "" }
          : fix === "displayDocTitle"
            ? { displayDocTitle: true }
            : fix === "tabOrder"
              ? { tabOrder: true }
              : { fieldDescriptions: true };
      doSetAccessibility(patch);
    },
    [doSetDocInfo, doSetAccessibility]
  );

  /** Checks whose answer already lives elsewhere in the editor hand off
   *  rather than growing a second copy of that tool here. */
  const onA11yGoTo = useCallback((to: A11yGoTo) => {
    if (to === "ocr") setOcrOpen(true);
    else if (to === "security") setSecurityOpen(true);
    else setPanel(to === "properties" ? "properties" : to === "comments" ? "comments" : "fonts");
  }, []);

  const selectedAnnot = useMemo(
    () => doc.annots.find((a) => a.id === selectedId) ?? null,
    [doc.annots, selectedId]
  );
  // Annotations read as selected in a mixed selection too — the region
  // frame moves them, but each one still shows its own selection box.
  const selectedIdSet = useMemo(
    () =>
      new Set(
        sel.kind === "annots"
          ? sel.ids
          : sel.kind === "region"
            ? sel.items.filter(isAnnotItem).map(annotIdOf)
            : []
      ),
    [sel]
  );
  const selectedInk = selectedAnnot?.kind === "ink" ? selectedAnnot : null;
  const selectedShape = selectedAnnot?.kind === "shape" ? selectedAnnot : null;
  const selectedNote = selectedAnnot?.kind === "note" ? selectedAnnot : null;
  /** Which markup palette to show (selected annotation wins over tool). */
  const activeMarkupKind: MarkupKind | null =
    selectedAnnot && isMarkup(selectedAnnot)
      ? selectedAnnot.kind
      : isMarkupTool(tool)
        ? tool
        : null;
  /** Everything the properties bar reads, in one object. Assembled here
   *  rather than spread across the JSX so the desktop row and the mobile
   *  sheet render the identical control set from the identical state. */
  const propsBarModel: PropsBarProps = {
    tool,
    selectedAnnot,
    selectedInk,
    selectedShape,
    selectedNote,
    activeMarkupKind,
    updateSelected,
    highlightColor,
    setHighlightColor,
    markupColor,
    setMarkupColor,
    textSize,
    setTextSize,
    textFont,
    setTextFont,
    textCjkScript,
    setTextCjkScript,
    textBold,
    setTextBold,
    textItalic,
    setTextItalic,
    onOpenGoogleFont: () => setGoogleFontOpen(true),
    textColor,
    setTextColor,
    textAlign,
    setTextAlign,
    textOpacity,
    setTextOpacity,
    inkWidth,
    setInkWidth,
    inkColor,
    setInkColor,
    shapeWidth,
    setShapeWidth,
    shapeColor,
    setShapeColor,
    shapeDash,
    setShapeDash,
    shapeHeadStart,
    setShapeHeadStart,
    shapeHeadEnd,
    setShapeHeadEnd,
    shapeFillOn,
    setShapeFillOn,
    shapeFillColor,
    setShapeFillColor,
    textEdit: textEdit ?? undefined,
    patchTextEdit,
    customFonts,
    fontUploadInputRef,
    noteColor,
    setNoteColor,
    measureUnit,
    setMeasureUnit,
    calibrateMeasure,
    measureFactor,
    setMeasureFactor,
    selCount,
    applyAlign,
    applyDistribute,
  };

  /** Desktop only — the phone keeps these controls in its bottom sheet,
   *  where there is no side pane to stack them in. */
  const showOptionsPanel = !compact;
  /** The right column's visibility is the USER's, not the tool's. It used
   *  to come and go with `propsBarHasContent`, so picking a tool with no
   *  options collapsed the whole column and reflowed the document —
   *  every switch between, say, Highlight and Select shifted the page
   *  sideways. Now it stays where it was put; the Options section shows
   *  a line of text when the armed tool has nothing to configure, which
   *  is quieter than the layout jumping.
   *
   *  A phone has no room for a column beside the page, so there it still
   *  exists only for an open panel (as a full-height sheet). */
  const rightPaneOpen =
    Boolean(pages && pages.length > 0) && (compact ? panel !== null : paneOpen);

  const toolStyles = useMemo<ToolStyles>(
    () => ({
      markupColor: tool === "highlight" ? highlightColor : markupColor,
      inkColor,
      inkWidth,
      shapeColor,
      shapeWidth,
      shapeFill: shapeFillOn ? shapeFillColor : null,
      shapeDash,
      shapeHeadStart,
      shapeHeadEnd,
    }),
    [tool, highlightColor, markupColor, inkColor, inkWidth, shapeColor, shapeWidth, shapeFillOn, shapeFillColor, shapeDash, shapeHeadStart, shapeHeadEnd]
  );
  const annotsByPage = useMemo(() => {
    const map = new Map<number, Annotation[]>();
    for (const a of doc.annots) {
      const list = map.get(a.pageIndex) ?? [];
      list.push(a);
      map.set(a.pageIndex, list);
    }
    return map;
  }, [doc.annots]);
  const dirtyIds = useMemo(() => {
    const set = new Set<string>();
    for (const t of tabs) {
      const annots = t.id === activeId ? doc.annots : t.stash.annots;
      const form = t.id === activeId ? formValues : t.formValues;
      if (annots.length > 0 || Object.keys(form).length > 0) set.add(t.id);
    }
    return set;
  }, [tabs, activeId, doc.annots, formValues]);
  const dirty = activeId !== null && dirtyIds.has(activeId);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useImperativeHandle(
    ref,
    (): PdfEditorHandle => ({
      save: async (opts) => (await exportBytes(opts)).bytes,
      isDirty: () => dirtyRef.current,
      getDocumentName: () => {
        const tab = tabsRef.current.find((t) => t.id === activeIdRef.current);
        return tab ? pdfFilename(tab.name, "annotated") : null;
      },
    }),
    [exportBytes]
  );
  const widgetsByPage = useMemo(() => {
    const map = new Map<number, WidgetInfo[]>();
    for (const w of doc.baseline?.widgets ?? []) {
      const list = map.get(w.pageIndex) ?? [];
      list.push(w);
      map.set(w.pageIndex, list);
    }
    return map;
  }, [doc.baseline]);
  const setFormValue = useCallback((name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);
  const searchHitsByPage = useMemo(() => {
    const map = new Map<number, { quads: Quad[]; current: boolean }[]>();
    searchHits.forEach((h, i) => {
      const list = map.get(h.pageIndex) ?? [];
      list.push({ quads: h.quads, current: i === searchCurrent });
      map.set(h.pageIndex, list);
    });
    return map;
  }, [searchHits, searchCurrent]);

  const onFilePicker = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = "";
      if (files.length) {
        setWelcome(false);
        void openFiles(files, { replaceBlank: true });
      }
    },
    [openFiles]
  );

  // ── render ──

  /** Whether a command survived the feature gates — the page rail's
   *  buttons mirror commands, so they must obey the same filter. */
  const has = (id: string): boolean => commands.some((c) => c.id === id);

  return (
    <div
      ref={rootRef}
      className={
        "pe-root relative flex h-full flex-col bg-[color:var(--color-bg)]" +
        (className ? ` ${className}` : "")
      }
      style={style}
      data-theme={resolvedTheme}
      onDragOver={(e: DragEvent<HTMLDivElement>) => {
        if (!openEnabled) return;
        if (Array.from(e.dataTransfer.types).includes("Files")) e.preventDefault();
      }}
      onDrop={(e: DragEvent<HTMLDivElement>) => {
        if (!openEnabled) return;
        // A drop mid-import would fight the active scratch document.
        if (mdImport || sheetImport || photoImport) return;
        const all = Array.from(e.dataTransfer.files ?? []);
        const files = all.filter(isPdfFile);
        // First match wins, in specificity order; images are collected
        // as a SET — dropping a folder of photos is the common case.
        const docx = files.length === 0 ? all.find(isDocxFile) : undefined;
        let rest = files.length === 0 && !docx;
        const html = rest ? all.find(isHtmlFile) : undefined;
        rest = rest && !html;
        // .txt matches BOTH isMdFile and isSheetFile — a dropped .txt
        // is far likelier prose than a delimited table, so markdown is
        // tried first and the spreadsheet flow keeps .csv/.xlsx/…
        const md = rest ? all.find(isMdFile) : undefined;
        rest = rest && !md;
        const sheet = rest ? all.find(isSheetFile) : undefined;
        const pics = rest && !sheet ? all.filter(isImageImportFile) : [];
        if (!files.length && !docx && !html && !md && !sheet && pics.length === 0) return;
        e.preventDefault();
        setIsDragOver(false);
        setWelcome(false);
        if (files.length) void openFiles(files, { replaceBlank: true });
        else if (docx) void onPickDocx(docx);
        else if (html) void onPickHtml(html);
        else if (md) void onPickMarkdown(md);
        else if (sheet) void onPickSheet(sheet);
        else if (pics.length) void onPickImages(pics);
      }}
    >
      {/* shared file input (toolbar Open + welcome overlay both use it) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        className="hidden"
        onChange={onFilePicker}
      />
      {/* markdown-import picker (rail button, palette, welcome overlay) */}
      <input
        ref={mdInputRef}
        type="file"
        accept=".md,.markdown,.mdown,.txt,text/markdown,text/plain"
        data-role="md-import-input"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void onPickMarkdown(f);
        }}
      />
      {/* Word-document picker (converted to markdown, then the md flow) */}
      <input
        ref={docxInputRef}
        type="file"
        accept={DOCX_ACCEPT}
        data-role="docx-import-input"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void onPickDocx(f);
        }}
      />
      {/* HTML-file picker (same conversion path as Word) */}
      <input
        ref={htmlInputRef}
        type="file"
        accept={HTML_ACCEPT}
        data-role="html-import-input"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void onPickHtml(f);
        }}
      />
      {/* spreadsheet-import picker (Open menu, rail, palette, welcome) */}
      <input
        ref={sheetInputRef}
        type="file"
        accept={SHEET_ACCEPT}
        data-role="sheet-import-input"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void onPickSheet(f);
        }}
      />
      {/* image-import picker — multiple (Open menu, rail, palette, welcome) */}
      <input
        ref={photoInputRef}
        type="file"
        accept={IMAGE_IMPORT_ACCEPT}
        multiple
        data-role="photo-import-input"
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = "";
          if (files.length) void onPickImages(files);
        }}
      />
      {/* replacement picker for a selected content image */}
      <input
        ref={replaceImageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) {
            void imageFileToDataUrl(f)
              .then(({ dataUrl }) => doImageOp({ kind: "replace", dataUrl }))
              .catch(() => showToast("Could not read that image"));
          }
        }}
      />
      {/* custom-stamp upload (Stamps menu) */}
      <input
        ref={stampUploadInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        data-role="stamp-upload-input"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void onUploadStamp(f);
        }}
      />
      {/* image-to-stamp picker (toolbar Image button) */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) {
            void imageFileToDataUrl(f)
              .then(startStampPlacement)
              .catch(() => showToast("Could not read that image"));
          }
        }}
      />

      {compact ? (
        <MobileTopBar
          docName={tabs.find((t) => t.id === activeId)?.name ?? "Untitled.pdf"}
          tool={tool}
          toolLocked={toolLocked}
          onPickTool={armTool}
          onToggleLock={() => setToolLocked((v) => !v)}
          commands={commands}
          drawer={mobileDrawer}
          setDrawer={setMobileDrawer}
        />
      ) : (
        <>
      {tabsEnabled && (
        <TabBar
          tabs={tabs}
          activeId={activeId}
          dirtyIds={dirtyIds}
          onSelect={selectTab}
          onClose={closeTab}
          onRename={renameTab}
          onNew={openEnabled ? () => void newBlankTab() : undefined}
        />
      )}

      {/* toolbar */}
      <div
        role="toolbar"
        aria-label="Editor tools"
        className="flex flex-wrap items-center gap-1 border-b border-[color:var(--color-border)] px-2 py-1.5"
      >
        <div className="relative">
          <button
            type="button"
            title="File — open, import, download or print this document"
            aria-haspopup="menu"
            onClick={() => setOpenMenuOpen((o) => !o)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm hover:bg-[color:var(--color-bg-subtle)]"
          >
            <i className="ri-file-3-line" aria-hidden />
            <span className="hidden lg:inline">File</span>
            <i className="ri-arrow-down-s-line -ml-0.5" aria-hidden />
          </button>
          {openMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setOpenMenuOpen(false)}
                aria-hidden
              />
              <div
                role="menu"
                className="absolute left-0 top-full z-40 mt-1 w-72 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-1 shadow-lg"
              >
                <div
                  className="mx-1 pb-1 text-[10px] uppercase tracking-wide text-[color:var(--color-fg-muted)]"
                  aria-hidden
                >
                  Open
                </div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpenMenuOpen(false);
                    fileInputRef.current?.click();
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)]"
                >
                  <i className="ri-file-pdf-line" aria-hidden /> PDF document…
                </button>
                <div
                  className="mx-1 my-1 border-t border-[color:var(--color-border)] pt-1 text-[10px] uppercase tracking-wide text-[color:var(--color-fg-muted)]"
                  aria-hidden
                >
                  Convert to PDF
                </div>
                {(
                  [
                    {
                      icon: "ri-file-word-line",
                      label: "Word document…",
                      hint: ".docx",
                      ref: docxInputRef,
                    },
                    {
                      icon: "ri-markdown-line",
                      label: "Markdown…",
                      hint: ".md",
                      ref: mdInputRef,
                    },
                    {
                      icon: "ri-html5-line",
                      label: "HTML file…",
                      hint: ".html",
                      ref: htmlInputRef,
                    },
                    {
                      icon: "ri-table-line",
                      label: "Spreadsheet…",
                      hint: "CSV · Excel · ODS",
                      ref: sheetInputRef,
                    },
                    {
                      icon: "ri-image-line",
                      label: "Images…",
                      hint: "JPG · PNG · HEIC",
                      ref: photoInputRef,
                    },
                  ] as const
                ).map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setOpenMenuOpen(false);
                      item.ref.current?.click();
                    }}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)]"
                  >
                    <i className={item.icon} aria-hidden />
                    <span>{item.label}</span>
                    <span className="ml-auto pl-2 text-xs text-[color:var(--color-fg-muted)]">
                      {item.hint}
                    </span>
                  </button>
                ))}

                <button
                  type="button"
                  role="menuitem"
                  disabled={!pages}
                  onClick={() => {
                    setOpenMenuOpen(false);
                    setCombineOpen(true);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
                >
                  <i className="ri-file-copy-2-line" aria-hidden /> Combine PDFs…
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={formFieldNames.length === 0}
                  onClick={() => {
                    setOpenMenuOpen(false);
                    onExportFormData();
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
                >
                  <i className="ri-file-download-line" aria-hidden /> Export form data (XFDF)…
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={!pages || doc.annots.length === 0}
                  onClick={() => {
                    setOpenMenuOpen(false);
                    onExportComments();
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
                >
                  <i className="ri-chat-download-line" aria-hidden /> Export comments (XFDF)…
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={!pages}
                  onClick={() => {
                    setOpenMenuOpen(false);
                    xfdfInputRef.current?.click();
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
                >
                  <i className="ri-file-upload-line" aria-hidden /> Import XFDF (comments or form data)…
                </button>

                {/* Download and print used to be their own cluster
                    pinned to the right edge of the toolbar. They are
                    file operations like everything above, so they live
                    with them — one menu for "what happens to this
                    document as a file". */}
                <div
                  className="mx-1 my-1 border-t border-[color:var(--color-border)] pt-1 text-[10px] uppercase tracking-wide text-[color:var(--color-fg-muted)]"
                  aria-hidden
                >
                  Save &amp; print
                </div>
                <button
                  type="button"
                  role="menuitem"
                  disabled={saving || !pages}
                  onClick={() => {
                    setOpenMenuOpen(false);
                    void onDownload();
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
                >
                  {saving ? (
                    <i className="ri-loader-4-line animate-spin" aria-hidden />
                  ) : (
                    <i className="ri-download-2-line" aria-hidden />
                  )}
                  {(doc.baseline?.widgets.length ?? 0) > 0
                    ? "Download PDF with editable form fields"
                    : "Download PDF"}
                </button>
                {(doc.baseline?.widgets.length ?? 0) > 0 && (
                  <button
                    type="button"
                    role="menuitem"
                    disabled={saving || !pages}
                    onClick={() => {
                      setOpenMenuOpen(false);
                      void onDownload({ flatten: "forms" });
                    }}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
                  >
                    <i className="ri-lock-2-line" aria-hidden /> Flattened form fields
                    (uneditable)
                  </button>
                )}
                <button
                  type="button"
                  role="menuitem"
                  disabled={saving || !pages}
                  onClick={() => {
                    setOpenMenuOpen(false);
                    void onDownload({ flatten: "all" });
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
                >
                  <i className="ri-stack-line" aria-hidden /> Flatten everything
                  (annotations baked in)
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={saving || !pages}
                  onClick={() => {
                    setOpenMenuOpen(false);
                    setImgExportOpen(true);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
                >
                  <i className="ri-image-2-line" aria-hidden /> Export pages as images…
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={saving || !pages}
                  onClick={() => {
                    setOpenMenuOpen(false);
                    setOptOpen(true);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
                >
                  <i className="ri-compasses-2-line" aria-hidden /> Reduce file size…
                </button>
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={stripMeta}
                  onClick={() => setStripMeta((v) => !v)}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)]"
                >
                  <i
                    className={stripMeta ? "ri-checkbox-line" : "ri-checkbox-blank-line"}
                    aria-hidden
                  />
                  Remove metadata (author, title…)
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpenMenuOpen(false);
                    setSecurityOpen(true);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)]"
                >
                  <i className="ri-shield-keyhole-line" aria-hidden /> Security &amp;
                  permissions…
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={!pages}
                  onClick={() => {
                    setOpenMenuOpen(false);
                    setCertifyStatus(null);
                    setCertifyOpen(true);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
                >
                  <i className="ri-shield-check-line" aria-hidden /> Digitally sign…
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={saving || !pages}
                  onClick={() => {
                    setOpenMenuOpen(false);
                    void onPrint();
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
                >
                  <i className="ri-printer-line" aria-hidden />
                  <span className="min-w-0 flex-1">Print</span>
                  <span className="text-xs text-[color:var(--color-fg-muted)]">Ctrl+P</span>
                </button>
              </div>
            </>
          )}
        </div>

        <span className="mx-2 h-5 w-px bg-[color:var(--color-border)]" aria-hidden />

        {/* The two persistent modes, then the five verbs. Twenty-one
            flat buttons became six: each verb button shows the member
            you last used and opens the rest on a second click. */}
        {[toolDef("select"), toolDef("hand")]
          .filter((d) => toolAllowed(d.tool))
          .map((d) => (
            <ToolButton
              key={d.tool}
              icon={d.icon}
              label={d.label}
              shortcut={toolShortcutText(d)}
              active={tool === d.tool}
              onClick={() => armTool(d.tool)}
            />
          ))}
        {visibleToolGroups.map((g) => (
          <ToolMenu
            key={g.id}
            group={g}
            tool={tool}
            lastPick={
              g.members.some((m) => m.tool === lastPick[g.id])
                ? lastPick[g.id]!
                : g.members[0]!.tool
            }
            locked={toolLocked}
            onPick={armTool}
            onToggleLock={() => setToolLocked((v) => !v)}
          />
        ))}

        {/* stamp family — signature, image, stamp library — all place a
            stamp annotation, so the "stamp" tool gate covers them */}
        {toolAllowed("stamp") && (
          <>
          <span className="mx-2 h-5 w-px bg-[color:var(--color-border)]" aria-hidden />

          <button
            type="button"
            title="Add signature (draw, type or upload)"
            aria-pressed={sigDialog || (tool === "stamp" && pendingStamp !== null)}
            onClick={() => setSigDialog(true)}
            className={`inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm ${
              tool === "stamp"
                ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                : "text-[color:var(--color-fg)] hover:bg-[color:var(--color-bg-subtle)]"
            }`}
          >
            <i className="ri-quill-pen-line" aria-hidden />
            <span className="hidden lg:inline">Sign</span>
          </button>
          <button
            type="button"
            title="Insert image"
            onClick={() => imageInputRef.current?.click()}
            className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm text-[color:var(--color-fg)] hover:bg-[color:var(--color-bg-subtle)]"
          >
            <i className="ri-image-add-line" aria-hidden />
            <span className="hidden lg:inline">Image</span>
          </button>
          <div className="relative">
            <button
              type="button"
              title="Stamp library — APPROVED, DRAFT, dated stamps and your own images"
              aria-haspopup="menu"
              aria-pressed={stampMenuOpen}
              onClick={() => setStampMenuOpen((o) => !o)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm text-[color:var(--color-fg)] hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-price-tag-3-line" aria-hidden />
              <span className="hidden lg:inline">Stamps</span>
              <i className="ri-arrow-down-s-line -ml-1 opacity-70" aria-hidden />
            </button>
            {stampMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-30"
                  onClick={() => setStampMenuOpen(false)}
                  aria-hidden
                />
                <div
                  role="menu"
                  className="absolute left-0 top-full z-40 mt-1 max-h-96 w-64 overflow-y-auto rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-1 shadow-lg"
                >
                  {STAMP_PRESETS.filter(
                    (preset) => !preset.text.includes("{name}") || author.trim()
                  ).map((preset) => {
                    const label = stampText(preset, author.trim());
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setStampMenuOpen(false);
                          startStampPlacement(renderTextStamp(label, preset.color));
                        }}
                        className="flex w-full items-center rounded px-2 py-1.5 text-left hover:bg-[color:var(--color-bg-subtle)]"
                      >
                        <span
                          className="inline-block rounded px-1.5 py-0.5 text-[11px] font-bold"
                          style={{ color: preset.color, border: `2px solid ${preset.color}` }}
                        >
                          {label}
                        </span>
                      </button>
                    );
                  })}
                  {customStamps.length > 0 && (
                    <div className="mx-2 my-1 h-px bg-[color:var(--color-border)]" aria-hidden />
                  )}
                  {customStamps.map((c) => (
                    <div key={c.id} className="group flex items-center gap-1 rounded px-1 hover:bg-[color:var(--color-bg-subtle)]">
                      <button
                        type="button"
                        role="menuitem"
                        title={`Place “${c.label}”`}
                        onClick={() => {
                          setStampMenuOpen(false);
                          startStampPlacement(c);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-2 py-1 text-left"
                      >
                        <img src={c.dataUrl} alt="" className="h-7 max-w-24 shrink-0 bg-white p-0.5" />
                        <span className="min-w-0 truncate text-xs">{c.label}</span>
                      </button>
                      <button
                        type="button"
                        title="Remove this stamp"
                        onClick={() => {
                          void deleteCustomStamp(c.id).catch(() => {});
                          setCustomStamps((prev) => prev.filter((x) => x.id !== c.id));
                        }}
                        className="hidden h-6 w-6 shrink-0 items-center justify-center rounded text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg)] group-hover:flex"
                      >
                        <i className="ri-delete-bin-line text-xs" aria-hidden />
                      </button>
                    </div>
                  ))}
                  <div className="mx-2 my-1 h-px bg-[color:var(--color-border)]" aria-hidden />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => stampUploadInputRef.current?.click()}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)]"
                  >
                    <i className="ri-upload-2-line" aria-hidden /> Add image as stamp…
                  </button>
                </div>
              </>
            )}
          </div>
          </>
        )}


        <span className="mx-2 h-5 w-px bg-[color:var(--color-border)]" aria-hidden />

        <button
          type="button"
          title="Undo (Ctrl+Z)"
          disabled={doc.past.length === 0}
          onClick={() => {
            dispatch({ type: "undo" });
            selectAnnot(null);
          }}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
        >
          <i className="ri-arrow-go-back-line" aria-hidden />
        </button>
        <button
          type="button"
          title="Redo (Ctrl+Shift+Z)"
          disabled={doc.future.length === 0}
          onClick={() => {
            dispatch({ type: "redo" });
            selectAnnot(null);
          }}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
        >
          <i className="ri-arrow-go-forward-line" aria-hidden />
        </button>
        <div className="relative">
          <button
            type="button"
            title="Checkpoints — restore an earlier state of this document"
            aria-haspopup="menu"
            aria-pressed={historyMenuOpen}
            disabled={!pages}
            onClick={() => setHistoryMenuOpen((o) => !o)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
          >
            <i className="ri-history-line" aria-hidden />
          </button>
          {historyMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setHistoryMenuOpen(false)}
                aria-hidden
              />
              <div
                role="menu"
                className="absolute left-0 top-full z-40 mt-1 w-72 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-1 shadow-lg"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setHistoryMenuOpen(false);
                    addCheckpoint("Checkpoint");
                    showToast("Checkpoint added");
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)]"
                >
                  <i className="ri-bookmark-2-line" aria-hidden /> Checkpoint now
                </button>
                <div className="mx-2 my-1 h-px bg-[color:var(--color-border)]" aria-hidden />
                {(activeId ? (checkpoints[activeId] ?? []) : []).length === 0 && (
                  <p className="px-2 py-1.5 text-xs text-[color:var(--color-fg-muted)]">
                    No checkpoints yet. One is added automatically on every
                    download; add one manually before risky edits.
                  </p>
                )}
                {(activeId ? (checkpoints[activeId] ?? []) : [])
                  .slice()
                  .reverse()
                  .map((cp) => (
                    <button
                      key={cp.id}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setHistoryMenuOpen(false);
                        restoreCheckpoint(cp);
                      }}
                      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)]"
                    >
                      <i
                        className={
                          cp.label === "Download" ? "ri-download-2-line" : "ri-bookmark-2-line"
                        }
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {cp.label} · {cp.baseline.pages.length} page
                        {cp.baseline.pages.length === 1 ? "" : "s"} · {cp.annots.length} annot
                        {cp.annots.length === 1 ? "" : "s"}
                      </span>
                      <span className="text-xs tabular-nums text-[color:var(--color-fg-muted)]">
                        {new Date(cp.time).toLocaleTimeString()}
                      </span>
                    </button>
                  ))}
              </div>
            </>
          )}
        </div>

        <span className="mx-2 h-5 w-px bg-[color:var(--color-border)]" aria-hidden />

        <button type="button" title="Zoom out (-)" onClick={() => zoomBy(-1)} className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]">
          <i className="ri-zoom-out-line" aria-hidden />
        </button>
        <div className="relative">
          <button
            type="button"
            title="Zoom presets"
            aria-haspopup="menu"
            onClick={() => setZoomMenuOpen((o) => !o)}
            className="inline-flex h-8 w-16 items-center justify-center gap-0.5 rounded-md text-xs tabular-nums text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
          >
            {Math.round(scale * 100)}%
            <i className="ri-arrow-down-s-line" aria-hidden />
          </button>
          {zoomMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setZoomMenuOpen(false)}
                aria-hidden
              />
              <div
                role="menu"
                className="absolute left-0 top-full z-40 mt-1 w-44 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-1 shadow-lg"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setZoomMenuOpen(false);
                    fitWidth();
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)]"
                >
                  <i className="ri-aspect-ratio-line" aria-hidden />
                  Fit width
                  <span className="ml-auto text-xs text-[color:var(--color-fg-muted)]">Ctrl+0</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setZoomMenuOpen(false);
                    fitPage();
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)]"
                >
                  <i className="ri-artboard-line" aria-hidden />
                  Fit page
                </button>
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={spread}
                  onClick={() => {
                    setZoomMenuOpen(false);
                    setSpread((v) => !v);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)]"
                >
                  <i
                    className={spread ? "ri-checkbox-line" : "ri-checkbox-blank-line"}
                    aria-hidden
                  />
                  Two-page view
                </button>
                <div className="mx-2 my-1 h-px bg-[color:var(--color-border)]" aria-hidden />
                {[0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4].map((z) => (
                  <button
                    key={z}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setZoomMenuOpen(false);
                      setScale(z);
                    }}
                    className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm tabular-nums hover:bg-[color:var(--color-bg-subtle)] ${
                      Math.abs(scale - z) < 0.005 ? "text-[color:var(--color-accent)]" : ""
                    }`}
                  >
                    {Math.round(z * 100)}%
                    {z === 1 && (
                      <span className="ml-auto text-xs text-[color:var(--color-fg-muted)]">
                        Ctrl+1
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <button type="button" title="Zoom in (+)" onClick={() => zoomBy(1)} className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]">
          <i className="ri-zoom-in-line" aria-hidden />
        </button>
        <button type="button" title="Fit width" onClick={fitWidth} className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]">
          <i className="ri-aspect-ratio-line" aria-hidden />
        </button>
        {/* panel toggles — rendered FROM the command registry, which
            already backs the palette and the keyboard. One entry there
            now yields a shortcut, a palette row and this button. */}
        {commandGroup(commands, "panel").map((c) => (
          <CommandButton key={c.id} command={c} />
        ))}
        <button
          type="button"
          title="Inspector — outline text blocks, lines, images, form fields and annotations"
          aria-pressed={inspect}
          onClick={() => setInspect((v) => !v)}
          className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${
            inspect
              ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
              : "hover:bg-[color:var(--color-bg-subtle)]"
          }`}
        >
          <i className="ri-focus-2-line" aria-hidden />
        </button>
        <button
          type="button"
          title="Find in document (Ctrl+F)"
          aria-pressed={searchOpen}
          onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
          className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${
            searchOpen
              ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
              : "hover:bg-[color:var(--color-bg-subtle)]"
          }`}
        >
          <i className="ri-search-line" aria-hidden />
        </button>
        <button
          type="button"
          title="Command palette (Ctrl+Shift+P)"
          aria-pressed={paletteOpen}
          onClick={() => setPaletteOpen(true)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]"
        >
          <i className="ri-command-line" aria-hidden />
        </button>
        <button
          type="button"
          title="Editor guide & keyboard shortcuts (?)"
          aria-pressed={helpOpen}
          onClick={() => setHelpOpen(true)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]"
        >
          <i className="ri-question-line" aria-hidden />
        </button>

        {/* Right edge is now status only — the download button and the
            print icon moved into the File menu with the rest of the
            file operations. */}
        <div className="ml-auto flex items-center gap-2">
          {opening && (
            <span className="text-xs text-[color:var(--color-fg-muted)]">
              <i className="ri-loader-4-line inline-block animate-spin" aria-hidden /> opening…
            </span>
          )}
          {converting && (
            <span className="text-xs text-[color:var(--color-fg-muted)]">
              <i className="ri-loader-4-line inline-block animate-spin" aria-hidden /> converting…
            </span>
          )}
          {saving && (
            <span className="text-xs text-[color:var(--color-fg-muted)]">
              <i className="ri-loader-4-line inline-block animate-spin" aria-hidden /> saving…
            </span>
          )}
        </div>
      </div>
        </>
      )}

      {/* body */}
      <div className="relative flex min-h-0 flex-1">
        {/* inspector legend */}
        {inspect && (
          <div className="absolute bottom-3 left-1/2 z-30 flex -translate-x-1/2 items-center gap-3 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-3 py-1.5 text-xs shadow-lg">
            {(
              [
                ["Text block", INSPECT_COLORS.block],
                ["Line", INSPECT_COLORS.line],
                ["Image", INSPECT_COLORS.image],
                ["Form field", INSPECT_COLORS.widget],
                ["Annotation", INSPECT_COLORS.annot],
              ] as const
            ).map(([label, color]) => (
              <span key={label} className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-3 w-3 rounded-sm border-2"
                  style={{ borderColor: color }}
                />
                {label}
              </span>
            ))}
          </div>
        )}

        {/* thumbnail batch bar */}
        {pageSel.size > 0 && (
          <div
            className="absolute left-1/2 z-30 flex -translate-x-1/2 items-center gap-1 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 py-1.5 text-xs shadow-lg"
            style={{ bottom: inspect ? 44 : 12 }}
          >
            <span className="px-1 font-medium tabular-nums">
              {pageSel.size} page{pageSel.size === 1 ? "" : "s"} selected
            </span>
            <button
              type="button"
              title="Extract the selected pages into a new tab"
              disabled={saving}
              onClick={() => doExtractPages("tab")}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-[color:var(--color-border)] px-2 hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-file-add-line" aria-hidden /> Extract to tab
            </button>
            <button
              type="button"
              title="Download the selected pages as a new PDF"
              disabled={saving}
              onClick={() => doExtractPages("download")}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-[color:var(--color-border)] px-2 hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-download-2-line" aria-hidden /> Download
            </button>
            <button
              type="button"
              title="Rotate the selected pages anti-clockwise"
              disabled={pageOpBusy}
              onClick={() => rotateSelectedPages(false)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[color:var(--color-border)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-anticlockwise-line" aria-hidden />
            </button>
            <button
              type="button"
              title="Rotate the selected pages clockwise"
              disabled={pageOpBusy}
              onClick={() => rotateSelectedPages(true)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[color:var(--color-border)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-clockwise-line" aria-hidden />
            </button>
            <button
              type="button"
              title="Delete the selected pages (one undo step)"
              disabled={pageOpBusy || pageSel.size >= (pages?.length ?? 0)}
              onClick={doDeletePagesBatch}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-[color:var(--color-border)] px-2 text-[color:var(--color-danger,#e03131)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-delete-bin-line" aria-hidden /> Delete
            </button>
            <button
              type="button"
              title="Clear the page selection"
              onClick={() => setPageSel(new Set())}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-close-line" aria-hidden />
            </button>
          </div>
        )}

        {/* find-in-document panel */}
        {findRedactOpen && (
          <FindRedactPanel
            busy={scanBusy}
            results={scanResults}
            error={scanError}
            selected={scanSelected}
            onScan={runScan}
            onToggle={(i) =>
              setScanSelected((cur) => {
                const next = new Set(cur);
                if (!next.delete(i)) next.add(i);
                return next;
              })
            }
            onSelectAll={(on) =>
              setScanSelected(
                on ? new Set((scanResults?.hits ?? []).map((_, i) => i)) : new Set()
              )
            }
            onJump={(hit) => scrollToPage(hit.pageIndex)}
            onMark={markScanHits}
            onClose={() => setFindRedactOpen(false)}
          />
        )}

        {searchOpen && (
          <div className="absolute right-6 top-2 z-30 flex flex-col gap-1 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-1 shadow-lg">
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              title="Find & replace"
              aria-pressed={replaceOpen}
              onClick={() => setReplaceOpen((v) => !v)}
              className={`inline-flex h-7 w-7 items-center justify-center rounded ${
                replaceOpen
                  ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                  : "hover:bg-[color:var(--color-bg-subtle)]"
              }`}
            >
              <i className="ri-find-replace-line" aria-hidden />
            </button>
            <i className="ri-search-line px-1.5 text-[color:var(--color-fg-muted)]" aria-hidden />
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Find in document…"
              className="h-7 w-44 bg-transparent px-1 text-sm outline-none"
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (e.shiftKey) searchPrev();
                  else searchNext();
                } else if (e.key === "Escape") {
                  closeSearch();
                }
              }}
            />
            <span className="w-14 text-center text-xs tabular-nums text-[color:var(--color-fg-muted)]">
              {searchBusy ? (
                <i className="ri-loader-4-line inline-block animate-spin" aria-hidden />
              ) : searchHits.length > 0 ? (
                `${searchCurrent + 1} / ${searchHits.length}`
              ) : searchQuery.trim() ? (
                "0"
              ) : (
                ""
              )}
            </span>
            <button
              type="button"
              title="Previous match (Shift+Enter)"
              disabled={searchHits.length === 0}
              onClick={searchPrev}
              className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-arrow-up-s-line" aria-hidden />
            </button>
            <button
              type="button"
              title="Next match (Enter)"
              disabled={searchHits.length === 0}
              onClick={searchNext}
              className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-arrow-down-s-line" aria-hidden />
            </button>
            <button
              type="button"
              title="Close (Esc)"
              onClick={closeSearch}
              className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-close-line" aria-hidden />
            </button>
          </div>
          {replaceOpen && (
            <div className="flex items-center gap-0.5">
              <span className="w-7" aria-hidden />
              <i className="ri-arrow-left-right-line px-1.5 text-[color:var(--color-fg-muted)]" aria-hidden />
              <input
                value={replaceValue}
                onChange={(e) => setReplaceValue(e.target.value)}
                placeholder="Replace with…"
                className="h-7 w-44 bg-transparent px-1 text-sm outline-none"
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    e.preventDefault();
                    doReplace(false);
                  } else if (e.key === "Escape") {
                    closeSearch();
                  }
                }}
              />
              <button
                type="button"
                title="Replace the current match"
                disabled={searchHits.length === 0 || pageOpBusy}
                onClick={() => doReplace(false)}
                className="inline-flex h-7 items-center rounded px-2 text-xs hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                Replace
              </button>
              <button
                type="button"
                title="Replace every match in the document"
                disabled={searchHits.length === 0 || pageOpBusy}
                onClick={() => doReplace(true)}
                className="inline-flex h-7 items-center rounded px-2 text-xs hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                All
              </button>
            </div>
          )}
          </div>
        )}
        {pages && pages.length > 0 && (
          <div
            className="hidden shrink-0 space-y-1 overflow-y-auto border-r border-[color:var(--color-border)] p-2 sm:block"
            style={{ width: railWidth }}
          >
            {pages.map((info, i) => (
              <div
                // Keyed by tab AND baseline gen: page ops re-raster
                // every thumb (dims/content may have changed).
                key={`${activeId}:${doc.baseline?.gen ?? 0}:${i}`}
                draggable={!pageOpBusy}
                onDragStart={(e) => {
                  dragPage.current = i;
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  if (dragPage.current === null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverPage(i);
                }}
                onDragLeave={() => setDragOverPage((v) => (v === i ? null : v))}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = dragPage.current;
                  dragPage.current = null;
                  setDragOverPage(null);
                  if (from !== null && from !== i) doPageOp({ kind: "move", from, to: i });
                }}
                onDragEnd={() => {
                  dragPage.current = null;
                  setDragOverPage(null);
                }}
                className={
                  dragOverPage === i && dragPage.current !== i
                    ? "rounded-sm ring-2 ring-[color:var(--color-accent)]"
                    : ""
                }
              >
                <ThumbView
                  engine={engineRef.current}
                  pageIndex={i}
                  info={info}
                  active={i === currentPage}
                  pageSelected={pageSel.has(i)}
                  busy={pageOpBusy}
                  canDelete={pages.length > 1}
                  onClick={onThumbClick}
                  onRotate={rotatePage}
                  onDelete={deletePage}
                />
              </div>
            ))}
            <button
              type="button"
              title="Insert a blank page after the current page"
              disabled={pageOpBusy}
              onClick={() => {
                const info = pages[currentPage] ?? A4;
                doPageOp({
                  kind: "insertBlank",
                  at: currentPage + 1,
                  width: info.width,
                  height: info.height,
                });
              }}
              className="flex w-full items-center justify-center gap-1 rounded-sm border border-dashed border-[color:var(--color-border-strong)] px-1 py-2 text-[10px] text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-add-line" aria-hidden /> Add page
            </button>
            {has("doc.insertPdf") && (
              <button
                type="button"
                title="Insert pages from another PDF after the current page"
                disabled={pageOpBusy}
                onClick={() => insertPdfInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-1 rounded-sm border border-dashed border-[color:var(--color-border-strong)] px-1 py-2 text-[10px] text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                <i className="ri-file-add-line" aria-hidden /> Insert PDF
              </button>
            )}
            <input
              ref={xfdfInputRef}
              type="file"
              accept=".xfdf,.xml,application/vnd.adobe.xfdf,text/xml"
              data-role="xfdf-input"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void onImportXfdf(file);
              }}
            />
            <input
              ref={combinePdfInputRef}
              type="file"
              multiple
              accept="application/pdf,.pdf"
              data-role="combine-input"
              className="hidden"
              onChange={(e) => {
                const files = [...(e.target.files ?? [])];
                e.target.value = "";
                if (files.length) void onPickCombinePdfs(files);
              }}
            />
            <input
              ref={insertPdfInputRef}
              type="file"
              accept="application/pdf,.pdf"
              data-role="insert-input"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void onPickInsertPdf(file);
              }}
            />
            {has("doc.importMd") && (
              <button
                type="button"
                title="Import a Markdown file as a styled PDF — pick a theme, customise it, preview, done"
                disabled={pageOpBusy}
                onClick={() => mdInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-1 rounded-sm border border-dashed border-[color:var(--color-border-strong)] px-1 py-2 text-[10px] text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                <i className="ri-markdown-line" aria-hidden /> Import MD
              </button>
            )}
            {has("doc.importDocx") && (
              <button
                type="button"
                title="Import a Word document (.docx) as a styled PDF — pick a theme, customise it, preview, done"
                disabled={pageOpBusy}
                onClick={() => docxInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-1 rounded-sm border border-dashed border-[color:var(--color-border-strong)] px-1 py-2 text-[10px] text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                <i className="ri-file-word-line" aria-hidden /> Import Word
              </button>
            )}
            {has("doc.importSheet") && (
              <button
                type="button"
                title="Import a spreadsheet as a styled PDF table — CSV, Excel, ODS and more"
                disabled={pageOpBusy}
                onClick={() => sheetInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-1 rounded-sm border border-dashed border-[color:var(--color-border-strong)] px-1 py-2 text-[10px] text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                <i className="ri-table-line" aria-hidden /> Import sheet
              </button>
            )}
            {has("doc.importPhotos") && (
              <button
                type="button"
                title="Turn images into a PDF — one per page or a grid, with frames and captions"
                disabled={pageOpBusy}
                onClick={() => photoInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-1 rounded-sm border border-dashed border-[color:var(--color-border-strong)] px-1 py-2 text-[10px] text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                <i className="ri-image-line" aria-hidden /> Images to PDF
              </button>
            )}
            {panelAllowed("pageSetup") && (
              <button
                type="button"
                title="Page size and margins"
                disabled={pageOpBusy}
                onClick={() => togglePanel("pageSetup")}
                className="flex w-full items-center justify-center gap-1 rounded-sm border border-dashed border-[color:var(--color-border-strong)] px-1 py-2 text-[10px] text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                <i className="ri-artboard-2-line" aria-hidden /> Page setup
              </button>
            )}
            {panelAllowed("decor") && (
              <button
                type="button"
                title="Headers, footers, page numbers and watermark"
                disabled={pageOpBusy}
                onClick={() => togglePanel("decor")}
                className="flex w-full items-center justify-center gap-1 rounded-sm border border-dashed border-[color:var(--color-border-strong)] px-1 py-2 text-[10px] text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                <i className="ri-layout-top-line" aria-hidden /> Headers
              </button>
            )}
            {has("doc.ocr") && (
              <button
                type="button"
                title="Recognise text on scanned pages (OCR) — makes them searchable and selectable"
                disabled={pageOpBusy}
                onClick={() => setOcrOpen(true)}
                className="flex w-full items-center justify-center gap-1 rounded-sm border border-dashed border-[color:var(--color-border-strong)] px-1 py-2 text-[10px] text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                <i className="ri-character-recognition-line" aria-hidden /> OCR
              </button>
            )}
            {has("doc.findRedact") && (
              <button
                type="button"
                title="Find every occurrence of a phrase — or of emails, phone numbers, card numbers — and mark it for redaction"
                disabled={pageOpBusy}
                onClick={() => setFindRedactOpen(true)}
                className="flex w-full items-center justify-center gap-1 rounded-sm border border-dashed border-[color:var(--color-border-strong)] px-1 py-2 text-[10px] text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                <i className="ri-shield-flash-line" aria-hidden /> Find &amp; redact
              </button>
            )}
            {has("doc.compare") && (
              <button
                type="button"
                title="Compare this document with another PDF — see what changed"
                disabled={pageOpBusy}
                onClick={() => comparePdfInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-1 rounded-sm border border-dashed border-[color:var(--color-border-strong)] px-1 py-2 text-[10px] text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                <i className="ri-contrast-2-line" aria-hidden /> Compare
              </button>
            )}
            <input
              ref={fontUploadInputRef}
              type="file"
              accept=".ttf,.otf,font/ttf,font/otf"
              data-role="font-upload-input"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void onUploadFont(file);
              }}
            />
            <input
              ref={comparePdfInputRef}
              type="file"
              accept="application/pdf,.pdf"
              data-role="compare-input"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void onPickComparePdf(file);
              }}
            />
          </div>
        )}

        {pages && pages.length > 0 && (
          <PaneResizer
            side="left"
            width={railWidth}
            min={72}
            max={320}
            onResize={setRailWidth}
          />
        )}

        <div
          ref={mainRef}
          onScroll={onMainScroll}
          data-pan={panning ? "active" : panMode ? "idle" : undefined}
          onPointerDown={onPanPointerDown}
          onPointerMove={onPanPointerMove}
          onPointerUp={endPan}
          onPointerCancel={endPan}
          onAuxClick={(e) => {
            if (e.button === 1) e.preventDefault();
          }}
          className="pe-main min-w-0 flex-1 overflow-auto bg-[color:var(--color-bg-subtle)] p-6"
        >
          {pages ? (
            <div
              className={
                spread
                  ? "grid grid-cols-2 items-start justify-items-center gap-6"
                  : "space-y-6"
              }
            >
              {pages.map((info, i) => (
              <PageView
                key={`${activeId}:${doc.baseline?.gen ?? 0}:${i}`}
                engine={engineRef.current}
                pageIndex={i}
                info={info}
                scale={scale}
                tool={panMode ? "hand" : tool}
                styles={toolStyles}
                annots={annotsByPage.get(i) ?? []}
                annotFontCss={annotFontCss}
                onRotateSelection={onRotateSelectionDrag}
                onMoveField={doMoveField}
                selectedIds={selectedIdSet}
                editingId={editingId}
                guides={snapGuides?.pageIndex === i ? snapGuides : undefined}
                onMarqueeSelect={onMarqueeSelect}
                onContextMenuAt={onContextMenuAt}
                measureUnit={measureUnit}
                measureFactor={measureFactor}
                onMeasure={onMeasure}
                searchHits={searchHitsByPage.get(i)}
                inspect={inspect}
                widgets={widgetsByPage.get(i)}
                formValues={formValues}
                onSetFormValue={setFormValue}
                registerEl={registerEl}
                onCreateMarkup={onCreateMarkup}
                onCreateText={onCreateText}
                onCreateInk={onCreateInk}
                onCreateShape={onCreateShape}
                onCreateNote={onCreateNote}
                onCreateRedact={onCreateRedact}
                onTapAt={onTapAt}
                textEdit={textEdit?.pageIndex === i ? textEdit : undefined}
                editFontCss={editFontCss}
                onCommitTextEdit={onCommitTextEdit}
                onCancelTextEdit={onCancelTextEdit}
                onPatchTextEdit={patchTextEdit}
                imageSel={imageSel?.pageIndex === i ? imageSel : undefined}
                fillSel={fillSel?.pageIndex === i ? fillSel : undefined}
                textSel={textSel?.pageIndex === i ? textSel : undefined}
                onTextSel={onTextSel}
                opBusy={pageOpBusy}
                onImageMove={(to) => doImageOp({ kind: "move", to })}
                onImageDelete={() => doImageOp({ kind: "delete" })}
                onImageReplaceRequest={() => replaceImageInputRef.current?.click()}
                onFillMove={(to) => doFillOp({ kind: "move", to })}
                onFillDelete={() => doFillOp({ kind: "delete" })}
                onFillRecolor={(hex) => doFillOp({ kind: "recolor", color: hex })}
                onFillRestack={(front) => doFillOp({ kind: "restack", front })}
                onGradientStops={(stops) => doFillOp({ kind: "gradientStops", ...stops })}
                onGradientReverse={() => doFillOp({ kind: "gradientReverse" })}
                onCreateBgBlock={onCreateBgBlock}
                onCreateLink={onCreateLink}
                onCropRegion={onCropRegion}
                onCreateFieldRegion={onCreateFieldRegion}
                regionSel={regionSel?.pageIndex === i ? regionSel : undefined}
                onRegionMove={onRegionMove}
                regionNudge={regionSel?.pageIndex === i ? nudgePreview : undefined}
                onRegionTapAt={onRegionTapAt}
                onToggleObjectAt={onToggleObjectAt}
                onFollowLink={onFollowLink}
                onEditLink={onEditLink}
                onSelectAnnot={selectAnnot}
                onStartEdit={startEditAnnot}
                onCommitText={onCommitText}
                onInputText={onInputText}
                onCommitNote={onCommitNote}
                onMoveText={onMoveText}
                onMeasureText={onMeasureText}
                onPlaceStamp={onPlaceStamp}
                onDragAnnot={onDragAnnot}
                onResizeShape={onResizeShape}
                onResizeStamp={onResizeStamp}
              />
              ))}
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[color:var(--color-fg-muted)]">
              <i className="ri-loader-4-line mr-2 inline-block animate-spin" aria-hidden />
              Loading the editor — first visit downloads the ~5&nbsp;MB engine…
            </div>
          )}
        </div>

        {rightPaneOpen && (
          <PaneResizer
            side="right"
            width={paneWidth}
            min={200}
            max={560}
            onResize={setPaneWidth}
          />
        )}

        {/* right column: the armed tool's options on top, then the
            panel drawer. One width, one resizer, one border — the two
            stack inside it rather than fighting over the edge. */}
        {rightPaneOpen && (
          <div
            className="flex min-h-0 shrink-0 flex-col border-l border-[color:var(--color-border)]"
            style={{ width: paneWidth }}
          >
        {showOptionsPanel && (
          <OptionsPanel
            capped={panel !== null}
            onHidePane={() => {
              setPaneOpen(false);
              setPanel(null);
            }}
            {...propsBarModel}
          />
        )}
        {panel !== null && pages && (
          <PanelDrawer panel={panel} onSelect={setPanel}>
          {/* page setup sidebar (size / orientation / margins + preview) */}
          {panel === "pageSetup" && (
            <PageSetupPanel
              paneWidth={paneWidth}
              engine={engineRef.current}
              pages={pages}
              currentPage={currentPage}
              gen={doc.baseline?.gen ?? 0}
              busy={pageOpBusy}
              onClose={() => setPanel(null)}
              onApply={(op) => doPageOp(op)}
              onRotate={(t, cw) => {
                const list = t === "all" ? (pages ?? []).map((_, i) => i) : [currentPage];
                // Each rotation is its own page op; they queue in order.
                for (const i of list) rotatePage(i, cw);
              }}
            />
          )}

          {/* headers / footers / watermark sidebar */}
          {panel === "decor" && (
            <DecorPanel
              paneWidth={paneWidth}
              currentPage={currentPage}
              pageCount={pages.length}
              busy={pageOpBusy}
              customFonts={customFonts}
              onClose={() => setPanel(null)}
              onApply={doDecorate}
            />
          )}

          {/* bookmarks (document outline) panel */}
          {panel === "bookmarks" && (
            <BookmarksPanel
              paneWidth={paneWidth}
              items={outlineItems}
              currentPage={currentPage}
              pageCount={pages.length}
              busy={pageOpBusy}
              onClose={() => setPanel(null)}
              onJump={(p) => scrollToPage(p)}
              onApply={doSetOutline}
            />
          )}

          {/* object list (paint order) */}
          {panel === "objects" && (
            <ObjectsPanel
              pageIndex={currentPage}
              pageCount={pages.length}
              objects={pageObjects}
              selectedId={selectedObjId}
              busy={pageOpBusy}
              paneWidth={paneWidth}
              onClose={() => {
                setPanel(null);
                setSelectedObjId(null);
              }}
              onGoToPage={(p) => scrollToPage(p)}
              onSelect={(o) => {
                setSelectedObjId(o?.id ?? null);
                if (!o) {
                  setSel((s) => (s.kind === "image" || s.kind === "fill" ? NO_SELECTION : s));
                  return;
                }
                scrollToPage(currentPage);
                // Mirror the click-selection the canvas would give.
                if (o.kind === "image") {
                  setSel({ kind: "image", pageIndex: currentPage, bbox: o.bbox });
                } else if (o.fillId) {
                  // Select by the object's OWN id — a centre-point hit
                  // test misses hollow shapes (outlines, stripe groups).
                  setSel({
                    kind: "fill",
                    pageIndex: currentPage,
                    fill: {
                      id: o.fillId,
                      bbox: o.bbox,
                      color: o.color ?? "#888888",
                      alpha: o.alpha ?? 1,
                      stroke: o.kind === "stroke",
                      rect: false,
                      partOfGroup: false,
                      recolorable: true,
                    },
                  });
                } else {
                  setSel((s) => (s.kind === "image" || s.kind === "fill" ? NO_SELECTION : s));
                }
              }}
              onEditText={(o) => {
                setTool("select");
                onEditTextAt(
                  currentPage,
                  o.bbox[0] + 2,
                  (o.bbox[1] + o.bbox[3]) / 2,
                  "line"
                );
              }}
            />
          )}

          {/* document properties (Info dictionary + security) */}
          {panel === "properties" && (
            <PropertiesPanel
              info={docInfo}
              security={security}
              busy={pageOpBusy}
              paneWidth={paneWidth}
              onClose={() => setPanel(null)}
              onApply={doSetDocInfo}
              onEditSecurity={() => setSecurityOpen(true)}
            />
          )}

          {/* accessibility audit + the repairs that are a flag */}
          {panel === "accessibility" && (
            <AccessibilityPanel
              facts={a11yFacts}
              error={a11yError}
              overlay={a11yOverlay}
              suggestedTitle={suggestedTitle}
              busy={pageOpBusy}
              paneWidth={paneWidth}
              onClose={() => setPanel(null)}
              onFix={onA11yFix}
              onGoTo={onA11yGoTo}
            />
          )}

          {/* font inventory panel */}
          {panel === "fonts" && (
            <FontsPanel fonts={docFonts} paneWidth={paneWidth} onClose={() => setPanel(null)} />
          )}

          {/* comments / annotations panel */}
          {panel === "comments" && (
            <CommentsPanel
              paneWidth={paneWidth}
              engine={engineRef.current}
              annots={doc.annots}
              author={author}
              onAuthorChange={setAuthor}
              onReply={onReplyToAnnot}
              onSetStatus={onSetAnnotStatus}
              onSetContents={onSetAnnotContents}
              onClose={() => setPanel(null)}
              onJump={(a) => {
                setTool("select");
                scrollToPage(a.pageIndex);
                selectAnnot(a.id);
              }}
              onEdit={(a) => {
                scrollToPage(a.pageIndex);
                if (a.kind === "link") {
                  selectAnnot(a.id);
                  onEditLink(a);
                  return;
                }
                setTool("select");
                startEditAnnot(a.id);
              }}
              onDelete={(id) => {
                dispatch({
                  type: "commit",
                  annots: annotsRef.current.filter((x) => x.id !== id),
                });
                clearAnnotIf(id);
              }}
              onCopySummary={(lines) => {
                void navigator.clipboard
                  .writeText(lines.join("\n"))
                  .then(() => showToast("Comment summary copied to clipboard"))
                  .catch(() => showToast("Could not copy to clipboard"));
              }}
            />
          )}
          </PanelDrawer>
        )}
          </div>
        )}
      </div>

      {compact && (
        <MobileBottomBar
          tool={tool}
          toolLocked={toolLocked}
          onPickTool={armTool}
          propsBar={propsBarModel}
        />
      )}

      {/* status bar */}
      {statusBarEnabled && (
      <div className="flex items-center gap-3 border-t border-[color:var(--color-border)] px-3 py-1 text-xs text-[color:var(--color-fg-muted)]">
        <span>
          Page {currentPage + 1} / {pages?.length ?? "—"}
        </span>
        <span>{Math.round(scale * 100)}%</span>
        <span>
          {doc.annots.length} annotation{doc.annots.length === 1 ? "" : "s"}
        </span>
        <span className="ml-auto hidden sm:inline">
          MuPDF · WebAssembly · nothing uploaded
        </span>
      </div>
      )}

      {/* first-run welcome overlay — sits over the blank document */}
      {welcome && (
        <div role="dialog" aria-modal="true" className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6">
          <div className="w-full max-w-xl space-y-4 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold tracking-tight">PDF Editor</h1>
                <p className="text-sm text-[color:var(--color-fg-muted)]">
                  Add text, sticky notes and shapes, draw freehand, and
                  highlight, underline or strike out existing text — then
                  download a PDF with real, Acrobat-editable annotations.
                  Everything runs locally — powered by the MuPDF engine
                  compiled to WebAssembly.
                </p>
              </div>
              <button
                type="button"
                title="Close"
                aria-label="Close welcome"
                onClick={() => setWelcome(false)}
                className="ml-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
              >
                <i className="ri-close-line" aria-hidden />
              </button>
            </div>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              className={`rounded-md border-2 border-dashed p-8 text-center ${
                isDragOver
                  ? "border-[color:var(--color-accent)] bg-[color:var(--color-bg-subtle)]"
                  : "border-[color:var(--color-border)] bg-[color:var(--color-bg-subtle)]"
              }`}
            >
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-4 py-2 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90"
              >
                <i className="ri-file-pdf-line" aria-hidden /> Choose PDF
              </button>
              <p className="mt-3 text-xs text-[color:var(--color-fg-muted)]">
                Or drag &amp; drop. Your file never leaves this page.
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs text-[color:var(--color-fg-muted)]">
              <span className="h-px flex-1 bg-[color:var(--color-border)]" aria-hidden />
              or
              <span className="h-px flex-1 bg-[color:var(--color-border)]" aria-hidden />
            </div>
            <button
              type="button"
              onClick={() => docxInputRef.current?.click()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-4 py-2 text-sm hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-file-word-line" aria-hidden /> Import a Word
              document (.docx) as a styled PDF
            </button>
            <button
              type="button"
              onClick={() => mdInputRef.current?.click()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-4 py-2 text-sm hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-markdown-line" aria-hidden /> Import a Markdown
              file as a styled PDF
            </button>
            <button
              type="button"
              onClick={() => sheetInputRef.current?.click()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-4 py-2 text-sm hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-table-line" aria-hidden /> Import a spreadsheet
              (CSV, Excel, ODS) as a PDF table
            </button>
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-4 py-2 text-sm hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-image-line" aria-hidden /> Turn images (JPG, PNG,
              HEIC) into a PDF
            </button>
            <button
              type="button"
              onClick={() => setWelcome(false)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-4 py-2 text-sm hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-file-add-line" aria-hidden /> Start with a blank
              page (A4)
            </button>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="inline-flex w-full items-center justify-center gap-1.5 text-xs text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]"
            >
              <i className="ri-question-line" aria-hidden /> New here? Open the
              editor guide — every feature, explained
            </button>
          </div>
        </div>
      )}

      {/* compare mode — full-screen, read-only */}
      {compare && (
        <CompareView
          name={compare.name}
          countA={compare.countA}
          countB={compare.countB}
          renderA={compareRenderA}
          renderB={compareRenderB}
          onClose={closeCompare}
        />
      )}

      {/* markdown import — full-screen theme/preview flow */}
      {mdImport && (
        <ImportMdView
          name={mdImport.name}
          markdown={mdImport.text}
          customFonts={customFonts}
          title={
            mdImport.kind === "docx"
              ? "Import Word document"
              : mdImport.kind === "html"
                ? "Import HTML"
                : "Import Markdown"
          }
          icon={
            mdImport.kind === "docx"
              ? "ri-file-word-line"
              : mdImport.kind === "html"
                ? "ri-html5-line"
                : "ri-markdown-line"
          }
          notes={mdImport.notes}
          build={mdBuild}
          renderPage={mdRenderPage}
          commit={mdCommit}
          onCancel={cancelMdImport}
          onCommit={onMdImported}
        />
      )}

      {/* image import — same flow, layout/frame/caption theming */}
      {photoImport && !mdImport && !sheetImport && (
        <ImportPhotosView
          name={photoImport.name}
          images={photoImport.images}
          failed={photoImport.failed}
          customFonts={customFonts}
          build={photoBuild}
          renderPage={mdRenderPage}
          commit={mdCommit}
          onCancel={cancelPhotoImport}
          onCommit={onPhotosImported}
        />
      )}

      {/* spreadsheet import — same flow, sheet-specific theming */}
      {sheetImport && !mdImport && (
        <ImportSheetView
          name={sheetImport.name}
          sheets={sheetImport.sheets}
          truncated={sheetImport.truncated}
          customFonts={customFonts}
          build={sheetBuild}
          renderPage={mdRenderPage}
          commit={mdCommit}
          onCancel={cancelSheetImport}
          onCommit={onSheetImported}
        />
      )}

      {/* form-field dialog */}
      {fieldDraft && (
        <div role="dialog" aria-modal="true" className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              commitFieldDraft();
            }}
            className="w-full max-w-sm space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl"
          >
            <div className="flex items-center gap-2">
              <i className="ri-input-field text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
              <h2 className="text-base font-semibold">Add form field</h2>
              <span className="ml-auto text-xs text-[color:var(--color-fg-muted)]">
                page {fieldDraft.pageIndex + 1}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-14 shrink-0 text-[color:var(--color-fg-muted)]">Type</span>
              <div className="grid min-w-0 flex-1 grid-cols-3 overflow-hidden rounded-md border border-[color:var(--color-border)]">
                {(
                  [
                    ["text", "Text", "ri-input-cursor-move"],
                    ["checkbox", "Checkbox", "ri-checkbox-line"],
                    ["combo", "Dropdown", "ri-arrow-down-s-line"],
                    ["radio", "Radio", "ri-radio-button-line"],
                    ["signature", "Signature", "ri-quill-pen-line"],
                  ] as const
                ).map(([k, label, icon]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setFieldDraft((d) => (d ? { ...d, kind: k } : d))}
                    className={`inline-flex h-8 items-center justify-center gap-1 text-xs ${
                      fieldDraft.kind === k
                        ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                        : "hover:bg-[color:var(--color-bg-subtle)]"
                    }`}
                  >
                    <i className={icon} aria-hidden />
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-14 shrink-0 text-[color:var(--color-fg-muted)]">Name</span>
              <input
                autoFocus
                value={fieldDraft.name}
                onChange={(e) => setFieldDraft((d) => (d ? { ...d, name: e.target.value } : d))}
                placeholder={
                  fieldDraft.kind === "text"
                    ? "e.g. Customer name"
                    : fieldDraft.kind === "checkbox"
                      ? "e.g. Agree to terms"
                      : fieldDraft.kind === "radio"
                        ? "Group — e.g. Delivery method"
                        : fieldDraft.kind === "signature"
                          ? "e.g. Applicant signature"
                          : "e.g. Plan"
                }
                className="h-8 min-w-0 flex-1 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-sm"
              />
            </div>
            {fieldDraft.kind === "text" && (
              <label className="flex cursor-pointer items-center gap-1.5 pl-16 text-xs text-[color:var(--color-fg-muted)]">
                <input
                  type="checkbox"
                  checked={fieldDraft.multiline}
                  onChange={(e) =>
                    setFieldDraft((d) => (d ? { ...d, multiline: e.target.checked } : d))
                  }
                />
                Multi-line (paragraph) field
              </label>
            )}
            {fieldDraft.kind === "radio" && (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-14 shrink-0 text-[color:var(--color-fg-muted)]">Choice</span>
                  <input
                    value={fieldDraft.onValue}
                    onChange={(e) =>
                      setFieldDraft((d) => (d ? { ...d, onValue: e.target.value } : d))
                    }
                    placeholder="This button's value — e.g. Express"
                    className="h-8 min-w-0 flex-1 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-sm"
                  />
                </div>
                <p className="pl-16 text-[11px] text-[color:var(--color-fg-muted)]">
                  Each box you drag is one button. Boxes with the same group name form one
                  group — picking any button clears the others.
                </p>
              </>
            )}
            {fieldDraft.kind === "combo" && (
              <div className="flex items-start gap-2 text-sm">
                <span className="w-14 shrink-0 pt-1 text-[color:var(--color-fg-muted)]">
                  Choices
                </span>
                <textarea
                  value={fieldDraft.options}
                  onChange={(e) =>
                    setFieldDraft((d) => (d ? { ...d, options: e.target.value } : d))
                  }
                  rows={4}
                  placeholder={"One per line\nBasic\nPro"}
                  className="min-w-0 flex-1 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 py-1 text-sm"
                />
              </div>
            )}
            <p className="text-[11px] text-[color:var(--color-fg-muted)]">
              Creates a real interactive field — fillable here and in any PDF reader. “Flatten”
              on download bakes the entered values in permanently. Undoable.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setFieldDraft(null)}
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90"
              >
                Add field
              </button>
            </div>
          </form>
        </div>
      )}

      {/* crop dialog */}
      {cropDraft && (
        <div role="dialog" aria-modal="true" className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const d = cropDraft;
              setCropDraft(null);
              setTool("select");
              doPageOp({
                kind: "crop",
                target: d.target === "all" ? "all" : d.pageIndex,
                rect: d.rect,
              });
            }}
            className="w-full max-w-sm space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl"
          >
            <div className="flex items-center gap-2">
              <i className="ri-crop-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
              <h2 className="text-base font-semibold">Crop page</h2>
              <span className="ml-auto text-xs text-[color:var(--color-fg-muted)]">
                {Math.round((cropDraft.rect[2] - cropDraft.rect[0]) / MM_TO_PT)} ×{" "}
                {Math.round((cropDraft.rect[3] - cropDraft.rect[1]) / MM_TO_PT)} mm
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="w-16 shrink-0 text-[color:var(--color-fg-muted)]">Apply to</span>
              <label className="inline-flex cursor-pointer items-center gap-1">
                <input
                  type="radio"
                  checked={cropDraft.target === "current"}
                  onChange={() => setCropDraft((d) => (d ? { ...d, target: "current" } : d))}
                />
                Page {cropDraft.pageIndex + 1}
              </label>
              <label className="inline-flex cursor-pointer items-center gap-1">
                <input
                  type="radio"
                  checked={cropDraft.target === "all"}
                  onChange={() => setCropDraft((d) => (d ? { ...d, target: "all" } : d))}
                />
                All pages
              </label>
            </div>
            <p className="text-[11px] text-[color:var(--color-fg-muted)]">
              Nothing is deleted — the visible page box is trimmed, and “Remove crop”
              restores the full page. Annotations move with their content. Undoable.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  const d = cropDraft;
                  setCropDraft(null);
                  setTool("select");
                  doPageOp({
                    kind: "crop",
                    target: d.target === "all" ? "all" : d.pageIndex,
                    rect: null,
                  });
                }}
                className="mr-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
              >
                <i className="ri-close-circle-line" aria-hidden /> Remove crop
              </button>
              <button
                type="button"
                onClick={() => setCropDraft(null)}
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90"
              >
                Crop
              </button>
            </div>
          </form>
        </div>
      )}

      {/* insert-pages-from-PDF dialog */}
      {insertDraft && (
        <div role="dialog" aria-modal="true" className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              runInsertPages();
            }}
            className="w-full max-w-sm space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl"
          >
            <div className="flex items-center gap-2">
              <i className="ri-file-add-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
              <h2 className="min-w-0 flex-1 truncate text-base font-semibold">
                Insert “{insertDraft.name}”
              </h2>
              <span className="shrink-0 text-xs text-[color:var(--color-fg-muted)]">
                {insertDraft.pageCount === null
                  ? "reading…"
                  : `${insertDraft.pageCount} page${insertDraft.pageCount === 1 ? "" : "s"}`}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-16 shrink-0 text-[color:var(--color-fg-muted)]">Where</span>
              <select
                value={insertDraft.position}
                onChange={(e) =>
                  setInsertDraft((d) => (d ? { ...d, position: e.target.value as never } : d))
                }
                className="h-8 min-w-0 flex-1 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 text-sm"
              >
                <option value="afterCurrent">After page {currentPage + 1}</option>
                <option value="start">At the start</option>
                <option value="end">At the end</option>
              </select>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="w-16 shrink-0 text-[color:var(--color-fg-muted)]">Pages</span>
              <label className="inline-flex cursor-pointer items-center gap-1">
                <input
                  type="radio"
                  checked={insertDraft.range === "all"}
                  onChange={() => setInsertDraft((d) => (d ? { ...d, range: "all" } : d))}
                />
                All
              </label>
              <label className="inline-flex cursor-pointer items-center gap-1">
                <input
                  type="radio"
                  checked={insertDraft.range === "custom"}
                  onChange={() =>
                    setInsertDraft((d) => (d ? { ...d, range: "custom", to: Math.min(d.to, 9999) } : d))
                  }
                />
                Range
              </label>
              {insertDraft.range === "custom" && (
                <span className="inline-flex items-center gap-1 text-xs">
                  {/* clamped to the source's real page count */}
                  <input
                    type="number"
                    min={1}
                    max={insertDraft.pageCount ?? 1}
                    disabled={insertDraft.pageCount === null}
                    value={insertDraft.from}
                    onChange={(e) =>
                      setInsertDraft((d) => {
                        if (!d) return d;
                        const last = d.pageCount ?? 1;
                        const from = Math.min(Math.max(1, Number(e.target.value) || 1), last);
                        return { ...d, from, to: Math.max(from, d.to) };
                      })
                    }
                    className="h-8 w-16 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 disabled:opacity-40"
                  />
                  –
                  <input
                    type="number"
                    min={insertDraft.from}
                    max={insertDraft.pageCount ?? 1}
                    disabled={insertDraft.pageCount === null}
                    value={insertDraft.to}
                    onChange={(e) =>
                      setInsertDraft((d) => {
                        if (!d) return d;
                        const last = d.pageCount ?? 1;
                        const to = Math.min(Math.max(1, Number(e.target.value) || 1), last);
                        return { ...d, to, from: Math.min(d.from, to) };
                      })
                    }
                    className="h-8 w-16 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 disabled:opacity-40"
                  />
                  <span className="text-[color:var(--color-fg-muted)]">
                    of {insertDraft.pageCount ?? "…"}
                  </span>
                </span>
              )}
            </div>

            {/* preview: pages included in the range are highlighted */}
            {insertDraft.thumbs.length > 0 && (
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-subtle)] p-2">
                <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto">
                  {insertDraft.thumbs.map((t) => {
                    const n = t.pageIndex + 1;
                    const included =
                      insertDraft.range === "all" ||
                      (n >= insertDraft.from && n <= insertDraft.to);
                    return (
                      <button
                        key={t.pageIndex}
                        type="button"
                        title={
                          insertDraft.range === "custom"
                            ? `Page ${n} — click to start the range here, shift-click to end it`
                            : `Page ${n}`
                        }
                        onClick={(e) =>
                          setInsertDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  range: "custom",
                                  ...(e.shiftKey
                                    ? { to: Math.max(n, d.from) }
                                    : { from: n, to: Math.max(n, d.range === "all" ? n : d.to) }),
                                }
                              : d
                          )
                        }
                        className={`relative block rounded-sm border p-0.5 ${
                          included
                            ? "border-[color:var(--color-accent)]"
                            : "border-transparent opacity-40"
                        }`}
                      >
                        <ThumbCanvas thumb={t} />
                        <span className="mt-0.5 block text-center text-[9px] leading-none text-[color:var(--color-fg-muted)]">
                          {n}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {insertDraft.pageCount !== null &&
                  insertDraft.pageCount > insertDraft.thumbs.length && (
                    <p className="pt-1 text-center text-[10px] text-[color:var(--color-fg-muted)]">
                      previewing the first {insertDraft.thumbs.length} of{" "}
                      {insertDraft.pageCount} pages
                    </p>
                  )}
              </div>
            )}

            <p className="text-[11px] text-[color:var(--color-fg-muted)]">
              Page content and images come across; the source’s own annotations and links do
              not. Undoable.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setInsertDraft(null)}
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90"
              >
                Insert
              </button>
            </div>
          </form>
        </div>
      )}

      {/* combine PDFs */}
      {combineOpen && (
        <div role="dialog" aria-modal="true" className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6">
          <div className="w-full max-w-lg space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl">
            <div className="flex items-center gap-2">
              <i className="ri-file-copy-2-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
              <h2 className="text-base font-semibold">Combine PDFs</h2>
            </div>
            <p className="text-xs text-[color:var(--color-fg-muted)]">
              Adds whole documents to this one, in the order below. Your own
              annotations move with their pages; annotations already inside the
              files you add are not carried over.
            </p>

            <button
              type="button"
              disabled={!!combineBusy}
              onClick={() => combinePdfInputRef.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-[color:var(--color-border-strong)] px-3 py-3 text-sm text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-add-line" aria-hidden /> Choose PDFs…
            </button>

            {combineList.length > 0 && (
              <ul className="max-h-64 space-y-1 overflow-y-auto">
                {combineList.map((f, i) => (
                  <li
                    key={f.id}
                    className="flex items-center gap-2 rounded-md border border-[color:var(--color-border)] px-2 py-1.5 text-sm"
                  >
                    <span className="w-5 shrink-0 text-center text-xs tabular-nums text-[color:var(--color-fg-muted)]">
                      {i + 1}
                    </span>
                    <i className="ri-file-pdf-line shrink-0 text-[color:var(--color-fg-muted)]" aria-hidden />
                    <span className="min-w-0 flex-1 truncate" title={f.name}>
                      {f.name}
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-[color:var(--color-fg-muted)]">
                      {f.pageCount === null
                        ? "reading…"
                        : `${f.pageCount} page${f.pageCount === 1 ? "" : "s"}`}
                    </span>
                    <button
                      type="button"
                      title="Move up"
                      disabled={i === 0 || !!combineBusy}
                      onClick={() => moveCombineItem(f.id, -1)}
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-30"
                    >
                      <i className="ri-arrow-up-line" aria-hidden />
                    </button>
                    <button
                      type="button"
                      title="Move down"
                      disabled={i === combineList.length - 1 || !!combineBusy}
                      onClick={() => moveCombineItem(f.id, 1)}
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-30"
                    >
                      <i className="ri-arrow-down-line" aria-hidden />
                    </button>
                    <button
                      type="button"
                      title="Remove from the list"
                      disabled={!!combineBusy}
                      onClick={() => setCombineList((prev) => prev.filter((x) => x.id !== f.id))}
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-30"
                    >
                      <i className="ri-close-line" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap gap-3 text-sm">
              <span className="text-xs font-medium text-[color:var(--color-fg-muted)]">
                Place them
              </span>
              {(
                [
                  ["end", `after this document (${pages?.length ?? 0} pages)`],
                  ["start", "before this document"],
                ] as const
              ).map(([mode, label]) => (
                <label key={mode} className="inline-flex cursor-pointer items-center gap-1">
                  <input
                    type="radio"
                    checked={combineAt === mode}
                    disabled={!!combineBusy}
                    onChange={() => setCombineAt(mode)}
                  />
                  {label}
                </label>
              ))}
            </div>

            {combineBusy && (
              <p className="text-xs text-[color:var(--color-fg-muted)]">
                Adding file {combineBusy.done} of {combineBusy.total}…
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={!!combineBusy}
                onClick={() => {
                  setCombineOpen(false);
                  setCombineList([]);
                }}
                className="inline-flex h-9 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  !!combineBusy ||
                  combineList.length === 0 ||
                  combineList.some((f) => f.pageCount === null)
                }
                onClick={runCombine}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] disabled:opacity-40"
              >
                {combineBusy ? (
                  <i className="ri-loader-4-line animate-spin" aria-hidden />
                ) : (
                  <i className="ri-file-copy-2-line" aria-hidden />
                )}
                Combine{" "}
                {combineList.length > 0 &&
                  `(${combineList.reduce((n, f) => n + (f.pageCount ?? 0), 0)} pages)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* reduce file size */}
      {optOpen && (
        <div role="dialog" aria-modal="true" className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6">
          <div className="w-full max-w-sm space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl">
            <div className="flex items-center gap-2">
              <i className="ri-compasses-2-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
              <h2 className="text-base font-semibold">Reduce file size</h2>
            </div>
            <p className="text-xs text-[color:var(--color-fg-muted)]">
              Deduplicates and recompresses the file's structure, which every edit
              adds to. Text stays text — nothing is rasterised. Applied to the open
              document as one undoable step.
            </p>

            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={optImages}
                disabled={optBusy}
                onChange={(e) => setOptImages(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Also recompress images
                <span className="block text-xs text-[color:var(--color-fg-muted)]">
                  Where it helps — an image is only replaced if the new version is
                  actually smaller. Bilevel scans and transparency are left alone.
                </span>
              </span>
            </label>

            {optImages && (
              <div className="space-y-2 border-l-2 border-[color:var(--color-border)] pl-3">
                <label className="block space-y-1">
                  <span className="text-xs font-medium">Maximum image resolution</span>
                  <select
                    value={optDpi}
                    disabled={optBusy}
                    onChange={(e) => setOptDpi(Number(e.target.value))}
                    className="h-9 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-sm"
                  >
                    <option value={96}>96 dpi — screen / email</option>
                    <option value={150}>150 dpi — good print</option>
                    <option value={200}>200 dpi — high quality</option>
                    <option value={300}>300 dpi — press</option>
                  </select>
                </label>
                <label className="block space-y-1">
                  <span className="text-xs font-medium">
                    JPEG quality{" "}
                    <span className="tabular-nums text-[color:var(--color-fg-muted)]">{optQuality}%</span>
                  </span>
                  <input
                    type="range"
                    min={40}
                    max={95}
                    value={optQuality}
                    disabled={optBusy}
                    onChange={(e) => setOptQuality(Number(e.target.value))}
                    className="w-full"
                  />
                </label>
              </div>
            )}

            {optResult && (
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-subtle)] p-2.5 text-xs">
                {optResult.after < optResult.before ? (
                  <p className="font-medium">
                    {formatBytes(optResult.before)} → {formatBytes(optResult.after)}{" "}
                    <span className="text-[color:var(--color-accent)]">
                      ({Math.round((1 - optResult.after / optResult.before) * 100)}% smaller)
                    </span>
                  </p>
                ) : (
                  <p className="font-medium">Nothing to gain — the file is already compact.</p>
                )}
                <p className="text-[color:var(--color-fg-muted)]">
                  {optResult.imagesSeen === 0
                    ? "No images in this document."
                    : `${optResult.imagesRecompressed} of ${optResult.imagesSeen} image${optResult.imagesSeen === 1 ? "" : "s"} recompressed.`}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={optBusy}
                onClick={() => {
                  setOptOpen(false);
                  setOptResult(null);
                }}
                className="inline-flex h-9 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                {optResult ? "Done" : "Cancel"}
              </button>
              <button
                type="button"
                disabled={optBusy || !pages}
                onClick={() => void onOptimize()}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] disabled:opacity-40"
              >
                {optBusy ? (
                  <i className="ri-loader-4-line animate-spin" aria-hidden />
                ) : (
                  <i className="ri-compasses-2-line" aria-hidden />
                )}
                {optResult ? "Optimise again" : "Optimise"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* export pages as images */}
      {imgExportOpen && (
        <div role="dialog" aria-modal="true" className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6">
          <div className="w-full max-w-sm space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl">
            <div className="flex items-center gap-2">
              <i className="ri-image-2-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
              <h2 className="text-base font-semibold">Export pages as images</h2>
            </div>
            <p className="text-xs text-[color:var(--color-fg-muted)]">
              Renders each page exactly as you see it — annotations, form values and
              all. One page saves as a single image; several save as a zip.
            </p>

            <div className="space-y-1.5">
              <span className="text-xs font-medium">Format</span>
              <div className="flex overflow-hidden rounded-md border border-[color:var(--color-border)]">
                {IMAGE_FORMATS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    aria-pressed={imgFormat === f.id}
                    disabled={!!imgBusy}
                    onClick={() => setImgFormat(f.id)}
                    className={`flex-1 px-2 py-1.5 text-sm disabled:opacity-40 ${
                      imgFormat === f.id
                        ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                        : "hover:bg-[color:var(--color-bg-subtle)]"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="block space-y-1.5">
              <span className="text-xs font-medium">Resolution</span>
              <select
                value={imgDpi}
                disabled={!!imgBusy}
                onChange={(e) => setImgDpi(Number(e.target.value))}
                className="h-9 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-sm"
              >
                {EXPORT_DPI.map((d) => (
                  <option key={d.dpi} value={d.dpi}>
                    {d.label} — {d.hint}
                  </option>
                ))}
              </select>
            </label>

            {formatOf(imgFormat).lossy && (
              <label className="block space-y-1">
                <span className="text-xs font-medium">
                  Quality <span className="tabular-nums text-[color:var(--color-fg-muted)]">{Math.round(imgQuality * 100)}%</span>
                </span>
                <input
                  type="range"
                  min={40}
                  max={100}
                  value={Math.round(imgQuality * 100)}
                  disabled={!!imgBusy}
                  onChange={(e) => setImgQuality(Number(e.target.value) / 100)}
                  className="w-full"
                />
              </label>
            )}

            <div className="space-y-1.5">
              <span className="text-xs font-medium">Pages</span>
              <div className="flex flex-wrap gap-3 text-sm">
                {(
                  [
                    ["all", `All ${pages?.length ?? 0}`],
                    ["current", `Page ${currentPage + 1}`],
                    ["range", "Range"],
                  ] as const
                ).map(([mode, label]) => (
                  <label key={mode} className="inline-flex cursor-pointer items-center gap-1">
                    <input
                      type="radio"
                      checked={imgScope === mode}
                      disabled={!!imgBusy}
                      onChange={() => setImgScope(mode)}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {imgScope === "range" && (
                <input
                  type="text"
                  value={imgRange}
                  disabled={!!imgBusy}
                  placeholder="e.g. 1-3, 5, 8-"
                  onChange={(e) => setImgRange(e.target.value)}
                  className="h-9 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-sm"
                />
              )}
            </div>

            {imgBusy && (
              <div className="space-y-1">
                <p className="text-xs text-[color:var(--color-fg-muted)]">
                  Rendering page {imgBusy.done} of {imgBusy.total}…
                </p>
                <div className="h-2 overflow-hidden rounded bg-[color:var(--color-bg-subtle)]">
                  <div
                    className="h-full bg-[color:var(--color-accent)] transition-[width]"
                    style={{ width: `${Math.round((imgBusy.done / Math.max(1, imgBusy.total)) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={!!imgBusy}
                onClick={() => setImgExportOpen(false)}
                className="inline-flex h-9 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!!imgBusy || !pages}
                onClick={() => void onExportImages()}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] disabled:opacity-40"
              >
                {imgBusy ? (
                  <i className="ri-loader-4-line animate-spin" aria-hidden />
                ) : (
                  <i className="ri-download-2-line" aria-hidden />
                )}
                Export
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OCR dialog */}
      {ocrOpen && (
        <div role="dialog" aria-modal="true" className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6">
          <div className="w-full max-w-sm space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl">
            <div className="flex items-center gap-2">
              <i className="ri-character-recognition-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
              <h2 className="text-base font-semibold">Recognise text (OCR)</h2>
            </div>
            <p className="text-xs text-[color:var(--color-fg-muted)]">
              Reads scanned pages and adds an invisible, selectable text layer — search,
              copy and Find &amp; Replace start working. English recognition, runs entirely
              in your browser (the ~15&nbsp;MB engine loads on first run). Undoable.
            </p>
            <div className="flex gap-3 text-sm">
              <label className="inline-flex cursor-pointer items-center gap-1">
                <input type="radio" checked={ocrScope === "all"} onChange={() => setOcrScope("all")} disabled={!!ocrState} />
                All pages
              </label>
              <label className="inline-flex cursor-pointer items-center gap-1">
                <input type="radio" checked={ocrScope === "current"} onChange={() => setOcrScope("current")} disabled={!!ocrState} />
                Page {currentPage + 1}
              </label>
            </div>
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-[color:var(--color-fg-muted)]">
              <input type="checkbox" checked={ocrSkip} onChange={(e) => setOcrSkip(e.target.checked)} disabled={!!ocrState} />
              Skip pages that already contain text
            </label>
            {ocrState && (
              <div className="space-y-1">
                <p className="text-xs text-[color:var(--color-fg-muted)]">
                  Page {ocrState.page} of {ocrState.total} — {Math.round(ocrState.progress * 100)}%
                </p>
                <div className="h-2 overflow-hidden rounded bg-[color:var(--color-bg-subtle)]">
                  <div
                    className="h-full bg-[color:var(--color-accent)] transition-[width]"
                    style={{
                      width: `${Math.round(((ocrState.page - 1 + ocrState.progress) / Math.max(1, ocrState.total)) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={!!ocrState}
                onClick={() => setOcrOpen(false)}
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
              >
                Close
              </button>
              <button
                type="button"
                disabled={!!ocrState || pageOpBusy}
                onClick={() => void runOcr()}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-40"
              >
                {ocrState ? "Recognising…" : "Run OCR"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* link create / edit dialog */}
      {linkDraft && (
        <div role="dialog" aria-modal="true" className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              commitLinkDraft();
            }}
            className="w-full max-w-sm space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl"
          >
            <div className="flex items-center gap-2">
              <i className="ri-link text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
              <h2 className="text-base font-semibold">
                {linkDraft.id ? "Edit link" : "Add link"}
              </h2>
            </div>
            <div className="flex gap-3 text-sm">
              <label className="inline-flex cursor-pointer items-center gap-1">
                <input
                  type="radio"
                  checked={linkDraft.mode === "url"}
                  onChange={() => setLinkDraft((d) => (d ? { ...d, mode: "url" } : d))}
                />
                Web address
              </label>
              <label className="inline-flex cursor-pointer items-center gap-1">
                <input
                  type="radio"
                  checked={linkDraft.mode === "page"}
                  onChange={() => setLinkDraft((d) => (d ? { ...d, mode: "page" } : d))}
                />
                Page in this document
              </label>
            </div>
            {linkDraft.mode === "url" ? (
              <input
                autoFocus
                value={linkDraft.url}
                onChange={(e) => setLinkDraft((d) => (d ? { ...d, url: e.target.value } : d))}
                onFocus={(e) => e.currentTarget.select()}
                placeholder="https://example.com"
                spellCheck={false}
                className="h-9 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-sm"
              />
            ) : (
              <label className="flex items-center gap-2 text-sm">
                Jump to page
                <input
                  autoFocus
                  type="number"
                  min={1}
                  max={pages?.length ?? 1}
                  value={linkDraft.page + 1}
                  onChange={(e) =>
                    setLinkDraft((d) =>
                      d ? { ...d, page: Math.max(0, (Number(e.target.value) || 1) - 1) } : d
                    )
                  }
                  className="h-9 w-20 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-sm"
                />
                of {pages?.length ?? 1}
              </label>
            )}
            <div className="flex justify-end gap-2">
              {linkDraft.id && (
                <button
                  type="button"
                  onClick={() => {
                    const id = linkDraft.id!;
                    dispatch({
                      type: "commit",
                      annots: annotsRef.current.filter((a) => a.id !== id),
                    });
                    clearAnnotIf(id);
                    setLinkDraft(null);
                  }}
                  className="mr-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-border)] px-3 text-sm text-[color:var(--color-danger,#e03131)] hover:bg-[color:var(--color-bg-subtle)]"
                >
                  <i className="ri-delete-bin-line" aria-hidden /> Remove
                </button>
              )}
              <button
                type="button"
                onClick={() => setLinkDraft(null)}
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90"
              >
                {linkDraft.id ? "Save" : "Add link"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* security & permissions dialog */}
      {googleFontOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Add a Google font"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const input = e.currentTarget.elements.namedItem("family") as HTMLInputElement;
              if (input.value.trim()) void onAddGoogleFont(input.value.trim());
            }}
            className="w-full max-w-sm space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl"
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <i className="ri-google-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
              <h2 className="text-base font-semibold">Add a Google font</h2>
            </div>
            <input
              name="family"
              autoFocus
              placeholder="Exact family name — e.g. Lobster, PT Serif"
              className="h-9 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-sm"
            />
            <p className="text-[11px] leading-relaxed text-[color:var(--color-fg-muted)]">
              Downloads the face from the Google Fonts catalogue (fonts.google.com) and embeds it
              into the PDF when you export. Added for this session only.
            </p>
            {googleFontError && (
              <p className="rounded-md bg-[#e0313115] p-2 text-[11px] text-[#e03131]">
                {googleFontError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={googleFontBusy}
                onClick={() => {
                  setGoogleFontOpen(false);
                  setGoogleFontError(null);
                }}
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={googleFontBusy}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-50"
              >
                {googleFontBusy ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" aria-hidden /> Fetching…
                  </>
                ) : (
                  "Add font"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {certifyOpen && pages && (
        <CertifyDialog
          pages={pages}
          currentPage={currentPage}
          defaultName={author}
          busy={certifyBusy}
          status={certifyStatus}
          onCancel={() => {
            setCertifyOpen(false);
            setCertifyStatus(null);
          }}
          onSign={(req) => void onCertify(req)}
        />
      )}

      {securityOpen && (
        <SecurityDialog
          value={security}
          sourceEncrypted={(docInfo?.encryption ?? "None") !== "None"}
          busy={saving}
          onCancel={() => setSecurityOpen(false)}
          onApply={(next, download) => {
            applySecurity(next);
            setSecurityOpen(false);
            if (download) void onDownload({ security: next });
            else
              showToast(
                next.mode === "encrypt"
                  ? "Saved — downloads will be encrypted"
                  : next.mode === "none"
                    ? "Saved — downloads will have protection removed"
                    : "Saved — downloads keep the original protection"
              );
          }}
        />
      )}

      {/* signature dialog */}
      {sigDialog && (
        <SignatureDialog onUse={startStampPlacement} onClose={() => setSigDialog(false)} />
      )}

      {/* password prompt for encrypted documents */}
      {pwPrompt && (
        <div role="dialog" aria-modal="true" className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6">
          <form
            onSubmit={submitPassword}
            className="w-full max-w-sm space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl"
          >
            <div className="flex items-center gap-2">
              <i className="ri-lock-2-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
              <h2 className="text-base font-semibold">Password required</h2>
            </div>
            <p className="text-xs text-[color:var(--color-fg-muted)]">
              <span className="font-medium">{pwPrompt.name}</span> is encrypted.
              Enter its password to open it — the password stays on this
              device and is never stored.
            </p>
            <input
              type="password"
              autoFocus
              value={pwValue}
              onChange={(e) => {
                setPwValue(e.target.value);
                setPwError(false);
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") cancelPassword();
              }}
              placeholder="Document password"
              className="h-9 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2.5 text-sm"
            />
            {pwError && (
              <p className="text-xs text-red-600">Incorrect password — try again.</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelPassword}
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!pwValue || pwBusy}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-50"
              >
                {pwBusy && <i className="ri-loader-4-line animate-spin" aria-hidden />}
                Unlock
              </button>
            </div>
          </form>
        </div>
      )}

      {/* measure calibration dialog */}
      {calibrate && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Calibrate measurement scale"
          className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6"
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              commitCalibrate();
            }}
            className="w-full max-w-sm space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl"
          >
            <div className="flex items-center gap-2">
              <i className="ri-ruler-2-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
              <h2 className="text-base font-semibold">Calibrate scale</h2>
            </div>
            <p className="text-xs text-[color:var(--color-fg-muted)]">
              The last measurement drew{" "}
              <span className="font-medium text-[color:var(--color-fg)]">
                {ptToUnit(calibrate.drawnPt, measureUnit).toFixed(2)} {measureUnit}
              </span>{" "}
              at 1:1. Enter its real-world length and every measurement will
              use the resulting scale (for drawings, floor plans…).
            </p>
            <label className="flex items-center gap-2 text-sm">
              Real length
              <input
                autoFocus
                type="number"
                min="0"
                step="any"
                value={calibrate.value}
                onChange={(e) =>
                  setCalibrate((c) => (c ? { ...c, value: e.target.value } : c))
                }
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Escape") setCalibrate(null);
                }}
                className="h-9 w-28 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-sm"
              />
              {measureUnit}
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCalibrate(null)}
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!Number.isFinite(Number(calibrate.value)) || Number(calibrate.value) <= 0}
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          </form>
        </div>
      )}

      {/* command palette */}
      {/* in-editor user guide (? / F1) */}
      {helpOpen && <HelpOverlay commands={commands} onClose={() => setHelpOpen(false)} />}

      {paletteOpen && (
        <CommandPalette commands={commands} onClose={() => setPaletteOpen(false)} />
      )}

      {/* right-click context menu */}
      {ctxMenu &&
        (() => {
          const target = ctxMenu.annotId
            ? (doc.annots.find((a) => a.id === ctxMenu.annotId) ?? null)
            : null;
          const items: ContextMenuItem[] = [];
          if (target) {
            items.push(
              {
                label: "Cut",
                icon: "ri-scissors-line",
                shortcut: "Ctrl+X",
                run: () => copySelection(true),
              },
              {
                label: "Copy",
                icon: "ri-file-copy-line",
                shortcut: "Ctrl+C",
                run: () => copySelection(false),
              },
              {
                label: "Duplicate",
                icon: "ri-file-copy-2-line",
                shortcut: "Ctrl+D",
                run: duplicateSelection,
              },
              "separator"
            );
            if (target.kind === "text") {
              items.push({
                label: "Edit text",
                icon: "ri-edit-line",
                run: () => startEditAnnot(target.id),
              });
            }
            if (target.kind === "note") {
              items.push({
                label: "Edit note",
                icon: "ri-edit-line",
                run: () => startEditAnnot(target.id),
              });
            }
            if (target.kind === "link") {
              items.push({ label: "Edit link", icon: "ri-link", run: () => onEditLink(target) });
            }
            items.push({
              label: "Delete",
              icon: "ri-delete-bin-line",
              shortcut: "Del",
              danger: true,
              run: deleteSelected,
            });
          } else {
            items.push(
              {
                label: "Paste",
                icon: "ri-clipboard-line",
                shortcut: "Ctrl+V",
                run: () => void pasteFromMenu(ctxMenu.pageIndex),
              },
              {
                label: "Add bookmark here",
                icon: "ri-bookmark-line",
                run: () => addBookmarkAt(ctxMenu.pageIndex),
              }
            );
          }
          return (
            <EditorContextMenu
              x={ctxMenu.x}
              y={ctxMenu.y}
              items={items}
              onClose={() => setCtxMenu(null)}
            />
          );
        })()}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed bottom-10 left-1/2 z-50 -translate-x-1/2 rounded-md border border-[color:var(--color-border-strong)] bg-[color:var(--color-bg-subtle)] px-3 py-1.5 text-xs shadow-sm"
        >
          {toast}
        </div>
      )}
    </div>
  );
}

/** The editor. `<PdfEditor />` with no props is the standalone
 *  application; every prop narrows it for an embedder. */
export const PdfEditor = forwardRef<PdfEditorHandle, PdfEditorProps>(PdfEditorInner);
PdfEditor.displayName = "PdfEditor";
